import React, { useState, useEffect, useLayoutEffect, useRef, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import { ActionTriggerField, Boolean as BooleanField, Calculation, Contact, ContactList, DateInput, Deadline, Dropdown, FileUpload, FormActivityTriggerField, Instructions, MultiFile, MultiSelect, NumberInput, Subheader, Text, TimeInput, TableOfContents, DataTable, SaveButton, SearchSelect, DocGen, DocumentSearch, formatValue, ESignatureQuickAccess, VerifyData, isDateCalculationField } from "./FieldComponents";
import { Folder as FolderIcon, FolderOutlined, ResetTv } from "@mui/icons-material";
import { File as FileIcon, FolderOpenIcon, RefreshCw, Loader2, CloudCheck, CloudBackup } from "lucide-react";
import { ActivityFeed } from './ActivityFeed';
import { DocumentSection } from './DocumentSection';
import ESignatureManager from './ESignatureManager';
import '../styles/Documents.css';
import { useLocation } from 'react-router';
import { TimeBilling } from './TimeBilling';
import { CreateContact } from './CreateContact';
import { EditDetail } from './EditDetail';
import { Team } from './Team';
import { IntakeDetails } from './IntakeDetails';
import { RelatedCases } from './RelatedCases';
import { CaseContacts } from './CaseContacts';
import { DeadlineCreation } from './DeadlineCreation';
import Alert from './Alert';
import { ReportFusion } from '../components/ReportFusion';
import { handleNewContactForField } from '../utils/contactUtils';
import { formatDate } from 'date-fns';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { CaseSidebar } from './CaseSidebar';
import Vitals from './Vitals';
import WeatherEvents from './WeatherEvents';
import { Negotiation } from './Negotiation';
import { NestedFeed } from './NestedFeed';
import { MultiVal } from './MultiVal';
import { useUploads } from './UploadManagerContext';
import simpleUploadService from '../services/simpleUploadService';
import {
    getDocumentFolderRenameParentPaths,
    getTerminalDocumentFolderRenames,
    documentFolderRenameInvalidatesRequestPath,
    isDocumentFolderRequestCurrent,
    isDocumentFolderPathWithin,
    normalizeDocumentCaseFolderPath,
    reconcileDocumentCurrentFolder,
    remapDocumentFolderMetadata,
    remapDocumentFolderRecord,
    removeDocumentFolderMetadata,
    removeDocumentFolderRecord
} from '../utils/documentUploadFiles';
import { resolveDocumentDropSourceKeys } from '../utils/documentDragSelection';
import {
    DOCUMENT_SELECTION_MOVE_EVENT
} from '../utils/documentSelectionPreferences';
import {
    buildDocumentFolderSnapshot,
    createDocumentLiveUnreadRegistry,
    documentLiveSetsEqual,
    getDocumentLiveItemIdentity,
    getDocumentLiveScopeKey
} from '../utils/documentLiveUpdates';
import {
    decodeDocumentCasePolicySegment,
    normalizeDocumentSyncFolderPath,
    normalizeDocumentSyncMatchPath
} from '../utils/documentCaseBoundary';
import {
    DOCUMENT_LIVE_UNSUPPORTED_ERROR_CODE,
    requestDocumentLiveRevision
} from '../services/documentLiveRevisionService';
import {
    appendDocumentUndoReceipt,
    DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT,
    executeDocumentUndoReceipt,
    getDocumentUndoUnavailableMessage,
    isDocumentUndoKeyboardEvent,
    pruneExpiredDocumentUndoReceipts,
    publishDocumentUndoReceipt,
    settleDocumentMutationRefreshes
} from '../utils/documentOperationUndo';
import {
    getDocumentTransferOutcome,
    getDocumentTransferRecoveryMessage,
} from '../utils/documentTransferOutcome';
import {
    DEFAULT_DOCUMENT_FOLDER_SORT,
    documentFolderSortsEqual,
    normalizeDocumentFolderSort,
    parseDocumentSizeBytes
} from '../utils/documentFolderSort';
import {
    expandDocumentFolderAncestors,
    pruneDocumentExpandedFolders,
    reconcileRenamedDocumentExpandedFolders,
    setDocumentFolderExpanded
} from '../utils/documentFolderExpansion';
import {
    canEditHiddenFieldsForCase,
    customFieldRecordContainsReadonlyValues,
    customFieldSelectorIsVisibleForCase,
    filterCustomFieldRecordForCase,
    filterCustomFieldUpdatesForCase,
    filterCustomFieldValuesForCase,
    isCustomFieldEditable,
    isCustomFieldVisible,
    readCaseBoundHiddenFieldCapability,
    sanitizeCustomFieldExportPayloadForCase,
    sanitizeCustomFieldDraftForCase
} from '../utils/customFieldVisibility';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import { FieldRecommendation, isRecommendationTargetBlank, useFieldRecommendations } from './FieldRecommendation';
import { isPublicFormRequestFieldType } from '../utils/matterPublicForms';
import MatterPublicFormField from './MatterPublicFormField';

const isSectionReadInteger = (value, allowZero = false) => {
    if (typeof value !== 'number' && (
        typeof value !== 'string' || value === '' || /\D/.test(value)
    )) return false;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= (allowZero ? 0 : 1);
};

export const buildSectionExportUrl = ({
    caseId,
    leadId,
    sectionId,
    sortKey,
    sortDirection,
    filterBy,
    filterValue
}) => {
    if (![caseId, leadId, sectionId].every(value => isSectionReadInteger(value))) {
        return null;
    }

    const params = new URLSearchParams({
        export: 'true',
        section_id: String(sectionId),
        lead_id: String(leadId),
        case_id: String(caseId)
    });

    if (sortKey) {
        params.set('sort_by', String(sortKey));
        params.set('sort_direction', String(sortDirection || 'asc'));
    }

    if (filterBy && filterValue) {
        params.set('filter_by', String(filterBy));
        params.set('filter_value', String(filterValue));
    }

    return `https://api.casedb.co/custom_fields.php?${params.toString()}`;
};

const DOCUMENT_LIVE_REFRESH_INTERVAL_MS = 20000;
const DOCUMENT_LIVE_INCOMPLETE_BACKOFF_MS = 120000;
const DOCUMENT_LIVE_OPAQUE_FAILURE_LIMIT = 3;

export const isDocumentFolderOnActivePath = (folderPath, activePath) => (
    typeof folderPath === 'string'
    && typeof activePath === 'string'
    && (activePath === folderPath || activePath.startsWith(`${folderPath}/`))
);

export const resolveDocumentDragOperation = (event = {}, dragData = null) => {
    const modifierValues = [event?.ctrlKey, event?.metaKey, event?.altKey];
    if (modifierValues.some(value => typeof value === 'boolean')) {
        return modifierValues.some(value => value === true) ? 'copy' : 'move';
    }
    return dragData?.operation === 'copy' ? 'copy' : 'move';
};

export const normalizeValueToIndex = (field, value) => {
    if (!field || !field.options) return value;

    let options = [];
    try {
        options = Array.isArray(field.options)
            ? field.options
            : JSON.parse(field.options);
    } catch {
        return value;
    }

    if (!Array.isArray(options) || options.length === 0) return value;

    const normalizeOptionText = (optionValue) => String(optionValue ?? '')
        .toLowerCase()
        .replace(/^['"]+|['"]+$/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const findOptionValue = (rawValue) => {
        const normalizedValue = normalizeOptionText(rawValue);
        if (!normalizedValue) return rawValue;

        for (let index = 0; index < options.length; index += 1) {
            const option = options[index];
            const optionValue = Array.isArray(option) ? option[0] : index;
            const optionLabel = Array.isArray(option) ? option[1] : option;
            const normalizedLabel = normalizeOptionText(optionLabel);
            const normalizedStoredValue = normalizeOptionText(optionValue);
            if (
                normalizedLabel === normalizedValue
                || normalizedStoredValue === normalizedValue
                || (normalizedLabel && normalizedValue.includes(normalizedLabel))
                || (normalizedValue && normalizedLabel.includes(normalizedValue))
            ) {
                return optionValue;
            }
        }

        return rawValue;
    };

    if (Number(field.field_id) === 11) {
        const values = Array.isArray(value)
            ? value
            : String(value ?? '').split(',').map(item => item.trim()).filter(Boolean);
        return values.map(findOptionValue).filter(item => item !== null && item !== undefined && item !== '');
    }

    if (typeof value === "string") {
        return findOptionValue(value);
    }

    return value;
};

const parseGuidedFieldOptions = (options) => {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    try {
        const parsed = JSON.parse(options);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const normalizeGuidedOptionText = (value = '') => String(value ?? '')
    .toLowerCase()
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const getGuidedOptionParts = (option, index) => {
    if (Array.isArray(option)) {
        return { value: option[0], label: option[1] ?? option[0] };
    }
    if (option && typeof option === 'object') {
        return {
            value: option.value ?? option.id ?? option.key ?? index,
            label: option.label ?? option.name ?? option.text ?? option.value ?? option.id ?? option.key ?? index,
        };
    }
    return { value: index, label: option };
};

const getGuidedOptionValueByOrdinal = (field, ordinal) => {
    const optionIndex = Number(ordinal) - 1;
    if (!Number.isInteger(optionIndex) || optionIndex < 0) return undefined;
    const options = parseGuidedFieldOptions(field?.options);
    const option = options[optionIndex];
    if (option === undefined || option === null) return undefined;
    return getGuidedOptionParts(option, optionIndex).value;
};

const getGuidedOptionValueByStrictLabel = (field, rawValue) => {
    const normalizedValue = normalizeGuidedOptionText(rawValue);
    if (!normalizedValue) return undefined;
    const options = parseGuidedFieldOptions(field?.options);
    for (let index = 0; index < options.length; index += 1) {
        const option = getGuidedOptionParts(options[index], index);
        const normalizedLabel = normalizeGuidedOptionText(option.label);
        const normalizedStoredValue = normalizeGuidedOptionText(option.value);
        if (normalizedLabel === normalizedValue || normalizedStoredValue === normalizedValue) {
            return option.value;
        }
    }
    return undefined;
};

const normalizeMetadataPath = (filePath) => {
    if (!filePath || typeof filePath !== 'string') return null;
    return filePath;
};

const getMetadataIdentityKey = (meta) => {
    const normalizedPath = normalizeMetadataPath(meta?.file_path);
    if (normalizedPath) {
        return `path:${normalizedPath}`;
    }

    if (meta?.id !== undefined && meta?.id !== null && meta?.id !== '') {
        return `id:${meta.id}`;
    }

    return null;
};

const SETTLEMENT_MADE_WHOLE_FIELD_ID = 781;
const SETTLEMENT_MADE_WHOLE_SUBHEADER_ID = 'Fee-Arrangement';
const SETTLEMENT_MADE_WHOLE_BOOLEAN_OPTIONS = ["Yes", "No", "Unknown"];
const DISENGAGEMENT_PHASE_ID = 3;
const DISENGAGEMENT_REQUIRED_FIELD_IDS = [212, 213, 782, 783];
const DISENGAGEMENT_UNKNOWN_IS_MISSING_FIELD_IDS = [212, 783];

const getSettlementMadeWholeResponseValue = (value) => {
    if (value === null || value === undefined || value === '') return value;

    let parsedValue = value;
    if (typeof parsedValue === 'string' && parsedValue.trim().startsWith('{')) {
        try {
            parsedValue = JSON.parse(parsedValue);
        } catch {
            parsedValue = value;
        }
    }

    if (parsedValue && typeof parsedValue === 'object') {
        return parsedValue.form_activity_status?.response?.value ?? '';
    }

    return parsedValue;
};

const getSettlementMadeWholeLabel = (value) => {
    const labels = { 0: 'Yes', 1: 'No', 2: 'Unknown' };
    return labels[Number(value)] || 'Unknown';
};

const createSettlementMadeWholeField = (orderId = 0) => ({
    id: SETTLEMENT_MADE_WHOLE_FIELD_ID,
    field_id: 27,
    section_id: "[20]",
    name: "Made Whole?",
    settlement_phase_only: true,
    options: JSON.stringify({
        prompt: "was the client made whole?",
        subject: "Made Whole?",
        buttonLabel: "Request Made Whole?",
        mention_paths: ["cases.team['1'][0]"]
    }),
    add_item: 0,
    hidden: 0,
    obsolete: 0,
    case_type_id: 0,
    order_id: orderId
});

const parseDocumentCaseStorageKey = (rawKey, { folder = false } = {}) => {
    if (typeof rawKey !== 'string' || !rawKey || rawKey.length > 4096) return null;
    const keyValue = rawKey;
    const trailingMatch = keyValue.match(/\/+$/);
    if ((trailingMatch?.[0]?.length || 0) > 1) return null;
    const hasTrailingSeparator = keyValue.endsWith('/');
    const key = hasTrailingSeparator ? keyValue.slice(0, -1) : keyValue;
    const segments = key.split('/');
    if (segments.length < 4
        || segments[0] !== 'cases'
        || !/^[1-9][0-9]*$/.test(segments[1])
        || segments[2] !== '{{Name}}'
        || segments.some(segment => segment === '')) {
        return null;
    }

    const logicalSegments = segments.slice(2);
    const policySegments = folder ? logicalSegments : logicalSegments.slice(0, -1);
    const decodedPolicySegments = policySegments
        .map(decodeDocumentCasePolicySegment)
        .flatMap(segment => segment === null ? [null] : segment.split('/'));
    const decodedFileName = folder
        ? null
        : decodeDocumentCasePolicySegment(logicalSegments[logicalSegments.length - 1]);
    const decodedFileParts = decodedFileName === null ? [] : decodedFileName.split('/');
    const invalidDecodedFileName = !folder && (
        decodedFileName === null
        || decodedFileParts.some(segment => !segment || segment === '.' || segment === '..')
        || decodedFileParts.slice(0, -1).some((segment, index) => {
            const normalized = segment.trim().toLowerCase();
            return (policySegments.length === 1 && index === 0 && normalized === 'leads')
                || ['pre-case lead documents', 'lead documents'].includes(normalized);
        })
    );
    if (invalidDecodedFileName
        || decodedPolicySegments.some(segment => segment === null)
        || decodedPolicySegments.some((segment, index) => {
        const normalized = String(segment).trim().toLowerCase();
        return segment === '.' || segment === '..'
            || (index > 0 && segment === '{{Name}}')
            || (index === 1 && normalized === 'leads')
            || normalized === 'pre-case lead documents'
            || normalized === 'lead documents';
    })) {
        return null;
    }

    return {
        decodedKey: keyValue,
        hasTrailingSeparator,
        logicalPath: logicalSegments.join('/'),
        logicalParentPath: logicalSegments.slice(0, -1).join('/'),
        name: logicalSegments[logicalSegments.length - 1]
    };
};

export const sanitizeDocumentCaseMetadata = (entries = []) => (
    (Array.isArray(entries) ? entries : []).filter(entry => (
        parseDocumentCaseStorageKey(String(entry?.file_path || entry?.key || '')) !== null
    ))
);

const mergeMetadataEntries = (existing = [], incoming = []) => {
    const merged = new Map();
    const unkeyed = [];

    const append = (entry) => {
        if (!entry || typeof entry !== 'object') return;
        const key = getMetadataIdentityKey(entry);
        if (!key) {
            unkeyed.push(entry);
            return;
        }
        merged.set(key, entry);
    };

    sanitizeDocumentCaseMetadata(existing).forEach(append);
    sanitizeDocumentCaseMetadata(incoming).forEach(append);

    return [...merged.values(), ...unkeyed];
};

export const FolderNode = ({ name, caseName, level = 0, subfoldersEl, size, onClick, isActive, shouldOpen, isExpanded, onExpandedChange, onItemDrop, fullPath, fetchFolderContents, pageSize = 100, isDragActive, isLoading = false, isSynced = false, isPathSynced, syncPending }) => {
    const [manualOpenOverride, setManualOpenOverride] = useState(level === 0 ? true : null);
    const [transientOpen, setTransientOpen] = useState(false);
    const [isDraggedOver, setIsDraggedOver] = useState(false);
    const [hoverSubfolders, setHoverSubfolders] = useState(null);
    const hoverTimerRef = useRef(null);
    const hoverRequestGenerationRef = useRef(0);
    const dragInsideSubtreeRef = useRef(false);
    const isDragActiveRef = useRef(isDragActive);
    const mountedRef = useRef(true);
    const hasChildren = subfoldersEl && subfoldersEl.length > 0;
    // `Boolean` is also a field component imported by this module, so use an
    // explicit comparison instead of accidentally invoking that component.
    const expansionIsControlled = typeof isExpanded === 'boolean';
    const navigationOpen = shouldOpen === true;
    const persistentOpen = expansionIsControlled
        ? isExpanded
        : (manualOpenOverride === null ? navigationOpen : manualOpenOverride);
    const open = persistentOpen || transientOpen;

    const requestPersistentExpansion = useCallback((shouldExpand) => {
        if (expansionIsControlled) {
            onExpandedChange?.(fullPath, shouldExpand);
            return;
        }
        setManualOpenOverride(shouldExpand);
    }, [expansionIsControlled, fullPath, onExpandedChange]);

    const activateFolder = useCallback(() => {
        if (isActive) {
            if (hasChildren) requestPersistentExpansion(!persistentOpen);
            return;
        }

        if (hasChildren) requestPersistentExpansion(true);
        onClick?.();
    }, [hasChildren, isActive, onClick, persistentOpen, requestPersistentExpansion]);

    const handleFolderKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activateFolder();
            return;
        }

        if (event.key === 'ArrowRight' && hasChildren && !persistentOpen) {
            event.preventDefault();
            requestPersistentExpansion(true);
            return;
        }

        if (event.key === 'ArrowLeft' && hasChildren && persistentOpen) {
            event.preventDefault();
            requestPersistentExpansion(false);
        }
    }, [activateFolder, hasChildren, persistentOpen, requestPersistentExpansion]);

    useEffect(() => {
        isDragActiveRef.current = isDragActive;
    }, [isDragActive]);

    const clearHoverTimer = useCallback(() => {
        if (hoverTimerRef.current !== null) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    const collapseTransientExpansion = useCallback(() => {
        dragInsideSubtreeRef.current = false;
        hoverRequestGenerationRef.current += 1;
        clearHoverTimer();
        setIsDraggedOver(false);
        setTransientOpen(false);
        setHoverSubfolders(null);
    }, [clearHoverTimer]);

    // A drag-opened folder is deliberately ephemeral. Ending the drag closes
    // only that transient state; manual/navigation expansion is left alone.
    useEffect(() => {
        if (!isDragActive) {
            collapseTransientExpansion();
        }
    }, [collapseTransientExpansion, isDragActive]);

    // Invalidate pending timers and fetches on unmount so a late folder listing
    // cannot reopen a node after the pointer has moved elsewhere.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            hoverRequestGenerationRef.current += 1;
            clearHoverTimer();
        };
    }, [clearHoverTimer]);

    const prefetchSubfolders = async (requestGeneration) => {
        if (!fetchFolderContents || !fullPath || hasChildren) return;

        try {
            const folderData = await fetchFolderContents(fullPath, 1, pageSize);
            if (
                !mountedRef.current
                || hoverRequestGenerationRef.current !== requestGeneration
                || !dragInsideSubtreeRef.current
                || !isDragActiveRef.current
            ) return;

            setHoverSubfolders(
                folderData && folderData.success !== false && Array.isArray(folderData.folders)
                    ? folderData.folders
                    : []
            );
        } catch {
            // Hover navigation is optional. A failed prefetch must not interrupt
            // the underlying move/copy gesture or surface an error to the user.
        }
    };

    const scheduleTransientExpansion = () => {
        dragInsideSubtreeRef.current = true;
        if (
            !isDragActiveRef.current
            || persistentOpen
            || transientOpen
            || isActive
            || hoverTimerRef.current !== null
        ) return;

        const requestGeneration = hoverRequestGenerationRef.current + 1;
        hoverRequestGenerationRef.current = requestGeneration;
        hoverTimerRef.current = setTimeout(() => {
            hoverTimerRef.current = null;
            if (
                !mountedRef.current
                || hoverRequestGenerationRef.current !== requestGeneration
                || !dragInsideSubtreeRef.current
                || !isDragActiveRef.current
            ) return;

            setTransientOpen(true);
            prefetchSubfolders(requestGeneration);
        }, 1000);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = resolveDocumentDragOperation(e);
        }
        setIsDraggedOver(true);
        scheduleTransientExpansion();
    };

    const handleRowDragLeave = (e) => {
        setIsDraggedOver(false);
        const subtree = e.currentTarget.parentElement;
        if (!subtree?.contains(e.relatedTarget)) {
            collapseTransientExpansion();
        }
    };

    const handleSubtreeDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            collapseTransientExpansion();
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let dragData = null;
        try {
            dragData = JSON.parse(e.dataTransfer.getData('application/json'));
            if (onItemDrop) {
                const targetPath = fullPath || name;
                onItemDrop(dragData, targetPath, resolveDocumentDragOperation(e, dragData));
            }
        } catch (error) {
            console.error('Error parsing drag data:', error);
        } finally {
            collapseTransientExpansion();
        }
    };

    if (name === 'root') return null;

    return (
        <li onDragLeave={handleSubtreeDragLeave}>
            <div
                className={`folder-node ${isActive ? 'active' : ''} ${isDraggedOver ? 'drag-over' : ''} ${transientOpen ? 'drag-auto-expanded' : ''}`}
                role="button"
                aria-expanded={hasChildren ? open : undefined}
                aria-current={isActive ? 'location' : undefined}
                tabIndex={0}
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDraggedOver(true);
                    scheduleTransientExpansion();
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleRowDragLeave}
                onDrop={handleDrop}
                onClick={activateFolder}
                onKeyDown={handleFolderKeyDown}
                style={{ 
                    transition: 'background-color 0.3s ease',
                    backgroundColor: isDraggedOver
                        ? 'rgba(59, 130, 246, 0.1)'
                        : (isActive ? undefined : 'transparent'),
                    borderRadius: '3px'
                }}
            >
                <span className='folder-icon-wrapper'>
                    {parseDocumentSizeBytes(size) > 0 ? (
                        open ? <FolderOpenIcon/> : <FolderIcon/>
                    ) : (
                        <FolderOutlined/>
                    )}
                </span>
                <span>{caseName && name === "{{Name}}" ? caseName : name}</span>
                {isSynced && syncPending && (
                    <span className="sync-status-icon" title="Syncing...">
                        <CloudBackup size={14} />
                    </span>
                )}
                {isLoading && (
                    <Loader2 size={16} className='spinner' />
                )}
            </div>

            {open && (
                <>
                    {hasChildren && open && (
                        <ul className={`folder-tree level-${level + 1}`}>
                            {subfoldersEl}
                        </ul>
                    )}
                    {transientOpen && hoverSubfolders && hoverSubfolders.length > 0 && !hasChildren && (
                        <ul className={`folder-tree level-${level + 1} hover-fetched`}>
                            {hoverSubfolders.map((folder, index) => {
                                const childPath = `${fullPath}/${folder.name}`;
                                return (
                                    <FolderNode
                                        key={`hover-${childPath}-${index}`}
                                        name={folder.name}
                                        fullPath={childPath}
                                        files={[]}
                                        caseName={caseName}
                                        level={level + 1}
                                        subfoldersEl={[]}
                                        onItemDrop={onItemDrop}
                                        fetchFolderContents={fetchFolderContents}
                                        pageSize={pageSize}
                                        isDragActive={isDragActive}
                                        onClick={onClick}
                                        isSynced={typeof isPathSynced === 'function' ? isPathSynced(childPath) : false}
                                        isPathSynced={isPathSynced}
                                        syncPending={syncPending}
                                    />
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </li>
    );
};

export const isDocumentCaseFolderPathAllowed = (rawPath) => {
    if (typeof rawPath !== 'string' || rawPath.length > 2048) return false;
    const path = rawPath;
    if (!path || /[\\\u0000-\u001f\u007f]/.test(path)) {
        return false;
    }
    const rawSegments = path.split('/');
    if (rawSegments[0] !== '{{Name}}' || rawSegments.length > 100) return false;
    const segments = rawSegments
        .map(decodeDocumentCasePolicySegment)
        .flatMap(segment => segment === null ? [null] : segment.split('/'));
    return segments.every((segment, index) => {
        const normalized = String(segment).trim().toLowerCase();
        return segment !== null
            && segment !== '.'
            && segment !== '..'
            && (index === 0 ? segment === '{{Name}}' : segment !== '{{Name}}')
            && !(index === 1 && normalized === 'leads')
            && !['pre-case lead documents', 'lead documents'].includes(normalized);
    });
};

const isDocumentCaseItemAllowed = (item, parentPath) => {
    if (!item || typeof item !== 'object' || !isDocumentCaseFolderPathAllowed(parentPath)) {
        return false;
    }
    const rawKey = String(item.key || item.file_path || '');
    const isFolder = item.type === 'folder' || item.is_folder === true || rawKey.endsWith('/');
    const parsedKey = parseDocumentCaseStorageKey(rawKey, { folder: isFolder });
    if (!parsedKey) return false;

    // The legacy full-tree payload stores a folder marker inside that same
    // folder's cache entry. It is safe to retain, but never let it stand in for
    // a sibling or a row from another logical folder.
    if (item.name === '(Folder)' && isFolder) {
        return parsedKey.logicalPath === parentPath;
    }

    if (isFolder) {
        const expectedPath = `${parentPath}/${String(item.name || '')}`;
        return Boolean(item.name)
            && isDocumentCaseFolderPathAllowed(expectedPath)
            && parsedKey.logicalPath === expectedPath;
    }

    return parsedKey.logicalParentPath === parentPath;
};

export const sanitizeDocumentCaseFolders = (inputFolders) => {
    if (!inputFolders || typeof inputFolders !== 'object') return {};
    return Object.fromEntries(
        Object.entries(inputFolders)
            .filter(([path]) => isDocumentCaseFolderPathAllowed(path))
            .map(([path, value]) => [
                path,
                Object.values(value || {})
                    .filter(item => isDocumentCaseItemAllowed(item, path))
            ])
    );
};

const sanitizeCurrentDocumentFolder = (folder) => {
    if (!folder || !isDocumentCaseFolderPathAllowed(folder.path)) return null;
    const path = folder.path;
    const files = Array.isArray(folder.files)
        ? folder.files.filter(item => isDocumentCaseItemAllowed(item, path))
        : [];
    const items = Array.isArray(folder.items)
        ? folder.items.filter(item => isDocumentCaseItemAllowed(item, path))
        : folder.items ?? null;
    const subfolders = Object.fromEntries(
        Object.entries(folder.subfolders || {}).filter(([name, value]) => (
            isDocumentCaseFolderPathAllowed(`${path}/${name}`)
            && (!value?.key || isDocumentCaseItemAllowed({ ...value, name, type: 'folder' }, path))
        ))
    );
    return { ...folder, files, items, subfolders };
};

const sanitizeDocumentFolderResponse = (folderData, parentPath) => {
    if (!folderData || folderData.success === false
        || !isDocumentCaseFolderPathAllowed(parentPath)) {
        return folderData;
    }
    const folders = Object.values(folderData.folders || {})
        .filter(item => isDocumentCaseItemAllowed({ ...item, type: 'folder' }, parentPath));
    const files = Object.values(folderData.files || {})
        .filter(item => isDocumentCaseItemAllowed({ ...item, type: 'file' }, parentPath));
    const items = Array.isArray(folderData.items)
        ? folderData.items.filter(item => isDocumentCaseItemAllowed(item, parentPath))
        : folderData.items ?? null;
    const visibleFolders = Object.values(
        folderData.visibleFolders || folderData.visible_folders || {}
    ).filter(item => isDocumentCaseItemAllowed({ ...item, type: 'folder' }, parentPath));
    return {
        ...folderData,
        folders,
        files,
        items,
        visibleFolders,
        metaData: sanitizeDocumentCaseMetadata(folderData.metaData || [])
    };
};

export const Documents = forwardRef(({ fetchDocuments, fetchFolderContents, folders, metaData, caseName, case_id, user_id, caseType, onFileUpload, showMessage, documentLiveUnreadRegistry = null }, ref) => {
    const { folderRenames = {}, dispatch: uploadDispatch } = useUploads();
    const [currentFolder, setCurrentFolder] = useState(null);
    const currentFolderRef = useRef(null);
    const liveFolderBaselineRef = useRef(null);
    const folderMutationGenerationRef = useRef(0);
    const folderMutationHistoryRef = useRef([]);
    const documentUndoStackRef = useRef([]);
    const documentUndoInFlightRef = useRef(false);
    const refreshCurrentFolderActionRef = useRef(null);
    const refreshFolderTreeActionRef = useRef(null);
    const documentShowMessageRef = useRef(showMessage);
    documentShowMessageRef.current = showMessage;
    const folderNavigationGenerationRef = useRef(0);
    const activeFolderPageRef = useRef(1);
    const [localFolders, setLocalFolders] = useState({});
    const [expandedFolderPaths, setExpandedFolderPaths] = useState(
        () => new Set(['{{Name}}'])
    );
    const expandedNavigationPathRef = useRef(null);
    const [localMetaData, setLocalMetaData] = useState([]);
    const [allFolderMetadata, setAllFolderMetadata] = useState({}); // Store all folder metadata by full path
    const syncFolderCacheWithChildren = useCallback((existingFolders, parentPath, nextFolders = []) => {
        const normalizedParentPath = (parentPath || '').replace(/\/+$/, '');
        const updated = sanitizeDocumentCaseFolders(existingFolders);
        if (!isDocumentCaseFolderPathAllowed(normalizedParentPath)) {
            return updated;
        }

        const childNames = new Set(
            (Array.isArray(nextFolders) ? nextFolders : [])
                .map(folder => folder?.name)
                .filter(childName => childName
                    && isDocumentCaseFolderPathAllowed(`${normalizedParentPath}/${childName}`))
        );

        Object.keys(updated).forEach(key => {
            if (key === normalizedParentPath || !key.startsWith(`${normalizedParentPath}/`)) {
                return;
            }

            const remainder = key.slice(normalizedParentPath.length + 1);
            const directChildName = remainder.split('/')[0];

            if (directChildName && !childNames.has(directChildName)) {
                delete updated[key];
            }
        });

        childNames.forEach(childName => {
            const childPath = `${normalizedParentPath}/${childName}`;
            if (!updated[childPath]) {
                updated[childPath] = [];
            }
        });

        return updated;
    }, []);
    const folderEntries = Object.entries(sanitizeDocumentCaseFolders(localFolders));
    const [docNav, setDocNav] = useState(true);
    const [isDragActive, setIsDragActive] = useState(false);
    const [folderInitialized, setFolderInitialized] = useState(false);
    const [syncInfo, setSyncInfo] = useState(null);
    const [syncStatus, setSyncStatus] = useState({ pending: false, lastChecked: null, error: null });
    const [pagination, setPagination] = useState(null);
    const [pageSize, setPageSize] = useState(100); // Default page size of 100
    const activePageSizeRef = useRef(100);
    const [folderSort, setFolderSort] = useState(DEFAULT_DOCUMENT_FOLDER_SORT);
    const folderSortRef = useRef(DEFAULT_DOCUMENT_FOLDER_SORT);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isFolderLoading, setIsFolderLoading] = useState(false);
    const isFolderLoadingRef = useRef(false);
    const [liveNewItemIds, setLiveNewItemIds] = useState(() => new Set());
    const ownsLiveUnreadRegistryRef = useRef(!documentLiveUnreadRegistry);
    const liveUnreadRegistryRef = useRef(
        documentLiveUnreadRegistry || createDocumentLiveUnreadRegistry()
    );
    if (documentLiveUnreadRegistry && liveUnreadRegistryRef.current !== documentLiveUnreadRegistry) {
        liveUnreadRegistryRef.current = documentLiveUnreadRegistry;
        ownsLiveUnreadRegistryRef.current = false;
    }
    const liveVisibleScopeRef = useRef(null);
    const documentsCaseIdRef = useRef(String(case_id ?? ''));
    documentsCaseIdRef.current = String(case_id ?? '');
    const documentCaseBaselinePendingRef = useRef(true);
    const liveRefreshInFlightRef = useRef(false);
    const liveRevisionControllerRef = useRef(null);
    const liveIncompleteBackoffUntilRef = useRef(new Map());
    const liveRefreshFunctionRef = useRef(null);
    const lastLiveRefreshAtRef = useRef(0);
    const documentRevisionRef = useRef(null);
    const liveRevisionFailureLoggedRef = useRef(false);
    const liveRevisionUnsupportedRef = useRef(false);
    const liveRevisionOpaqueFailureCountRef = useRef(0);
    const liveRevisionOpaqueRetryAfterRef = useRef(0);
    const [loadingFolderPath, setLoadingFolderPath] = useState(null);
    const loadingFolderPathRef = useRef(null);
    const reconcilingFolderRenamesRef = useRef(new Set());
    const reconcileFolderRenameRef = useRef(null);
    const location = useLocation();
    const folderEffectRun = useRef(false);

    const captureFolderRequest = useCallback((
        path,
        navigationGeneration = null,
        page = null,
        requestPageSize = null,
        requestSort = null
    ) => ({
        caseId: case_id,
        path: normalizeDocumentCaseFolderPath(path),
        mutationGeneration: folderMutationGenerationRef.current,
        sort: normalizeDocumentFolderSort(requestSort || folderSortRef.current),
        ...(navigationGeneration === null ? {} : { navigationGeneration }),
        ...(page === null ? {} : { page: Number(page) || 1 }),
        ...(requestPageSize === null ? {} : { pageSize: Number(requestPageSize) || 100 })
    }), [case_id]);

    const folderRequestIsCurrent = useCallback((token, requireActiveNavigation = false) => (
        String(token?.caseId ?? '') === documentsCaseIdRef.current
        && documentFolderSortsEqual(token?.sort, folderSortRef.current)
        && isDocumentFolderRequestCurrent({
            token,
            mutations: folderMutationHistoryRef.current,
            activePath: currentFolderRef.current?.path,
            currentNavigationGeneration: folderNavigationGenerationRef.current,
            requireActiveNavigation
        })
    ), []);

    const activeFolderRequestIsCurrent = useCallback((token) => (
        String(token?.caseId) === String(case_id)
        && folderRequestIsCurrent(token, true)
        && Number(token?.page || 1) === Number(activeFolderPageRef.current || 1)
        && Number(token?.pageSize || 100) === Number(activePageSizeRef.current || 100)
    ), [case_id, folderRequestIsCurrent]);

    const abortLiveRevisionRequest = useCallback(() => {
        liveRevisionControllerRef.current?.abort();
        liveRevisionControllerRef.current = null;
        liveRefreshInFlightRef.current = null;
        lastLiveRefreshAtRef.current = 0;
    }, []);

    const invalidateActiveFolderRequests = useCallback(() => {
        abortLiveRevisionRequest();
        const navigationGeneration = folderNavigationGenerationRef.current + 1;
        folderNavigationGenerationRef.current = navigationGeneration;

        // A newer ordinary request supersedes any navigation spinner/request.
        // Clearing it here prevents a stale navigation's guarded `finally`
        // block from leaving the document area permanently loading.
        if (loadingFolderPathRef.current) {
            loadingFolderPathRef.current = null;
            setLoadingFolderPath(null);
            setIsFolderLoading(false);
        }

        return navigationGeneration;
    }, [abortLiveRevisionRequest]);

    const beginActiveFolderRequest = useCallback((path, page = 1, requestSort = null) => {
        const normalizedPath = normalizeDocumentCaseFolderPath(path);
        const normalizedPage = Math.max(1, Number(page) || 1);
        const normalizedSort = normalizeDocumentFolderSort(requestSort || folderSortRef.current);
        const navigationGeneration = invalidateActiveFolderRequests();
        activeFolderPageRef.current = normalizedPage;
        return captureFolderRequest(
            normalizedPath,
            navigationGeneration,
            normalizedPage,
            activePageSizeRef.current,
            normalizedSort
        );
    }, [captureFolderRequest, invalidateActiveFolderRequests]);

    const fetchFolderContentsWithSort = useCallback((
        path,
        page = 1,
        requestedPageSize = activePageSizeRef.current,
        requestSort = null
    ) => {
        if (!fetchFolderContents) return Promise.resolve(null);
        const normalizedPath = normalizeDocumentCaseFolderPath(path);
        if (!isDocumentCaseFolderPathAllowed(normalizedPath)) return Promise.resolve(null);
        return Promise.resolve(fetchFolderContents(
            path,
            page,
            requestedPageSize,
            normalizeDocumentFolderSort(requestSort || folderSortRef.current)
        )).then(folderData => sanitizeDocumentFolderResponse(folderData, normalizedPath));
    }, [fetchFolderContents]);

    const commitCurrentFolder = useCallback((nextFolder, liveOptions = undefined) => {
        const safeFolder = sanitizeCurrentDocumentFolder(nextFolder);
        if (!safeFolder) return false;
        currentFolderRef.current = safeFolder;
        setCurrentFolder(safeFolder);
        liveFolderBaselineRef.current?.(safeFolder, liveOptions);
        return true;
    }, []);

    useLayoutEffect(() => {
        currentFolderRef.current = currentFolder;
    }, [currentFolder]);

    useEffect(() => {
        const activePath = currentFolder?.path;
        if (!activePath || expandedNavigationPathRef.current === activePath) return;

        expandedNavigationPathRef.current = activePath;
        setExpandedFolderPaths(previous => (
            expandDocumentFolderAncestors(previous, activePath)
        ));
    }, [currentFolder?.path]);

    useEffect(() => {
        if (!folderInitialized) return;
        setExpandedFolderPaths(previous => (
            pruneDocumentExpandedFolders(previous, Object.keys(localFolders || {}))
        ));
    }, [folderInitialized, localFolders]);

    const setFolderExpanded = useCallback((folderPath, shouldExpand) => {
        if (!isDocumentCaseFolderPathAllowed(folderPath)) return;
        setExpandedFolderPaths(previous => (
            setDocumentFolderExpanded(previous, folderPath, shouldExpand)
        ));
    }, []);

    useLayoutEffect(() => {
        activePageSizeRef.current = pageSize;
    }, [pageSize]);

    useLayoutEffect(() => {
        folderSortRef.current = normalizeDocumentFolderSort(folderSort);
    }, [folderSort]);

    useLayoutEffect(() => {
        isFolderLoadingRef.current = isFolderLoading;
    }, [isFolderLoading]);

    const normalizeSyncPath = normalizeDocumentSyncFolderPath;
    const normalizeSyncMatchPath = normalizeDocumentSyncMatchPath;

    const effectiveSyncRules = syncInfo?.rules || {};
    const syncFolderPaths = (effectiveSyncRules.folder_paths || []).map(normalizeSyncPath).filter(Boolean);

    const buildSyncKeyForFolder = useCallback((path) => {
        if (!path) return null;
        let cleanPath = path;
        if (caseName && cleanPath.startsWith(`${caseName}/`) && !cleanPath.includes('{{Name}}')) {
            cleanPath = cleanPath.replace(`${caseName}/`, '{{Name}}/');
        }
        cleanPath = cleanPath.replace(/^\/+/, '');
        if (!cleanPath.endsWith('/')) cleanPath += '/';
        return normalizeSyncPath(`cases/${case_id}/${cleanPath}`);
    }, [caseName, case_id, normalizeSyncPath]);

    const isPathSynced = useCallback((fullPath) => {
        const key = buildSyncKeyForFolder(fullPath);
        if (!key) return false;
        const normalized = normalizeSyncMatchPath(key);
        if (!normalized) return false;
        return syncFolderPaths.some(folder => normalized.startsWith(folder));
    }, [buildSyncKeyForFolder, normalizeSyncMatchPath, syncFolderPaths]);

    const fetchSyncStatus = useCallback(async () => {
        const requestedCaseId = String(case_id ?? '');
        if (!case_id || syncFolderPaths.length === 0) {
            setSyncStatus({ pending: false, lastChecked: Date.now(), error: null });
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/case_sync_queue.php?parent_case_id=${case_id}&status=pending`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (documentsCaseIdRef.current !== requestedCaseId) return;
            const pending = Array.isArray(data?.items) ? data.items.length > 0 : false;
            setSyncStatus({ pending, lastChecked: Date.now(), error: null });
        } catch (error) {
            if (documentsCaseIdRef.current !== requestedCaseId) return;
            setSyncStatus({ pending: false, lastChecked: Date.now(), error: 'fetch_failed' });
        }
    }, [case_id, syncFolderPaths.length]);

    useEffect(() => {
        fetchSyncStatus();
        if (syncFolderPaths.length === 0) return;
        const interval = setInterval(fetchSyncStatus, 15000);
        return () => clearInterval(interval);
    }, [fetchSyncStatus, syncFolderPaths.length]);
    
    // Get URL parameters for file to open
    const params = new URLSearchParams(location.search);
    const fileParam = params.get("file");

    // Monitor window width changes
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Track global drag operations for hover expansion
    useEffect(() => {
        const handleDragStart = () => setIsDragActive(true);
        const handleDragEnd = () => setIsDragActive(false);

        document.addEventListener('dragstart', handleDragStart, true);
        document.addEventListener('dragend', handleDragEnd, true);
        document.addEventListener('drop', handleDragEnd, true);

        return () => {
            document.removeEventListener('dragstart', handleDragStart, true);
            document.removeEventListener('dragend', handleDragEnd, true);
            document.removeEventListener('drop', handleDragEnd, true);
        };
    }, []);

    const showLiveUnreadScope = useCallback((scopeKey) => {
        liveVisibleScopeRef.current = scopeKey;
        const nextVisibleIds = liveUnreadRegistryRef.current.getVisibleUnread(scopeKey);
        setLiveNewItemIds(previous => (
            documentLiveSetsEqual(previous, nextVisibleIds) ? previous : nextVisibleIds
        ));
    }, []);

    const resolveLiveScopeKey = useCallback((folderPath, folderData = null, options = {}) => (
        getDocumentLiveScopeKey({
            folderPath: normalizeDocumentCaseFolderPath(folderPath || '{{Name}}'),
            page: options.page ?? folderData?.pagination?.current_page ?? activeFolderPageRef.current,
            pageSize: options.pageSize
                ?? folderData?.pagination?.per_page
                ?? folderData?.pagination?.page_size
                ?? activePageSizeRef.current
        })
    ), []);

    const recordLiveFolderSnapshot = useCallback((folderPath, folderData, { announceNew = true, page, pageSize } = {}) => {
        const normalizedPath = normalizeDocumentCaseFolderPath(folderPath || '{{Name}}');
        const scopeKey = resolveLiveScopeKey(normalizedPath, folderData, { page, pageSize });
        const authoritativeItems = Array.isArray(folderData?.items)
            ? folderData.items
            : null;
        const nextSnapshot = buildDocumentFolderSnapshot(authoritativeItems !== null
            ? {
                files: authoritativeItems.filter(item => item?.type === 'file'),
                folders: authoritativeItems.filter(item => item?.type === 'folder')
            }
            : {
                files: folderData?.files || [],
                // `folders` remains exhaustive for the navigation tree. Only
                // page-visible folders belong in this page's discovery scope.
                folders: folderData?.visibleFolders
                    ?? folderData?.visible_folders
                    ?? folderData?.folders
                    ?? []
            });
        const result = liveUnreadRegistryRef.current.record(scopeKey, nextSnapshot, { announceNew });
        showLiveUnreadScope(scopeKey);
        return result.unchanged;
    }, [resolveLiveScopeKey, showLiveUnreadScope]);

    const markLiveItemSeen = useCallback((type, item) => {
        const identity = getDocumentLiveItemIdentity(type, item);
        if (!identity.id) return;
        liveUnreadRegistryRef.current.markSeen(identity);
        const nextVisibleIds = liveUnreadRegistryRef.current.getVisibleUnread(
            liveVisibleScopeRef.current
        );
        setLiveNewItemIds(previous => (
            documentLiveSetsEqual(previous, nextVisibleIds) ? previous : nextVisibleIds
        ));
    }, []);

    useLayoutEffect(() => {
        liveFolderBaselineRef.current = (folder, {
            recordSnapshot = true,
            announceNew = true,
            page,
            pageSize: snapshotPageSize
        } = {}) => {
            if (!folder?.path) return;
            if (!recordSnapshot) {
                showLiveUnreadScope(resolveLiveScopeKey(folder.path));
                return;
            }
            recordLiveFolderSnapshot(folder.path, {
                items: Array.isArray(folder.items) ? folder.items : null,
                files: folder.files || [],
                folders: Object.entries(folder.subfolders || {}).map(([name, value]) => (
                    value && !Array.isArray(value) && typeof value === 'object' && value.name
                        ? value
                        : { name }
                ))
            }, { announceNew, page, pageSize: snapshotPageSize });
        };

        return () => {
            liveFolderBaselineRef.current = null;
        };
    }, [recordLiveFolderSnapshot, resolveLiveScopeKey, showLiveUnreadScope]);

    useLayoutEffect(() => {
        // A case switch invalidates every outstanding revision/listing request,
        // even when the next case happens to use the same visible folder path.
        invalidateActiveFolderRequests();
        currentFolderRef.current = null;
        setCurrentFolder(null);
        setLocalFolders({});
        setExpandedFolderPaths(new Set(['{{Name}}']));
        expandedNavigationPathRef.current = null;
        setLocalMetaData([]);
        setAllFolderMetadata({});
        setFolderInitialized(false);
        setSyncInfo(null);
        setSyncStatus({ pending: false, lastChecked: null, error: null });
        setPagination(null);
        setIsFolderLoading(false);
        setLoadingFolderPath(null);
        loadingFolderPathRef.current = null;
        activeFolderPageRef.current = 1;
        folderSortRef.current = DEFAULT_DOCUMENT_FOLDER_SORT;
        setFolderSort(DEFAULT_DOCUMENT_FOLDER_SORT);
        documentCaseBaselinePendingRef.current = true;
        folderEffectRun.current = false;
        folderMutationGenerationRef.current = 0;
        folderMutationHistoryRef.current = [];
        documentUndoStackRef.current = [];
        documentUndoInFlightRef.current = false;
        reconcilingFolderRenamesRef.current.clear();
        liveRefreshInFlightRef.current = null;
        if (ownsLiveUnreadRegistryRef.current) {
            liveUnreadRegistryRef.current.clear();
        }
        liveVisibleScopeRef.current = null;
        liveIncompleteBackoffUntilRef.current.clear();
        documentRevisionRef.current = null;
        liveRevisionFailureLoggedRef.current = false;
        liveRevisionOpaqueFailureCountRef.current = 0;
        liveRevisionOpaqueRetryAfterRef.current = 0;
        setLiveNewItemIds(new Set());
    }, [case_id, invalidateActiveFolderRequests]);

    // Function to refresh the current folder contents
    const refreshCurrentFolder = async (page = 1, folderPathOverride = null, options = {}) => {
        const requestedPath = folderPathOverride || currentFolderRef.current?.path;
        if (!requestedPath || !fetchFolderContents) return false;
        const targetPath = normalizeDocumentCaseFolderPath(requestedPath);
        const targetPage = Math.max(1, Number(page) || 1);
        const targetSort = normalizeDocumentFolderSort(
            options.sort || options.requestToken?.sort || folderSortRef.current
        );
        const requestToken = options.requestToken
            || beginActiveFolderRequest(targetPath, targetPage, targetSort);
        const targetPageSize = Number(requestToken.pageSize || pageSize);
        if (
            requestToken.path !== targetPath
            || Number(requestToken.page || 1) !== targetPage
            || !documentFolderSortsEqual(requestToken.sort, targetSort)
        ) return false;
        
        try {
            // Fetch fresh contents for the current folder
            const folderData = await fetchFolderContentsWithSort(
                targetPath,
                targetPage,
                targetPageSize,
                targetSort
            );
            
            // Live responses are scoped to the exact path, page and navigation
            // generation captured before the revision request. Never let an old
            // response alter even the snapshot/cache after navigation or paging.
            if (
                folderData
                && folderData.success !== false
                && activeFolderRequestIsCurrent(requestToken)
            ) {
                const unchangedLiveSnapshot = recordLiveFolderSnapshot(targetPath, folderData, {
                    // Once this page has a baseline for a folder/page, every
                    // authoritative reconciliation can discover additions.
                    // A sort-only reorder is the exception: it establishes a
                    // new visible baseline without treating old page-N items
                    // as newly uploaded documents.
                    announceNew: options.announceNew !== false
                });
                if (options.source === 'live' && unchangedLiveSnapshot) {
                    const nextPagination = folderData.pagination || null;
                    activeFolderPageRef.current = Number(
                        nextPagination?.current_page || targetPage
                    );
                    setPagination(previous => (
                        JSON.stringify(previous) === JSON.stringify(nextPagination)
                            ? previous
                            : nextPagination
                    ));
                    return true;
                }
                // Update localFolders with the new data
                setLocalFolders(prev => {
                    const updated = syncFolderCacheWithChildren(prev, targetPath, folderData.folders || []);

                    // Update the current folder's files
                    updated[targetPath] = Object.values(folderData.files || {});

                    return updated;
                });
                
                // Update metadata
                if (folderData.metaData) {
                    setLocalMetaData(prev => {
                        return mergeMetadataEntries(prev, folderData.metaData || []);
                    });
                }
                
                // Update currentFolder's subfolders with fresh metadata
                if (folderData.folders) {
                    // Update global folder metadata
                    const folderMeta = {};
                    folderData.folders.forEach(folder => {
                        const folderPath = `${targetPath}/${folder.name}`;
                        folderMeta[folderPath] = folder;
                    });
                    setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));

                    const nextFolder = {
                        ...(currentFolderRef.current || {}),
                        path: targetPath,
                        files: folderData.files || [],
                        items: folderData.items ?? null,
                        subfolders: folderData.folders.reduce((acc, folder) => {
                            acc[folder.name] = folder;
                            return acc;
                        }, {})
                    };
                    const safeNextFolder = sanitizeCurrentDocumentFolder(nextFolder);
                    if (!safeNextFolder) return false;
                    currentFolderRef.current = safeNextFolder;
                    setCurrentFolder(prev => (
                        normalizeDocumentCaseFolderPath(prev?.path || '{{Name}}') === targetPath
                            ? safeNextFolder
                            : prev
                    ));
                    console.log('refreshCurrentFolder - Setting pagination:', folderData.pagination);
                    activeFolderPageRef.current = Number(folderData.pagination?.current_page || targetPage);
                    setPagination(folderData.pagination || null);
                }
                return true;
            }
        } catch (error) {
            console.error('Error refreshing current folder:', error);
        }
        return false;
    };

    liveRefreshFunctionRef.current = refreshCurrentFolder;

    const handleFolderSortChange = async (sortBy, sortDirection) => {
        const nextSort = normalizeDocumentFolderSort({
            by: sortBy,
            direction: sortDirection
        });
        if (documentFolderSortsEqual(nextSort, folderSortRef.current)) return true;

        // Update the ref before starting the request so every stale request is
        // rejected immediately, even before its AbortController observes the
        // navigation invalidation.
        folderSortRef.current = nextSort;
        setFolderSort(nextSort);
        documentRevisionRef.current = null;

        const activePath = currentFolderRef.current?.path;
        if (!activePath) return true;
        return refreshCurrentFolder(1, activePath, {
            sort: nextSort,
            announceNew: false
        });
    };

    useEffect(() => {
        const refreshVisibleFolder = async ({ force = false } = {}) => {
            if (
                liveRevisionUnsupportedRef.current
                || document.visibilityState === 'hidden'
                || isFolderLoadingRef.current
            ) return;
            if (liveRefreshInFlightRef.current) return;

            const now = Date.now();
            if (liveRevisionOpaqueRetryAfterRef.current > now) return;
            if (liveRevisionOpaqueRetryAfterRef.current) {
                liveRevisionOpaqueRetryAfterRef.current = 0;
            }
            const currentPath = currentFolderRef.current?.path;
            if (!currentPath || !liveRefreshFunctionRef.current) return;
            const activePath = normalizeDocumentCaseFolderPath(currentPath);
            const incompleteBackoffUntil = Number(
                liveIncompleteBackoffUntilRef.current.get(activePath) || 0
            );
            if (incompleteBackoffUntil > now) return;
            if (incompleteBackoffUntil) {
                liveIncompleteBackoffUntilRef.current.delete(activePath);
            }
            if (!force && now - lastLiveRefreshAtRef.current < DOCUMENT_LIVE_REFRESH_INTERVAL_MS - 1000) {
                return;
            }

            const activePage = Math.max(1, Number(activeFolderPageRef.current || 1));
            const requestToken = captureFolderRequest(
                activePath,
                folderNavigationGenerationRef.current,
                activePage,
                activePageSizeRef.current,
                folderSortRef.current
            );
            const liveRun = Symbol('document-live-refresh');
            const revisionController = new AbortController();

            liveRefreshInFlightRef.current = liveRun;
            liveRevisionControllerRef.current = revisionController;
            lastLiveRefreshAtRef.current = now;
            try {
                const revision = await requestDocumentLiveRevision({
                    caseId: case_id,
                    path: activePath,
                    signal: revisionController.signal
                });
                liveRevisionOpaqueFailureCountRef.current = 0;
                liveRevisionOpaqueRetryAfterRef.current = 0;
                if (!activeFolderRequestIsCurrent(requestToken)) return;
                if (
                    revision.path
                    && normalizeDocumentCaseFolderPath(revision.path) !== requestToken.path
                ) return;

                const priorRevision = documentRevisionRef.current;
                const authoritativeRevision = revision.complete === true
                    || (revision.complete === undefined && revision.truncated !== true);
                if (authoritativeRevision) {
                    liveIncompleteBackoffUntilRef.current.delete(requestToken.path);
                } else {
                    liveIncompleteBackoffUntilRef.current.set(
                        requestToken.path,
                        Date.now() + DOCUMENT_LIVE_INCOMPLETE_BACKOFF_MS
                    );
                }
                if (
                    authoritativeRevision
                    && priorRevision?.path === requestToken.path
                    && priorRevision?.revision === revision.revision
                ) {
                    liveRevisionFailureLoggedRef.current = false;
                    return;
                }

                const refreshed = await liveRefreshFunctionRef.current(
                    requestToken.page,
                    requestToken.path,
                    { source: 'live', requestToken }
                );
                if (refreshed && activeFolderRequestIsCurrent(requestToken)) {
                    documentRevisionRef.current = authoritativeRevision
                        ? { path: requestToken.path, revision: revision.revision }
                        : null;
                    liveRevisionFailureLoggedRef.current = false;
                }
            } catch (error) {
                if (error?.code === DOCUMENT_LIVE_UNSUPPORTED_ERROR_CODE) {
                    // Older deployments may not have the compact revision route
                    // yet. Explicit unsupported HTTP statuses are terminal for
                    // this mounted section; the ordinary listing remains usable.
                    liveRevisionUnsupportedRef.current = true;
                    return;
                }
                if (error?.name === 'TypeError') {
                    // A missing cross-origin route can be opaque to fetch, but so
                    // can a brief network outage. Require consecutive failures
                    // at the normal cadence before disabling this optional poll.
                    if (
                        !revisionController.signal.aborted
                        && activeFolderRequestIsCurrent(requestToken)
                    ) {
                        const failureCount = liveRevisionOpaqueFailureCountRef.current + 1;
                        liveRevisionOpaqueFailureCountRef.current = failureCount;
                        if (failureCount >= DOCUMENT_LIVE_OPAQUE_FAILURE_LIMIT) {
                            liveRevisionUnsupportedRef.current = true;
                        } else {
                            liveRevisionOpaqueRetryAfterRef.current = (
                                Date.now() + DOCUMENT_LIVE_REFRESH_INTERVAL_MS
                            );
                        }
                    }
                    return;
                }
                if (
                    error?.name !== 'AbortError'
                    && !revisionController.signal.aborted
                    && activeFolderRequestIsCurrent(requestToken)
                    && !liveRevisionFailureLoggedRef.current
                ) {
                    console.warn('Live document refresh is temporarily unavailable:', error);
                    liveRevisionFailureLoggedRef.current = true;
                }
            } finally {
                if (liveRevisionControllerRef.current === revisionController) {
                    liveRevisionControllerRef.current = null;
                }
                if (liveRefreshInFlightRef.current === liveRun) {
                    liveRefreshInFlightRef.current = null;
                }
            }
        };

        const interval = window.setInterval(refreshVisibleFolder, DOCUMENT_LIVE_REFRESH_INTERVAL_MS);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshVisibleFolder({ force: true });
            }
        };
        const handleFocus = () => refreshVisibleFolder({ force: true });

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            abortLiveRevisionRequest();
        };
    }, [abortLiveRevisionRequest, activeFolderRequestIsCurrent, captureFolderRequest, case_id]);

    // Refresh the root folder tree without switching the current folder view
    const refreshFolderTree = async () => {
        if (!fetchFolderContents) return;
        if (isFolderLoadingRef.current || loadingFolderPathRef.current) return;
        // Tree inventory is cache work. Stop an older live poll, but do not
        // supersede a user navigation/pagination request.
        abortLiveRevisionRequest();
        const requestPageSize = activePageSizeRef.current;
        const requestToken = captureFolderRequest(
            '{{Name}}',
            folderNavigationGenerationRef.current,
            1,
            requestPageSize
        );

        try {
            const rootData = await fetchFolderContentsWithSort('{{Name}}', 1, requestPageSize);
            if (rootData && rootData.success !== false && folderRequestIsCurrent(requestToken)) {
                const rootIsActive = normalizeDocumentCaseFolderPath(
                    currentFolderRef.current?.path || '{{Name}}'
                ) === '{{Name}}';
                const mayReplaceActivePage = !rootIsActive || activeFolderRequestIsCurrent(requestToken);
                setLocalFolders(prev => {
                    const updated = syncFolderCacheWithChildren(prev, '{{Name}}', rootData.folders || []);
                    if (mayReplaceActivePage) {
                        updated['{{Name}}'] = Object.values(rootData.files || {});
                    }
                    return updated;
                });

                if (rootData.metaData) {
                    setLocalMetaData(prev => {
                        return mergeMetadataEntries(prev, rootData.metaData || []);
                    });
                }

                if (rootData.folders) {
                    const folderMeta = {};
                    rootData.folders.forEach(folder => {
                        const folderPath = `{{Name}}/${folder.name}`;
                        folderMeta[folderPath] = folder;
                    });
                    setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));

                    if (rootIsActive && activeFolderRequestIsCurrent(requestToken)) {
                        recordLiveFolderSnapshot('{{Name}}', rootData);
                        const nextFolder = {
                            ...(currentFolderRef.current || {}),
                            path: '{{Name}}',
                            files: rootData.files || [],
                            items: rootData.items ?? null,
                            subfolders: rootData.folders.reduce((acc, folder) => {
                                acc[folder.name] = folder;
                                return acc;
                            }, {})
                        };
                        const safeNextFolder = sanitizeCurrentDocumentFolder(nextFolder);
                        if (!safeNextFolder) return;
                        currentFolderRef.current = safeNextFolder;
                        setCurrentFolder(prev => (
                            normalizeDocumentCaseFolderPath(prev?.path) === '{{Name}}'
                                ? safeNextFolder
                                : prev
                        ));
                    }
                }
            }
        } catch (error) {
            console.error('Error refreshing root folder tree:', error);
        }
    };

    const refreshFolderPathCache = async (targetPath, { updateActiveView = true } = {}) => {
        if (!targetPath || !fetchFolderContents) return;

        const normalizedTargetPath = normalizeDocumentCaseFolderPath(targetPath);
        const requestPageSize = activePageSizeRef.current;
        const requestToken = captureFolderRequest(
            normalizedTargetPath,
            folderNavigationGenerationRef.current,
            1,
            requestPageSize
        );
        const folderData = await fetchFolderContentsWithSort(
            normalizedTargetPath,
            1,
            requestPageSize
        );
        if (
            !folderData
            || folderData.success === false
            || !folderRequestIsCurrent(requestToken)
        ) return;

        const targetIsActive = normalizeDocumentCaseFolderPath(
            currentFolderRef.current?.path || '{{Name}}'
        ) === normalizedTargetPath;
        const mayReplaceActivePage = !targetIsActive || activeFolderRequestIsCurrent(requestToken);
        setLocalFolders(prev => {
            const updated = syncFolderCacheWithChildren(prev, normalizedTargetPath, folderData.folders || []);
            if (mayReplaceActivePage) {
                updated[normalizedTargetPath] = Object.values(folderData.files || {});
            }
            return updated;
        });

        if (folderData.metaData) {
            setLocalMetaData(prev => mergeMetadataEntries(prev, folderData.metaData || []));
        }

        const folderMeta = {};
        (folderData.folders || []).forEach(folder => {
            folderMeta[`${normalizedTargetPath}/${folder.name}`] = folder;
        });
        setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));

        // The user may navigate while this request is in flight. Update the
        // visible folder only when it is still the exact path we fetched.
        if (
            updateActiveView
            && activeFolderRequestIsCurrent(requestToken)
        ) {
            const nextFolder = {
                ...(currentFolderRef.current || {}),
                path: normalizedTargetPath,
                files: folderData.files || [],
                items: folderData.items ?? null,
                subfolders: (folderData.folders || []).reduce((acc, folder) => {
                    acc[folder.name] = folder;
                    return acc;
                }, {})
            };
            const safeNextFolder = sanitizeCurrentDocumentFolder(nextFolder);
            if (!safeNextFolder) return;
            currentFolderRef.current = safeNextFolder;
            setCurrentFolder(prev => (
                normalizeDocumentCaseFolderPath(prev?.path || '{{Name}}') === normalizedTargetPath
                    ? safeNextFolder
                    : prev
            ));
            setPagination(folderData.pagination || null);
        }
    };

    const reconcileFolderRename = ({
        success,
        sourceKey,
        targetKey,
        sourcePath: providedSourcePath,
        targetPath: providedTargetPath
    }) => {
        const sourcePath = normalizeDocumentCaseFolderPath(providedSourcePath || sourceKey);
        const targetPath = normalizeDocumentCaseFolderPath(providedTargetPath || targetKey);
        const mutation = {
            caseId: case_id,
            generation: folderMutationGenerationRef.current + 1,
            sourcePath,
            targetPath
        };
        folderMutationGenerationRef.current = mutation.generation;
        folderMutationHistoryRef.current.push(mutation);

        if (
            loadingFolderPathRef.current
            && documentFolderRenameInvalidatesRequestPath(
                loadingFolderPathRef.current,
                sourcePath,
                targetPath
            )
        ) {
            folderNavigationGenerationRef.current += 1;
            loadingFolderPathRef.current = null;
            setLoadingFolderPath(null);
            setIsFolderLoading(false);
        }

        const applyActiveViewChange = () => {
            const currentSnapshot = currentFolderRef.current;
            if (!currentSnapshot?.path) return null;

            const nextFolder = reconcileDocumentCurrentFolder(
                currentSnapshot,
                sourcePath,
                targetPath,
                success
            );
            const safeNextFolder = sanitizeCurrentDocumentFolder(nextFolder);
            if (!safeNextFolder) return currentSnapshot.path;
            currentFolderRef.current = safeNextFolder;
            setCurrentFolder(prev => {
                if (!prev?.path) return prev;
                return sanitizeCurrentDocumentFolder(reconcileDocumentCurrentFolder(
                    prev,
                    sourcePath,
                    targetPath,
                    success
                )) || prev;
            });
            return safeNextFolder.path;
        };

        if (success) {
            setLocalFolders(prev => remapDocumentFolderRecord(prev, sourcePath, targetPath));
            setAllFolderMetadata(prev => remapDocumentFolderRecord(prev, sourcePath, targetPath));
            setLocalMetaData(prev => remapDocumentFolderMetadata(prev, sourcePath, targetPath));
            setExpandedFolderPaths(prev => reconcileRenamedDocumentExpandedFolders(
                prev,
                sourcePath,
                targetPath,
                true
            ));
        } else {
            // A failed operation leaves source authoritative; discard any
            // optimistic target cache before reloading the parent/source.
            setLocalFolders(prev => removeDocumentFolderRecord(prev, targetPath));
            setAllFolderMetadata(prev => removeDocumentFolderRecord(prev, targetPath));
            setLocalMetaData(prev => removeDocumentFolderMetadata(prev, targetPath));
            setExpandedFolderPaths(prev => reconcileRenamedDocumentExpandedFolders(
                prev,
                sourcePath,
                targetPath,
                false
            ));
        }

        applyActiveViewChange();

        const parentPaths = getDocumentFolderRenameParentPaths(sourcePath, targetPath);
        const refreshPromise = (async () => {
            // Refresh the parent listing first: that is where the old child must
            // disappear and the renamed child must appear.
            await Promise.all(parentPaths.map(parentPath => refreshFolderPathCache(parentPath)));

            if (String(mutation.caseId ?? '') !== documentsCaseIdRef.current) return;

            // Re-read the navigation ref after the async boundary. A user who
            // moved elsewhere must never be pulled back into the renamed tree.
            const activePathAfterParentRefresh = applyActiveViewChange();
            const authoritativeScope = success ? targetPath : sourcePath;
            if (
                activePathAfterParentRefresh
                && isDocumentFolderPathWithin(activePathAfterParentRefresh, authoritativeScope)
                && !parentPaths.includes(activePathAfterParentRefresh)
            ) {
                await refreshFolderPathCache(activePathAfterParentRefresh);
            }
        })();

        return { sourcePath, targetPath, parentPaths, refreshPromise };
    };

    reconcileFolderRenameRef.current = reconcileFolderRename;

    useLayoutEffect(() => {
        const terminalRenames = getTerminalDocumentFolderRenames(folderRenames, case_id);

        terminalRenames.forEach(rename => {
            if (reconcilingFolderRenamesRef.current.has(rename.id)) return;
            reconcilingFolderRenamesRef.current.add(rename.id);

            let reconciliation;
            try {
                reconciliation = reconcileFolderRenameRef.current(rename);
            } catch (error) {
                reconcilingFolderRenamesRef.current.delete(rename.id);
                showMessage?.({
                    type: 'error',
                    text: `The folder rename finished, but its local folder state could not be reconciled: ${error?.message || 'Reload the page.'}`
                });
                return;
            }

            // Synchronous path/cache remapping is complete. Acknowledge this
            // exact terminal record now; parent/current refreshes remain
            // non-blocking and cannot hijack later navigation.
            uploadDispatch({
                type: 'ACK_FOLDER_RENAME',
                payload: { id: rename.id }
            });
            reconciliation?.refreshPromise?.catch(error => {
                showMessage?.({
                    type: 'warning',
                    text: `The folder rename finished, but the folder listing could not refresh: ${error?.message || 'Reload the page.'}`
                });
            });
        });
    }, [folderRenames, case_id, uploadDispatch, showMessage]);

    const startFolderLoad = useCallback((path) => {
        const normalizedPath = normalizeDocumentCaseFolderPath(path);
        if (!isDocumentCaseFolderPathAllowed(normalizedPath)) {
            throw new Error('The document folder path is outside the current case.');
        }
        const requestToken = beginActiveFolderRequest(normalizedPath, 1);
        setIsFolderLoading(true);
        setLoadingFolderPath(normalizedPath);
        loadingFolderPathRef.current = normalizedPath;
        const previousFolder = currentFolderRef.current;
        const nextFolder = normalizeDocumentCaseFolderPath(previousFolder?.path) === normalizedPath
            ? { ...previousFolder, path: normalizedPath, files: [], items: [], subfolders: {} }
            : { path: normalizedPath, files: [], items: [], subfolders: {} };
        // The empty loading placeholder is not an authoritative listing and
        // must never clear unread markers retained for this page lifetime.
        commitCurrentFolder(nextFolder, { recordSnapshot: false });
        return requestToken;
    }, [beginActiveFolderRequest, commitCurrentFolder]);

    const finishFolderLoad = useCallback((requestToken) => {
        if (
            !requestToken
            || Number(requestToken.navigationGeneration) !== folderNavigationGenerationRef.current
            || loadingFolderPathRef.current !== requestToken.path
        ) {
            return;
        }
        setIsFolderLoading(false);
        setLoadingFolderPath(null);
        loadingFolderPathRef.current = null;
    }, []);

    // Handle drag and drop of items to folders in navigation
    const handleItemDrop = async (dragData, targetFolderName, requestedOperation = 'move') => {
        try {
            console.log('Item dropped:', dragData, 'Target folder:', targetFolderName);
            const sourceKeys = resolveDocumentDropSourceKeys(dragData);
            if (sourceKeys.length === 0) {
                throw new Error('No document items were found in the drag operation.');
            }
            const operation = requestedOperation === 'copy' ? 'copy' : 'move';
            
            // Build the target path - preserve the full folder structure  
            let targetPath = targetFolderName;
            if (targetFolderName === '{{Name}}') {
                targetPath = ''; // Root folder (API will add {{Name}} prefix)
            } else if (targetPath.startsWith('{{Name}}/')) {
                // Keep the full path after {{Name}}/ - don't strip it
                targetPath = targetPath.replace('{{Name}}/', '');
            }
            // If targetFolderName doesn't start with {{Name}}/, use it as-is
            // The backend will add the proper {{Name}} prefix
            
            console.log(`${operation === 'copy' ? 'Copying' : 'Moving'} to target path:`, targetPath);

            const uploadContext = { dispatch: uploadDispatch };
            const result = operation === 'copy'
                ? await simpleUploadService.copyFilesWithProgress(
                    sourceKeys,
                    case_id,
                    targetPath,
                    uploadContext,
                    {},
                    { userId: user_id }
                )
                : await simpleUploadService.moveFiles(
                    sourceKeys,
                    targetPath,
                    case_id,
                    uploadContext,
                    {},
                    null,
                    { userId: user_id }
                );

            const transferOutcome = getDocumentTransferOutcome(result, operation === 'copy');
            const refreshTransferListings = () => settleDocumentMutationRefreshes([
                () => refreshCurrentFolder(activeFolderPageRef.current || 1),
                () => refreshFolderTree(),
            ]);
            const publishTransferReceipt = () => publishDocumentUndoReceipt({
                caseId: case_id,
                userId: user_id,
                operation,
                result,
                sourceKeys,
            });

            if (transferOutcome.recoveryRequired) {
                const refreshOutcome = await refreshTransferListings();
                publishTransferReceipt();
                showMessage?.({
                    type: 'warning',
                    text: `${getDocumentTransferRecoveryMessage(result, operation === 'copy')}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`,
                });
            } else if (transferOutcome.outcomeUnknown) {
                const refreshOutcome = await refreshTransferListings();
                showMessage?.({
                    type: 'error',
                    text: `${getDocumentTransferRecoveryMessage(result, operation === 'copy')}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`,
                });
            } else if (transferOutcome.terminal && transferOutcome.undone) {
                const refreshOutcome = await refreshTransferListings();
                publishTransferReceipt();
                showMessage?.({
                    type: 'warning',
                    text: `${result?.message || `The ${operation} was already undone.`}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`,
                });
            } else if (transferOutcome.committed) {
                if (operation === 'move') {
                    window.dispatchEvent(new CustomEvent(DOCUMENT_SELECTION_MOVE_EVENT, {
                        detail: { caseId: case_id, sourceKeys }
                    }));
                }

                // The server mutation is already committed. Reconciliation is
                // best effort and must never turn success into a retry prompt.
                const refreshOutcome = await refreshTransferListings();
                const undoUnavailable = getDocumentUndoUnavailableMessage(result);
                const messageObj = {
                    type: refreshOutcome.success ? 'success' : 'warning',
                    text: `${sourceKeys.length === 1 ? 'Item' : `${sourceKeys.length} selected items`} ${operation === 'copy' ? 'copied' : 'moved'} successfully!${undoUnavailable ? ` ${undoUnavailable}` : ''}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`
                };
                showMessage?.(messageObj);
                // Publish only after selection cleanup and refresh settlement,
                // preventing a fast Ctrl/Cmd+Z from racing stale UI work.
                publishTransferReceipt();
            } else {
                throw new Error(result.message || result.error || `Failed to ${operation} the selected items.`);
            }
        } catch (error) {
            console.error('Error in handleItemDrop:', error);
            showMessage?.({ type: 'error', text: error.message || 'The document operation failed.' });
        }
    };

    refreshCurrentFolderActionRef.current = refreshCurrentFolder;
    refreshFolderTreeActionRef.current = refreshFolderTree;

    useEffect(() => {
        documentUndoStackRef.current = [];
        documentUndoInFlightRef.current = false;

        const recordReceipt = (event) => {
            const receipt = event?.detail;
            if (String(receipt?.caseId ?? '') !== String(case_id ?? '')) return;
            if (receipt?.receiptId) {
                // Durable actions are undone explicitly from Recent actions.
                // Drop older token-only entries so Ctrl/Cmd+Z cannot undo a
                // different, earlier mutation out of order.
                documentUndoStackRef.current = [];
                return;
            }
            documentUndoStackRef.current = appendDocumentUndoReceipt(
                documentUndoStackRef.current,
                receipt
            );
        };

        const handleUndoKeyDown = async (event) => {
            documentUndoStackRef.current = pruneExpiredDocumentUndoReceipts(
                documentUndoStackRef.current
            );
            const receipt = documentUndoStackRef.current.at(-1);
            if (!isDocumentUndoKeyboardEvent(event, Boolean(receipt))
                || String(receipt?.caseId ?? '') !== String(case_id ?? '')) return;

            event.preventDefault();
            if (documentUndoInFlightRef.current) return;
            documentUndoInFlightRef.current = true;
            try {
                let result;
                try {
                    result = await executeDocumentUndoReceipt({
                        receipt,
                        caseId: case_id,
                        dispatch: uploadDispatch,
                    });
                } catch (error) {
                    // The inverse did not commit, so preserve the receipt for a
                    // conflict resolution or transient retry.
                    documentShowMessageRef.current?.({
                        type: 'error',
                        text: error.message || 'CaseDB could not safely undo that operation.',
                    });
                    return;
                }

                // The inverse committed. Pop immediately; a listing failure
                // must not make the destructive inverse eligible to run twice.
                documentUndoStackRef.current = documentUndoStackRef.current.filter(
                    candidate => candidate.undoToken !== receipt.undoToken
                );
                const refreshOutcome = await settleDocumentMutationRefreshes([
                    () => refreshCurrentFolderActionRef.current?.(activeFolderPageRef.current || 1),
                    () => refreshFolderTreeActionRef.current?.(),
                ]);
                documentShowMessageRef.current?.({
                    type: refreshOutcome.success ? 'success' : 'warning',
                    text: `${result.message || `${receipt.operation === 'copy' ? 'Copy' : 'Move'} undone successfully.`}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`,
                });
            } finally {
                documentUndoInFlightRef.current = false;
            }
        };

        window.addEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, recordReceipt);
        window.addEventListener('keydown', handleUndoKeyDown);
        return () => {
            window.removeEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, recordReceipt);
            window.removeEventListener('keydown', handleUndoKeyDown);
            documentUndoStackRef.current = [];
            documentUndoInFlightRef.current = false;
        };
    }, [case_id, uploadDispatch]);

    // Expose refreshCurrentFolder function to parent component
    useImperativeHandle(ref, () => ({
        refreshCurrentFolder,
        refreshFolderTree
    }));

    // Pagination handler for DocumentSection
    const handleFolderPagination = async (page) => {
        const targetPath = currentFolderRef.current?.path;
        if (targetPath && fetchFolderContents) {
            const previousPage = Number(activeFolderPageRef.current || 1);
            const targetPage = Math.max(1, Number(page) || 1);
            const requestToken = beginActiveFolderRequest(targetPath, targetPage);
            try {
                const folderData = await fetchFolderContentsWithSort(
                    targetPath,
                    targetPage,
                    pageSize,
                    requestToken.sort
                );
                
                if (
                    folderData
                    && folderData.success !== false
                    && activeFolderRequestIsCurrent(requestToken)
                ) {
                    recordLiveFolderSnapshot(requestToken.path, folderData);
                    // Extract pagination data
                    if (folderData.pagination) {
                        console.log('handleFolderPagination - Setting pagination:', folderData.pagination);
                        activeFolderPageRef.current = Number(folderData.pagination.current_page || targetPage);
                        setPagination(folderData.pagination);
                    }
                    
                    // Update current folder files
                    const nextFiles = Object.values(folderData.files || {});
                    const safeNextFolder = sanitizeCurrentDocumentFolder({
                        ...(currentFolderRef.current || {}),
                        path: requestToken.path,
                        files: nextFiles,
                        items: folderData.items ?? null
                    });
                    if (!safeNextFolder) return;
                    currentFolderRef.current = safeNextFolder;
                    setCurrentFolder(prev => (
                        normalizeDocumentCaseFolderPath(prev?.path) === requestToken.path
                            ? safeNextFolder
                            : prev
                    ));
                    
                    // Update localFolders
                    setLocalFolders(prev => ({
                        ...prev,
                        [requestToken.path]: nextFiles
                    }));
                    
                    // Update metadata
                    if (folderData.metaData) {
                        setLocalMetaData(prev => {
                            const filtered = prev.filter(meta => 
                                !meta.file_path?.startsWith(`cases/${case_id}/${requestToken.path}`)
                            );
                            return mergeMetadataEntries(filtered, folderData.metaData || []);
                        });
                    }
                } else if (activeFolderRequestIsCurrent(requestToken)) {
                    activeFolderPageRef.current = previousPage;
                }
            } catch (error) {
                console.error('Error handling folder pagination:', error);
                if (activeFolderRequestIsCurrent(requestToken)) {
                    activeFolderPageRef.current = previousPage;
                }
            }
        }
    };

    const buildFolderTree = () => {
        const root = {};
        folderEntries.forEach(([path, files]) => {
            const parts = path.split('/');
            let current = root;

            parts.forEach((part, index) => {
                if (!current[part]) current[part] = { __files: [] };
                if (index === parts.length - 1) {
                    current[part].__files = files;
                }
                current = current[part];
            });
        });

        return root;
    };

    useEffect(() => {
        // Initialize with {{Name}} only
        const initializeDocuments = async () => {
            if (fetchFolderContents && case_id && !folderInitialized) {
                try {
                    const foldersData = {};
                    const folderMeta = {};
                    let nameData = null;
                    const requestToken = captureFolderRequest('{{Name}}');

                    // Fetch {{Name}} folder
                    try {
                        nameData = await fetchFolderContentsWithSort('{{Name}}', 1, pageSize);
                        if (
                            nameData
                            && nameData.success !== false
                            && folderRequestIsCurrent(requestToken)
                        ) {
                            if (nameData.sync_info) {
                                setSyncInfo(nameData.sync_info);
                            }
                            setPagination(nameData.pagination || null);
                            foldersData['{{Name}}'] = Object.values(nameData.files || {});

                            if (nameData.folders) {
                                nameData.folders.forEach(folder => {
                                    const subFolderPath = `{{Name}}/${folder.name}`;
                                    foldersData[subFolderPath] = [];
                                    folderMeta[subFolderPath] = folder;
                                });
                            }

                            setLocalMetaData(sanitizeDocumentCaseMetadata(nameData.metaData || []));
                        }
                    } catch (error) {
                        console.error('Error fetching {{Name}} folder contents:', error);
                    }

                    if (!folderRequestIsCurrent(requestToken)) return;
                    documentCaseBaselinePendingRef.current = false;

                    if (Object.keys(folderMeta).length > 0) {
                        setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));
                    }

                    setLocalFolders(sanitizeDocumentCaseFolders(foldersData));
                    setFolderInitialized(true);

                    if (nameData && nameData.success !== false) {
                        commitCurrentFolder({
                            path: '{{Name}}',
                            files: nameData.files || [],
                            items: nameData.items ?? null,
                            subfolders: nameData.folders ? nameData.folders.reduce((acc, folder) => {
                                acc[folder.name] = folder;
                                return acc;
                            }, {}) : {},
                        }, {
                            // A fresh case/page registry has no prior snapshot,
                            // so this remains its baseline. A registry retained
                            // across a same-case section remount can announce
                            // documents that arrived while Documents was away.
                            announceNew: true,
                            page: nameData.pagination?.current_page || 1,
                            pageSize: nameData.pagination?.per_page
                                ?? nameData.pagination?.page_size
                                ?? pageSize
                        });
                    }
                } catch (error) {
                    console.error('Error initializing documents:', error);
                    documentCaseBaselinePendingRef.current = false;
                    // Fallback to old approach if available
                    if (folders && Object.keys(folders).length > 0) {
                        setLocalFolders(sanitizeDocumentCaseFolders(folders));
                        setLocalMetaData(prev => prev.length > 0
                            ? prev
                            : sanitizeDocumentCaseMetadata(metaData));
                        setFolderInitialized(true);
                    }
                }
            } else if (folders && Object.keys(folders).length > 0 && !folderInitialized) {
                // Fallback to old data if new approach isn't available
                // Only set metadata if we don't have any yet to prevent overwriting folder-specific metadata
                documentCaseBaselinePendingRef.current = false;
                setLocalFolders(sanitizeDocumentCaseFolders(folders));
                setLocalMetaData(prev => prev.length > 0
                    ? prev
                    : sanitizeDocumentCaseMetadata(metaData));
                setFolderInitialized(true);
            }
        };

        initializeDocuments();
    }, [fetchFolderContents, fetchFolderContentsWithSort, case_id, folderInitialized, folders, metaData, pageSize, captureFolderRequest, folderRequestIsCurrent, commitCurrentFolder]);

    useEffect(() => {
        if (documentCaseBaselinePendingRef.current) return;
        if (!localFolders || Object.keys(localFolders).length === 0) return;
    
        const params = new URLSearchParams(location.search);
        const folderParam = params.get("folder");
        
        console.log('URL parameter processing:', { folderParam, localFolders: Object.keys(localFolders) });
    
        // Look for the {{Name}} folder which is always returned by the API
        const nameFolder = Object.keys(localFolders).find((key) => key === "{{Name}}");
        
        // If no {{Name}} folder found, use the first top-level folder as root
        const rootFolder = nameFolder || Object.keys(localFolders)[0];
        
        if (!rootFolder) return;

        if (folderParam && !folderEffectRun.current) {
            const fullPath = `${rootFolder}/${folderParam}`;
            if (!isDocumentCaseFolderPathAllowed(fullPath)) {
                params.delete('folder');
                const safeUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
                window.history.replaceState({}, '', safeUrl);
                folderEffectRun.current = true;
                return;
            }
            // A cached folder contains only the page/sort that populated it.
            // Always fetch page one for the active sort before making URL-based
            // navigation visible.
            const fetchTargetFolder = async () => {
                    const requestToken = startFolderLoad(fullPath);
                    try {
                        // For API call, always use the full path
                        const apiPath = `${rootFolder}/${folderParam}`;
                        console.log('Fetching folder with details:', { 
                            rootFolder, 
                            folderParam, 
                            apiPath, 
                            fullPath 
                        });
                        
                        const result = await fetchFolderContentsWithSort(
                            apiPath,
                            1,
                            pageSize,
                            requestToken.sort
                        );
                        console.log('Fetch result:', result);
                        console.log('API result.folders:', result?.folders);
                        
                        if (result && activeFolderRequestIsCurrent(requestToken)) {
                            const subfoldersObj = result.folders ? result.folders.reduce((acc, folder) => {
                                acc[folder.name] = folder;
                                return acc;
                            }, {}) : {};

                            // Update global folder metadata
                            if (result.folders) {
                                const folderMeta = {};
                                result.folders.forEach(folder => {
                                    const folderPath = `${fullPath}/${folder.name}`;
                                    folderMeta[folderPath] = folder;
                                });
                                setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));
                            }

                            console.log('Setting currentFolder with:', {
                                path: fullPath,
                                filesCount: Object.keys(result.files || {}).length,
                                resultFolders: result.folders,
                                subfoldersObj,
                                subfoldersCount: Object.keys(subfoldersObj).length
                            });
                            
                            commitCurrentFolder({
                                path: fullPath,
                                files: result.files || [],
                                items: result.items ?? null,
                                subfolders: subfoldersObj,
                            });
                            activeFolderPageRef.current = Number(result.pagination?.current_page || 1);
                            setPagination(result.pagination);
                            
                            // Update localFolders with the fetched data
                            setLocalFolders(prev => ({
                                ...prev,
                                [fullPath]: Object.values(result.files || {})
                            }));
                            
                            // CRITICAL: Update localMetaData with metadata for this folder
                            console.log('Updating metadata for folder navigation:', {
                                fullPath: fullPath,
                                metaDataCount: (result.metaData || []).length,
                                metaDataSample: (result.metaData || []).slice(0, 3)
                            });
                            setLocalMetaData(sanitizeDocumentCaseMetadata(result.metaData || []));

                            // Only clear the folder parameter, leave file parameter for DocumentSection
                            params.delete("folder");
                            const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
                            window.history.replaceState({}, "", newUrl);

                            folderEffectRun.current = true;
                        }
                    } catch (error) {
                        console.error("Error fetching target folder:", error);
                    } finally {
                        finishFolderLoad(requestToken);
                    }
            };

            fetchTargetFolder();
            return;
        }

        if (!currentFolderRef.current && rootFolder) {
            const subfolderTree = Object.fromEntries(
                Object.entries(localFolders)
                    .filter(([key]) => key.startsWith(`${rootFolder}/`))
                    .map(([key, value]) => {
                        const sub = key.replace(`${rootFolder}/`, '').split('/')[0];
                        return [sub, value];
                    })
            );

            commitCurrentFolder({
                path: rootFolder,
                files: localFolders[rootFolder] || [],
                subfolders: subfolderTree,
            });
        }
    }, [
        localFolders,
        location.search,
        caseName,
        activeFolderRequestIsCurrent,
        beginActiveFolderRequest,
        commitCurrentFolder,
        fetchFolderContents,
        fetchFolderContentsWithSort,
        finishFolderLoad,
        pageSize,
        startFolderLoad
    ]);

    useEffect(() => {
        if (documentCaseBaselinePendingRef.current) return;
        setCurrentFolder(prev => {
            if (!prev?.path) return prev;
            const updatedFiles = localFolders[prev.path] || [];
            const updatedSubfolders = Object.fromEntries(
                Object.entries(localFolders)
                    .filter(([key]) => key.startsWith(`${prev.path}/`))
                    .map(([key, value]) => {
                        const sub = key.replace(`${prev.path}/`, '').split('/')[0];
                        return [sub, value];
                    })
            );
            const prevKeys = Object.keys(prev.subfolders || {}).sort().join(',');
            const newKeys = Object.keys(updatedSubfolders).sort().join(',');
    
            const fileChanged = JSON.stringify(prev.files) !== JSON.stringify(updatedFiles);
            const subfolderChanged = prevKeys !== newKeys;
    
            if (!fileChanged && !subfolderChanged) return prev;
    
            const nextFolder = {
                ...prev,
                files: updatedFiles,
                subfolders: updatedSubfolders
            };
            const safeFolder = sanitizeCurrentDocumentFolder(nextFolder);
            if (!safeFolder) return prev;
            currentFolderRef.current = safeFolder;
            return safeFolder;
        });
    }, [localFolders]);

    // Reset pagination when currentFolder path changes to a different folder
    // Don't reset if it's just an update to the same folder
    const prevFolderPath = useRef(null);
    useEffect(() => {
        if (currentFolder?.path && currentFolder.path !== prevFolderPath.current) {
            console.log('Folder path changed from:', prevFolderPath.current, 'to:', currentFolder.path, 'Resetting pagination');
            // Only reset pagination if we're not in the middle of an API call that will set it
            // We'll let the API calls handle setting pagination directly
            prevFolderPath.current = currentFolder.path;
        }
    }, [currentFolder?.path]);

    // Refresh current folder when page size changes
    useEffect(() => {
        const activeFolder = currentFolderRef.current;
        if (activeFolder?.path && fetchFolderContents && liveRefreshFunctionRef.current) {
            console.log('Page size changed to:', pageSize, 'Refreshing current folder');
            liveRefreshFunctionRef.current(1, activeFolder.path); // Reset to page 1 with new page size
        }
    }, [fetchFolderContents, pageSize]);

    const renderTree = (tree, level = 0, parentPath = '') => {
        return Object.entries(tree).map(([folderName, data]) => {
            const subfolders = Object.fromEntries(
                Object.entries(data).filter(([key]) => key !== '__files')
            );

            const fileList = data.__files || [];
            const fullPath = parentPath ? `${parentPath}/${folderName}` : folderName;
            
            // Get size from allFolderMetadata using full path
            const folderSize = allFolderMetadata[fullPath]?.size || 0;

            return (
                <FolderNode
                    key={fullPath}
                    name={folderName}
                    fullPath={fullPath}
                    files={fileList}
                    caseName={caseName}
                    level={level}
                    subfoldersEl={renderTree(subfolders, level + 1, fullPath)}
                    size={folderSize}
                    onItemDrop={handleItemDrop}
                    fetchFolderContents={fetchFolderContentsWithSort}
                    pageSize={pageSize}
                    isDragActive={isDragActive}
                    isLoading={isFolderLoading && loadingFolderPath === fullPath}
                    isSynced={isPathSynced(fullPath)}
                    isPathSynced={isPathSynced}
                    syncPending={syncStatus.pending}
                    isExpanded={expandedFolderPaths.has(fullPath)}
                    onExpandedChange={setFolderExpanded}
                    onClick={async () => {
                        // Always try to fetch fresh data to get proper pagination
                        // Check if we already have data for this folder in localFolders
                        const existingData = localFolders[fullPath];
                        let requestToken = null;
                        
                        try {
                            requestToken = startFolderLoad(fullPath);
                            // Don't remove {{Name}}/ from the path - the API needs it to locate the correct folder
                            let cleanPath = fullPath;
                            // Only remove case name prefix if it exists (but keep {{Name}})
                            if (caseName && fullPath.startsWith(`${caseName}/`) && !fullPath.includes('{{Name}}')) {
                                cleanPath = fullPath.replace(`${caseName}/`, '{{Name}}/');
                            }
                            console.log('Tree click - Fetching fresh data for:', cleanPath);
                            const folderData = await fetchFolderContentsWithSort(
                                cleanPath,
                                1,
                                pageSize,
                                requestToken.sort
                            );

                            if (folderData === null) {
                                return;
                            }

                            if (
                                folderData
                                && folderData.success !== false
                                && activeFolderRequestIsCurrent(requestToken)
                            ) {
                                if (folderData.sync_info) {
                                    setSyncInfo(folderData.sync_info);
                                }
                                // Always update pagination data (reset if not provided)
                                console.log('Tree click - Setting pagination:', folderData.pagination);
                                setPagination(folderData.pagination || null);
                                
                                // Update local folders with the fetched data
                                const nextFiles = Object.values(folderData.files || {});
                                if (folderData.folders) {
                                    const folderMeta = {};
                                    folderData.folders.forEach(folder => {
                                        const subFolderPath = `${fullPath}/${folder.name}`;
                                        folderMeta[subFolderPath] = folder; // Store full folder object
                                    });
                                    setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));
                                }

                                setLocalFolders(prev => {
                                    const updated = syncFolderCacheWithChildren(
                                        prev,
                                        fullPath,
                                        folderData.folders || []
                                    );
                                    updated[fullPath] = nextFiles;
                                    return updated;
                                });
                                setLocalMetaData(prev => mergeMetadataEntries(prev, folderData.metaData || []));

                                commitCurrentFolder({
                                    path: fullPath,
                                    files: nextFiles,
                                    items: folderData.items ?? null,
                                    subfolders: folderData.folders ? folderData.folders.reduce((acc, folder) => {
                                        acc[folder.name] = folder; // Store the full folder object with metadata
                                        return acc;
                                    }, {}) : {}
                                });
                            } else if (activeFolderRequestIsCurrent(requestToken)) {
                                // Fallback to existing data if API call fails
                                console.log('Tree click - API fallback, resetting pagination');
                                setPagination(null);
                                const actualFiles = existingData && existingData.length > 0 ? existingData : fileList;
                                const subfolders = Object.fromEntries(
                                    Object.entries(data).filter(([key]) => key !== '__files')
                                );
                                commitCurrentFolder({ path: fullPath, files: actualFiles, subfolders });
                            }
                        } catch (error) {
                            console.error('Error fetching folder contents in FolderNode:', error);
                            // Fallback to existing data on error
                            console.log('Tree click - Error fallback, resetting pagination');
                            if (activeFolderRequestIsCurrent(requestToken)) {
                                setPagination(null);
                                const actualFiles = existingData && existingData.length > 0 ? existingData : fileList;
                                const subfolders = Object.fromEntries(
                                    Object.entries(data).filter(([key]) => key !== '__files')
                                );
                                commitCurrentFolder({ path: fullPath, files: actualFiles, subfolders });
                            }
                        } finally {
                            finishFolderLoad(requestToken);
                        }
                    }}
                    isActive={currentFolder?.path === fullPath}
                />
            );
        });
    };

    const folderTree = buildFolderTree();
    const effectiveFolder = currentFolder || { path: '{{Name}}', files: [], subfolders: {} };
    const isLoadingDocuments = !folderInitialized || isFolderLoading;

    return (
        <>
            {windowWidth >= 1000 && (
                <ul className="document-nav" style={docNav ? { width: 'auto', padding: '15px 15px 0 15px', borderRight: '1px solid var(--border-color)' } : { width: '0', minWidth: '0', maxWidth: '0', padding: '0', border: 'none', overflow: 'hidden' }}>
                    {Object.keys(folderTree).length > 0 ? (
                        renderTree(folderTree)
                    ) : !isLoadingDocuments ? (
                        <li>No documents found for case</li>
                    ) : <li>Loading documents...</li>}
                </ul>
            )}
            {(
                <DocumentSection
                    key={case_id}
                    folderName={effectiveFolder.path}
                    folders={localFolders}
                    files={effectiveFolder.files}
                    items={effectiveFolder.items ?? null}
                    metaData={localMetaData}
                    subfolders={effectiveFolder.subfolders}
                    caseName={caseName}
                    caseTypeId={caseType}
                    syncInfo={syncInfo}
                    refreshCurrentFolder={refreshCurrentFolder}
                    refreshFolderTree={refreshFolderTree}
                    sortColumn={folderSort.by}
                    sortDirection={folderSort.direction}
                    onSortChange={handleFolderSortChange}
                    liveNewItemIds={liveNewItemIds}
                    onLiveItemSeen={markLiveItemSeen}
                    isLoading={isLoadingDocuments}
                    onFolderClick={async (subfolderName) => {
                        const activePath = currentFolderRef.current?.path || '{{Name}}';
                        const nextPath = `${activePath}/${subfolderName}`;
                        if (!isDocumentCaseFolderPathAllowed(nextPath)) return;
                        let requestToken = null;
                        
                        try {
                            requestToken = startFolderLoad(nextPath);
                            const cleanPath = nextPath;
                            const folderData = await fetchFolderContentsWithSort(
                                cleanPath,
                                1,
                                pageSize,
                                requestToken.sort
                            );

                            if (folderData === null) {
                                return;
                            }

                            if (
                                folderData
                                && folderData.success !== false
                                && activeFolderRequestIsCurrent(requestToken)
                            ) {
                                // Always update pagination data (reset if not provided)
                                setPagination(folderData.pagination || null);
                                
                                // Update local folders with the fetched data
                                const nextFiles = Object.values(folderData.files || {});
                                if (folderData.folders) {
                                    const folderMeta = {};
                                    folderData.folders.forEach(folder => {
                                        const subFolderPath = `${nextPath}/${folder.name}`;
                                        folderMeta[subFolderPath] = folder; // Store full folder object
                                    });
                                    setAllFolderMetadata(prev => ({ ...prev, ...folderMeta }));
                                }

                                setLocalFolders(prev => {
                                    const updated = syncFolderCacheWithChildren(
                                        prev,
                                        nextPath,
                                        folderData.folders || []
                                    );
                                    updated[nextPath] = nextFiles;
                                    return updated;
                                });
                                setLocalMetaData(prev => mergeMetadataEntries(prev, folderData.metaData || []));

                                commitCurrentFolder({
                                    path: nextPath,
                                    files: nextFiles,
                                    items: folderData.items ?? null,
                                    subfolders: folderData.folders ? folderData.folders.reduce((acc, folder) => {
                                        acc[folder.name] = folder; // Store the full folder object with metadata
                                        return acc;
                                    }, {}) : {}
                                });
                            } else if (activeFolderRequestIsCurrent(requestToken)) {
                                // Fallback to old logic if new approach fails
                                console.log('Folder data not available, resetting pagination');
                                setPagination(null);
                                
                                const fullEntry = localFolders[nextPath];
                                const subfolderTree = Object.fromEntries(
                                    Object.entries(localFolders)
                                        .filter(([key]) => key.startsWith(`${nextPath}/`))
                                        .map(([key, value]) => {
                                            const sub = key.replace(`${nextPath}/`, '').split('/')[0];
                                            return [sub, value];
                                        })
                                );
                                commitCurrentFolder({
                                    path: nextPath,
                                    files: fullEntry || [],
                                    subfolders: subfolderTree
                                });
                            }
                        } catch (error) {
                            console.error('Error fetching folder contents:', error);
                            // Fallback to old logic on error
                            console.log('onFolderClick - Error fallback, resetting pagination');
                            if (activeFolderRequestIsCurrent(requestToken)) {
                                setPagination(null);
                                const fullEntry = localFolders[nextPath];
                                const subfolderTree = Object.fromEntries(
                                    Object.entries(localFolders)
                                        .filter(([key]) => key.startsWith(`${nextPath}/`))
                                        .map(([key, value]) => {
                                            const sub = key.replace(`${nextPath}/`, '').split('/')[0];
                                            return [sub, value];
                                        })
                                );
                                commitCurrentFolder({
                                    path: nextPath,
                                    files: fullEntry || [],
                                    subfolders: subfolderTree
                                });
                            }
                        } finally {
                            finishFolderLoad(requestToken);
                        }
                    }}
                    onBreadcrumbClick={async (path) => {
                        if (!isDocumentCaseFolderPathAllowed(path)) return;
                        let didStartLoad = false;
                        let requestToken = null;
                        try {
                            // A local folder cache is only a page/sort snapshot.
                            // Breadcrumb navigation therefore always reloads
                            // authoritative page one for the active sort.
                            requestToken = startFolderLoad(path);
                            didStartLoad = true;
                            const folderData = await fetchFolderContentsWithSort(
                                path,
                                1,
                                pageSize,
                                requestToken.sort
                            );

                            if (folderData === null) {
                                return;
                            }
                            if (
                                folderData
                                && folderData.success !== false
                                && activeFolderRequestIsCurrent(requestToken)
                            ) {
                                setPagination(folderData.pagination || null);
                                const nextFiles = Object.values(folderData.files || {});
                                setLocalFolders(prev => {
                                    const updated = syncFolderCacheWithChildren(
                                        prev,
                                        path,
                                        folderData.folders || []
                                    );
                                    updated[path] = nextFiles;
                                    return updated;
                                });
                                setLocalMetaData(prev => {
                                    return mergeMetadataEntries(prev, folderData.metaData || []);
                                });

                                commitCurrentFolder({
                                    path,
                                    files: nextFiles,
                                    items: folderData.items ?? null,
                                    subfolders: folderData.folders ? folderData.folders.reduce((acc, folder) => {
                                        acc[folder.name] = folder;
                                        return acc;
                                    }, {}) : {}
                                });
                            } else if (activeFolderRequestIsCurrent(requestToken)) {
                                setPagination(null);
                                const subfolderTree = Object.fromEntries(
                                    Object.entries(localFolders)
                                        .filter(([key]) => key.startsWith(`${path}/`))
                                        .map(([key, value]) => {
                                            const sub = key.replace(`${path}/`, '').split('/')[0];
                                            return [sub, value];
                                        })
                                );
                                commitCurrentFolder({
                                    path,
                                    files: localFolders[path] || [],
                                    items: null,
                                    subfolders: subfolderTree
                                });
                            }
                        } catch (error) {
                            console.error('Error in breadcrumb navigation:', error);
                            // Fallback to old logic on error
                            console.log('Breadcrumb click - Error fallback, resetting pagination');
                            if (!didStartLoad || activeFolderRequestIsCurrent(requestToken)) {
                                setPagination(null);
                                const subfolderTree = Object.fromEntries(
                                    Object.entries(localFolders)
                                        .filter(([key]) => key.startsWith(`${path}/`))
                                        .map(([key, value]) => {
                                            const sub = key.replace(`${path}/`, '').split('/')[0];
                                            return [sub, value];
                                        })
                                );
                                commitCurrentFolder({
                                    path,
                                    files: localFolders[path] || [],
                                    subfolders: subfolderTree
                                });
                            }
                        } finally {
                            if (didStartLoad) {
                                finishFolderLoad(requestToken);
                            }
                        }
                    }}
                    setDocNav={setDocNav}
                    docNav={docNav}
                    case_id={case_id}
                    user_id={user_id}
                    fetchDocuments={fetchDocuments}
                    fetchFolderContents={fetchFolderContentsWithSort}
                    onPageChange={handleFolderPagination}
                    pagination={pagination}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onFileUpload={onFileUpload}
                    initialFileToOpen={fileParam}
                />
            )}
            {currentFolder && console.log('Rendering DocumentSection with pagination:', pagination)}
        </>
    );
});

const parseFieldOptions = (options) => {
    if (!options) return {};
    if (typeof options === 'object') return options;

    try {
        const parsed = JSON.parse(options);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const getCalculationSourceFieldId = (field) => {
    const options = parseFieldOptions(field?.options);
    const getSourceId = (config) => {
        if (!config || typeof config !== 'object') return null;

        const dateConfig = config.date || config.date_calculation || config.dateCalculation || config;
        const sourceFieldId = dateConfig.source_field_id
            ?? dateConfig.sourceFieldId
            ?? dateConfig.date_field_id
            ?? dateConfig.dateFieldId
            ?? dateConfig.source
            ?? dateConfig.field_id
            ?? dateConfig.fieldId
            ?? (Array.isArray(config.field_ids) ? config.field_ids[0] : null);

        const numericSourceFieldId = Number(sourceFieldId);
        if (Number.isFinite(numericSourceFieldId) && numericSourceFieldId > 0) return numericSourceFieldId;

        if (config.fallback) return getSourceId(config.fallback);
        if (Array.isArray(config.rules)) {
            for (const rule of config.rules) {
                const ruleSourceFieldId = getSourceId(rule);
                if (ruleSourceFieldId) return ruleSourceFieldId;
            }
        }

        return null;
    };

    return getSourceId(options);
};

const isDeadlineBasedCalculationField = (field, fields = [], sourceDeadlineId = null) => {
    if (Number(field?.field_id) !== 17) return false;

    const sourceFieldId = getCalculationSourceFieldId(field);
    if (!sourceFieldId) return false;
    if (sourceDeadlineId !== null && Number(sourceFieldId) !== Number(sourceDeadlineId)) return false;

    const sourceField = fields.find(candidate => Number(candidate.id) === Number(sourceFieldId));
    return Number(sourceField?.field_id) === 20;
};

export const Field = ({ field, value, handleFieldChange, conditional = false, formData, newData, fieldUpdates, fields, lead_id, case_id, caseName, user_id, refreshAfterCalc = null, sectionName, section_id = null, setNewContactFieldId, setCreateContact, startCheckRequest, handleEditContact, saveFields, resetTaskflow, selectedRow, groupedData, addItemMode, required = false, handleTaskflowAction = null, prepareDocGenGeneration = null, prepareDocGenReview = null, handleDocGenGenerated = null, settlementBooleanOverride = false, handleSettlementMadeWholeChange = null, domId = null, recommendation = null }) => {
    if (!field) return null;
    const isDeadlineDerivedCalculation = isDeadlineBasedCalculationField(field, fields);

    const currentGroupId = selectedRow?.group_id ?? formData?.group_id ?? null;

    if (settlementBooleanOverride) {
        const responseValue = getSettlementMadeWholeResponseValue(value);
        const booleanValue = (responseValue === undefined || responseValue === null || responseValue === '') ? 2 : Number(responseValue);
        return (
            <div
                id={domId || undefined}
                data-field-id={field.id}
                className={`form-group nm ${required ? 'required-field' : ''}`}
                style={domId ? { scrollMarginTop: '120px' } : undefined}
            >
                <label className='subtext'>{field.name}</label>
                <BooleanField
                    options={SETTLEMENT_MADE_WHOLE_BOOLEAN_OPTIONS}
                    value={booleanValue}
                    required={required}
                    onChange={(selectedValue) => {
                        if (handleSettlementMadeWholeChange) {
                            handleSettlementMadeWholeChange(field.id, selectedValue);
                            return;
                        }

                        handleFieldChange(field.id, selectedValue);
                    }}
                />
            </div>
        );
    }

    if (field.field_id === 21 && !value) {
        // Parse the action config stored in field.options (JSON for taskflow fields)
        let actionConfig = {};
        try {
            actionConfig = typeof field.options === 'string'
                ? JSON.parse(field.options || '{}')
                : (field.options && typeof field.options === 'object' ? field.options : {});
        } catch (e) {
            actionConfig = {};
        }
        const actionType = actionConfig.type || null;

        // No action configured — render nothing
        if (!actionType) return null;

        const buttonLabels = {
            check_request: 'Start Taskflow',
            email: 'Send Email',
            add_to_calendar: 'Add to Calendar'
        };

        const handleTaskflowClick = async () => {
            switch (actionType) {
                case 'check_request': {
                    if (saveFields) {
                        const saveResult = await saveFields();
                        if (saveResult && saveResult.success && saveResult.group_id !== null && saveResult.group_id !== undefined) {
                            startCheckRequest(field.id, saveResult.group_id);
                        } else {
                            startCheckRequest(field.id);
                        }
                    } else {
                        startCheckRequest(field.id);
                    }
                    break;
                }
                case 'email':
                case 'add_to_calendar': {
                    if (handleTaskflowAction) {
                        handleTaskflowAction(actionType, field.id, actionConfig);
                    }
                    break;
                }
                default:
                    break;
            }
        };

        return (
            <div className='form-group nm'>
                <label className='subtext'>{field.name}</label>
                <button
                    className='action small quaternary'
                    onClick={handleTaskflowClick}
                >
                    {buttonLabels[actionType] || 'Start Taskflow'}
                </button>
            </div>
        );
    }

    if (field.field_id === 23) {
        return (
            <ActionTriggerField
                field={field}
                value={value}
                leadId={lead_id}
                caseId={case_id}
                groupId={currentGroupId}
            />
        );
    }

    if (field.field_id === 27) {
        return (
            <FormActivityTriggerField
                field={field}
                value={value}
                leadId={lead_id}
                caseId={case_id}
                groupId={currentGroupId}
            />
        );
    }

    const fetchUsers = async () => {
        const response = await fetch('https://api.casedb.co/user.php?users=true');
        const data = await response.json();
        console.log(value);
        return data.users || [];
    }

    const renderFieldInput = (field, required = false) => {
        switch (field.field_id) {
            case 1:
                return <Text type="text" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 2:
                return <Text type="textarea" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 3:
                return <NumberInput type="currency" value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 4:
                return <NumberInput type="percent" value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 5:
                return <NumberInput type="number" value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 6:
                return <DateInput value={String(value || '')} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 7:
                return <TimeInput value={value} onChange={(val) => handleFieldChange(field.id, val)} required={required} />;
            case 8:
                return (
                    <Contact
                        selectedContact={value}
                        setSelectedContact={(contact) => handleFieldChange(field.id, contact?.id || null)}
                        onCreateNewContact={() => {
                            setNewContactFieldId(field.id);
                            setCreateContact(true);
                        }}
                        users={field.name === "Person Performing Intake" ? fetchUsers() : null}
                        onEditContact={handleEditContact}
                    />
                );
            case 9:
                // Parse ContactList value - it can be an array, a JSON string array, or individual values
                let contactIds = [];
                try {
                    let parsedValue = value;
                    if (typeof value === 'string') {
                        parsedValue = JSON.parse(value);
                    }
                    if (!Array.isArray(parsedValue)) {
                        parsedValue = parsedValue ? [parsedValue] : [];
                    }
                    parsedValue.forEach(item => {
                        const id = typeof item === 'object' && item !== null ? item.id : item;
                        if (id) {
                            contactIds.push(Number(id));
                        }
                    });
                } catch (e) {
                    console.error('Error parsing ContactList value:', value, e);
                    contactIds = [];
                }
                return (
                    <ContactList
                        value={contactIds}
                        onChange={(ids) => handleFieldChange(field.id, ids)}
                        onCreateNewContact={() => {
                            setNewContactFieldId(field.id);
                            setCreateContact(true);
                        }}
                        onEditContact={handleEditContact}
                    />
                );
            case 10:
                return <Dropdown
                            options={field.options || "[]"}
                            value={value}
                            onChange={(index) => handleFieldChange(field.id, index)}
                            marketing_list={field.id === 225}
                        />;
            case 11:
                return <MultiSelect options={field.options || "[]"} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 12:
                // Treat undefined, null, and empty string as 'unknown' (2); otherwise coerce to number
                const booleanValue = (value === undefined || value === null || value === '') ? 2 : Number(value);
                return <BooleanField options={field.options || []} value={booleanValue} required={required} onChange={(selectedValue) => handleFieldChange(field.id, selectedValue)} />;
            case 13:
                // Create URL-safe ID from field name for hash navigation
                const subheaderId = field.name.replace(/\s+/g, '');
                return <Subheader title={field.name} id={subheaderId} />;
            case 14:
                return <Instructions instructions={field.name}/>;
            case 15:
                if (field.add_item === 1) {
                    return <DocumentSearch
                        value={value}
                        onChange={(val) => handleFieldChange(field.id, val)}
                        case_id={case_id}
                        lead_id={lead_id}
                        caseName={caseName}
                        sectionName={sectionName}
                        fieldId={field.id}
                        sectionId={section_id}
                    />
                }
                return <FileUpload
                    value={value}
                    onChange={(val) => handleFieldChange(field.id, val)}
                    lead_id={lead_id}
                    section_name={sectionName}
                />;
            case 16:
                if (field.add_item === 1) {
                    return <DocumentSearch
                        value={value}
                        onChange={(val) => handleFieldChange(field.id, val)}
                        case_id={case_id}
                        lead_id={lead_id}
                        caseName={caseName}
                        sectionName={sectionName}
                        fieldId={field.id}
                        sectionId={section_id}
                    />
                }
                return <MultiFile
                    value={value}
                    lead_id={lead_id}
                    sectionName={sectionName}
                    onChange={(val) => handleFieldChange(field.id, val)}
                />;
            case 17:
                // Check if this calculation field is part of add_item section
                const isAddItemField = field.add_item === 1;
                const calculationGroupId = isAddItemField 
                    ? (selectedRow ? selectedRow.group_id : (formData && formData.group_id ? formData.group_id : null))
                    : undefined; // For non-add_item fields, don't use groupId
                return (
                    <Calculation
                        options={field.options}
                        fieldId={field.id}
                        fields={fields}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        groupId={calculationGroupId}
                        groupedData={groupedData}
                        addItemMode={addItemMode}
                        handleFieldChange={handleFieldChange}
                        onChange={(val) => {
                            if (typeof refreshAfterCalc === 'function') {
                                refreshAfterCalc(field.id, val);
                            } else {
                                console.error('refreshAfterCalc is not a function:', typeof refreshAfterCalc);
                                handleFieldChange(field.id, val);
                            }
                        }}
                    />
                );
            case 18:
                return <DocGen
                            value={value}
                            onChange={(val, metadata) => {
                                if (typeof handleDocGenGenerated === 'function') {
                                    handleDocGenGenerated(field.id, val, metadata);
                                } else {
                                    handleFieldChange(field.id, val);
                                }
                            }}
                            options={field.docgen_templates || []}
                            lead_id={lead_id}
                            case_id={case_id}
                            field_id={field.id}
                            section_id={section_id}
                            formData={formData}
                            newData={newData}
                            fieldUpdates={fieldUpdates}
                            fields={fields}
                            groupId={currentGroupId}
                            onBeforeGenerate={field.add_item === 1 && typeof prepareDocGenGeneration === 'function'
                                ? () => prepareDocGenGeneration(field.id)
                                : null}
                            onBeforeReview={field.add_item === 1 && typeof prepareDocGenReview === 'function'
                                ? () => prepareDocGenReview(field.id)
                                : null}
                        />;
            case 20:
                let parsedValue = value || {};
                if (typeof value === 'string') {
                    try {
                        parsedValue = JSON.parse(value || '{}');
                    } catch (error) {
                        parsedValue = { due: value || '', done: '' };
                    }
                }
                return <Deadline 
                            value={{ due: parsedValue.due || '', done: parsedValue.done || '' }}
                            title={field.name}
                            onChange={(updated) => handleFieldChange(field.id, {
                                ...parsedValue,
                                ...updated
                            })}
                        />;
            case 21:
                return (
                    <div className='taskflow-wrapper' key={value}>
                        <div className='taskflow-started subtext'>
                            {value}
                        </div>
                        <div className='form-box alt' onClick={() => resetTaskflow(field.id)}>
                            <RefreshCw size={18} />
                        </div>
                    </div>
                );
            case 22:
                return <SearchSelect
                            value={value}
                            onChange={(val) => handleFieldChange(field.id, val)}
                            options={field.options}
                            placeholder={field.name}
                        />;
            case 24:
                return <ReportFusion
                            field={field}
                            case_id={case_id}
                            user_id={user_id}
                            section_id={section_id}
                            value={value}
                            handleFieldChange={handleFieldChange}
                            refreshAfterCalc={refreshAfterCalc}
                        />;
            case 26:
                return <MultiVal
                            value={value}
                            options={field.options || '[]'}
                            fieldName={field.name}
                            onChange={(val) => handleFieldChange(field.id, val)}
                        />;
            case 28:
                return <MatterPublicFormField
                            field={field}
                            leadId={lead_id}
                            caseId={case_id}
                            groupId={currentGroupId}
                        />;
            default:
                return null;
        }
    };

    return ![13, 14, 20, 26].includes(field.field_id) ? (
        <div
            id={domId || undefined}
            data-field-id={field.id}
            className={`form-group nm ${conditional === false ? '' : 'conditional'} ${required ? 'required-field' : ''} ${isDeadlineDerivedCalculation ? 'deadline-derived-calculation' : ''}`}
            style={domId ? { scrollMarginTop: '120px' } : undefined}
        >
            <label className='subtext'>{field.name}</label>
            {recommendation ? (
                <div className="field-recommendation-input-row">
                    <div className="field-recommendation-input">{renderFieldInput(field, required)}</div>
                    {recommendation}
                </div>
            ) : renderFieldInput(field, required)}
        </div>
    ) : renderFieldInput(field, required);
};

export const Section = forwardRef(({ folders, metaData, fetchDocuments, fetchFolderContents, id, lead_id, caseName, caseType, caseTypes, section_id, template_id, user_id, caseData, fetchCase, onFileUpload, refetchFields, activityKey, documentLiveUnreadRegistry = null, openVitals, vitals, fetchVitals, settlementFieldsRequired = false, pendingSettlementPhase = null, settlementVerificationNotice = null, settlementActivityDraft = null, disengagementFieldsRequired = false, pendingDisengagementPhase = null, disengagementActivityDraft = null, guidedActivityDraft = null, guidedBillingDraft = null, guidedSectionDataDraft = null, generationEditTarget = null, sections, onSettlementDraftConsumed = () => {}, onDisengagementDraftConsumed = () => {}, onGuidedDraftConsumed = () => {}, onGuidedBillingDraftConsumed = () => {}, onGuidedSectionDataDraftConsumed = () => {}, onGenerationEditTargetConsumed = () => {}, onSettlementPhaseConfirmed = () => {}, onSettlementDraftSubmitted = () => {}, onSettlementDraftCancelled = () => {}, onDisengagementDraftSubmitted = () => {}, onDisengagementDraftCancelled = () => {}, onPhasesSynced }, ref) => {
    const [fields, setFields] = useState([]);
    const [hiddenFieldCapability, setHiddenFieldCapability] = useState(null);
    const [fieldUpdates, setFieldUpdates] = useState([]);
    const [formData, setFormData] = useState({});
    const [newData, setNewData] = useState({});
    const [originalFormData, setOriginalFormData] = useState({}); // Track original values
    const [addItemMode, setAddItemMode] = useState(false);
    const [forcedSectionDataMode, setForcedSectionDataMode] = useState(false);
    const [dataChanged, setDataChanged] = useState(false);
    const [groupedData, setGroupedData] = useState([]);
    const [sectionName, setSectionName] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [createContact, setCreateContact] = useState(false);
    const [newContactFieldId, setNewContactFieldId] = useState(null);
    const [editContact, setEditContact] = useState(false);
    const [contactToEdit, setContactToEdit] = useState(null);
    const [deleteGroup, setDeleteGroup] = useState(null);
    const [startTaskflow, setStartTaskflow] = useState({});
    const [updatedLeadData, setUpdatedLeadData] = useState(null);
    const [message, setMessage] = useState(null);
    const [filterBy, setFilterBy] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [contactMap, setContactMap] = useState({});
    const [fieldRefreshKey, setFieldRefreshKey] = useState(0);
    const [globalTotal, setGlobalTotal] = useState(0); // Global total from server
    const [totalsByField, setTotalsByField] = useState({}); // Field-level totals from server
    const [calculationMethod, setCalculationMethod] = useState('sum'); // Whether 'sum' or 'difference' was used
    const [differenceTotal, setDifferenceTotal] = useState(null); // Difference total if applicable
    const [showSettlementVerificationNotice, setShowSettlementVerificationNotice] = useState(false);
    const [pendingSettlementMadeWholeValue, setPendingSettlementMadeWholeValue] = useState(null);
    const pendingSettlementMadeWholeValueRef = useRef(null);
    const [disengagementFieldsSaved, setDisengagementFieldsSaved] = useState(false);
    const lastSettlementNoticeId = useRef(null);
    const lastSettlementRequiredScrollKey = useRef(null);

    const parentCaseId = Number(caseData?.parent_case_id || caseData?.case?.parent_case_id || 0);
    const isParentCase = !parentCaseId;
    const [fieldSyncRules, setFieldSyncRules] = useState(null);
    const [childSyncStatus, setChildSyncStatus] = useState(null);
    const [fieldSyncBusy, setFieldSyncBusy] = useState(false);
    const fieldRequestGenerationRef = useRef(0);
    const fieldRequestScopeRef = useRef('');
    const fieldDataScopeRef = useRef('');
    const currentFieldRequestScope = [id, lead_id, section_id, template_id, caseType]
        .map(value => String(value ?? ''))
        .join(':');
    fieldRequestScopeRef.current = currentFieldRequestScope;
    const fieldSaveScope = useMemo(() => ({ scope: currentFieldRequestScope, actor: user_id, request: 0 }), [currentFieldRequestScope, user_id]);
    const fieldSaveScopeRef = useRef(fieldSaveScope);
    fieldSaveScopeRef.current = fieldSaveScope;

    useEffect(() => {
        setMessage(null);
        return () => { fieldSaveScope.request += 1; };
    }, [fieldSaveScope]);

    const isFieldRenderable = useCallback(
        (field) => fieldDataScopeRef.current === currentFieldRequestScope
            && isCustomFieldVisible(field, hiddenFieldCapability, id),
        [currentFieldRequestScope, hiddenFieldCapability, id]
    );

    const isFieldEditable = useCallback(
        (field) => fieldDataScopeRef.current === currentFieldRequestScope
            && isCustomFieldEditable(field, hiddenFieldCapability, id),
        [currentFieldRequestScope, hiddenFieldCapability, id]
    );

    useEffect(() => {
        fieldRequestGenerationRef.current += 1;
        fieldDataScopeRef.current = '';
        setHiddenFieldCapability(null);
        setFields([]);
        setFieldUpdates([]);
        setFormData({});
        setNewData({});
        setOriginalFormData({});
        setGroupedData([]);
        setSelectedRow(null);
        setAddItemMode(false);
        setForcedSectionDataMode(false);
        setFilterBy('');
        setFilterValue('');
        setGlobalTotal(0);
        setTotalsByField({});
        setCalculationMethod('sum');
        setDifferenceTotal(null);
        setFetchingData(false);
    }, [id, lead_id, section_id, template_id, caseType]);

    const getSettlementFieldCurrentValue = (fieldId) => {
        if (Object.prototype.hasOwnProperty.call(formData || {}, fieldId)) return formData[fieldId];
        if (Object.prototype.hasOwnProperty.call(newData?.field_values || {}, fieldId)) return newData.field_values[fieldId];
        if (Object.prototype.hasOwnProperty.call(fieldUpdates || {}, fieldId)) return fieldUpdates[fieldId];
        return '';
    };

    const shouldRequireSettlementMadeWhole = () => {
        const field164Value = getSettlementFieldCurrentValue(164);
        return String(field164Value) === '1' || Number(field164Value) === 1;
    };

    const normalizeGuidedFieldKey = (value = '') => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

    const getGuidedFieldNames = (field) => [field?.name, field?.docgen_name, field?.fusion_name]
        .map(normalizeGuidedFieldKey)
        .filter(Boolean);

    const isGuidedIntentCompatibleWithField = (field, intent) => {
        const valueType = String(intent?.value_type || '').toLowerCase();
        const fieldType = Number(field?.field_id || 0);
        if (valueType === 'option') return [10, 11, 12].includes(fieldType) && parseGuidedFieldOptions(field?.options).length > 0;
        if (valueType === 'contact') return [8, 9].includes(fieldType);
        if (valueType === 'number') return [3, 4, 5, 17].includes(fieldType);
        if (valueType === 'text') return [1, 2].includes(fieldType);
        return true;
    };

    const scoreGuidedFieldIntent = (field, intent) => {
        if (!isGuidedIntentCompatibleWithField(field, intent)) return 0;
        const label = normalizeGuidedFieldKey(intent?.label || intent?.field || intent?.name || '');
        if (!label) return 0;
        const fieldNames = getGuidedFieldNames(field);
        const labelTokens = label.split(' ').filter(Boolean);

        return fieldNames.reduce((bestScore, fieldName) => {
            if (fieldName === label) return Math.max(bestScore, 100);
            if (fieldName.includes(label) || label.includes(fieldName)) return Math.max(bestScore, 85);
            const fieldTokens = new Set(fieldName.split(' ').filter(Boolean));
            const overlap = labelTokens.filter(token => fieldTokens.has(token)).length;
            if (overlap > 0 && overlap === labelTokens.length) return Math.max(bestScore, 70);
            if (overlap > 1) return Math.max(bestScore, 55);
            return bestScore;
        }, 0);
    };

    const getGuidedIntentValueForField = (field, intent) => {
        const optionOrdinal = Number(intent?.option_ordinal || 0);
        if (String(intent?.value_type || '').toLowerCase() === 'option') {
            if (parseGuidedFieldOptions(field?.options).length === 0) return undefined;
            if (optionOrdinal > 0) {
                const optionValue = getGuidedOptionValueByOrdinal(field, optionOrdinal);
                if (optionValue !== undefined) return optionValue;
            }

            return getGuidedOptionValueByStrictLabel(field, intent?.display_value ?? intent?.value);
        }

        if (String(intent?.value_type || '').toLowerCase() === 'contact') {
            if ([8, 9].includes(Number(field?.field_id)) && Number(intent?.contact_id || 0) > 0) {
                return Number(intent.contact_id);
            }

            return undefined;
        }

        return intent?.value;
    };

    const buildGuidedFieldValues = (eligibleFields = [], values = {}, fieldIntents = []) => {
        const valueIntents = Object.entries(values || {}).map(([label, value]) => ({ label, value }));
        const normalizedFieldIntents = Array.isArray(fieldIntents) ? fieldIntents : [];
        const intents = normalizedFieldIntents.length > 0 ? normalizedFieldIntents : valueIntents;
        const fieldValues = {};
        const usedFieldIds = new Set();

        intents.forEach(intent => {
            let bestField = null;
            let bestScore = 0;

            eligibleFields.forEach(field => {
                if (usedFieldIds.has(Number(field.id))) return;
                const score = scoreGuidedFieldIntent(field, intent);
                if (score > bestScore) {
                    bestScore = score;
                    bestField = field;
                }
            });

            if (!bestField || bestScore < 55) return;

            const guidedValue = getGuidedIntentValueForField(bestField, intent);
            if (guidedValue !== undefined && guidedValue !== null && guidedValue !== '') {
                fieldValues[bestField.id] = normalizeValueToIndex(bestField, guidedValue);
                usedFieldIds.add(Number(bestField.id));
            }
        });

        return fieldValues;
    };

    const fetchFieldSyncRules = useCallback(async () => {
        if (!id) return;
        try {
            const requestUrl = isParentCase
                ? `https://api.casedb.co/case_sync_rules.php?parent_case_id=${id}`
                : `https://api.casedb.co/case_sync_rules.php?case_id=${id}&section_id=${section_id}`;

            const response = await fetch(requestUrl, {
                credentials: 'include'
            });
            const data = await response.json();
            if (data?.success && isParentCase) {
                setFieldSyncRules(data.rules || {});
            } else if (data?.success) {
                setChildSyncStatus(data.status || null);
            }
        } catch (error) {
            // ignore fetch failures for sync rules
        }
    }, [id, isParentCase, section_id]);

    useEffect(() => {
        fetchFieldSyncRules();
    }, [fetchFieldSyncRules]);

    useEffect(() => {
        if (!guidedSectionDataDraft?.id || Number(guidedSectionDataDraft.section_id) !== Number(section_id) || fields.length === 0) {
            return;
        }

        const values = guidedSectionDataDraft.values || {};
        const fieldIntents = Array.isArray(guidedSectionDataDraft.field_intents) ? guidedSectionDataDraft.field_intents : [];
        const eligibleFields = fields
            .filter(field => !isPublicFormRequestFieldType(field?.field_id)
                && isFieldEditable(field)
                && (Number(field.case_type_id) === Number(caseType) || Number(field.case_type_id) === 0));
        const fieldValues = buildGuidedFieldValues(eligibleFields, values, fieldIntents);

        setForcedSectionDataMode(true);
        setAddItemMode(true);
        if (!addItemMode) {
            setSelectedRow(null);
        }
        setFormData(prev => ({
            ...prev,
            ...fieldValues,
        }));
        setNewData(prev => ({
            ...prev,
            lead_id,
            case_id: id,
            field_values: {
                ...(prev?.field_values || {}),
                ...fieldValues,
            },
        }));
        onGuidedSectionDataDraftConsumed?.();
    }, [guidedSectionDataDraft, section_id, fields, caseType, lead_id, id, addItemMode, isFieldEditable, onGuidedSectionDataDraftConsumed]);

    const normalizeFieldGroupEntry = useCallback((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'number' || (typeof entry === 'string' && entry !== '' && !isNaN(entry))) {
            return { group_id: Number(entry), section_id: 0, auto_sync: true, key: `g:${Number(entry)}` };
        }
        if (typeof entry !== 'object') return null;
        const groupId = Number(entry.group_id || 0);
        const sectionId = Number(entry.section_id || 0);
        const autoSync = Object.prototype.hasOwnProperty.call(entry, 'auto_sync') ? !!entry.auto_sync : true;
        return { group_id: groupId, section_id: sectionId, auto_sync: autoSync, key: `s:${sectionId}:g:${groupId}` };
    }, []);

    const fieldGroupConfigs = useMemo(() => {
        const groups = fieldSyncRules?.field_groups || [];
        return groups.map(normalizeFieldGroupEntry).filter(Boolean);
    }, [fieldSyncRules, normalizeFieldGroupEntry]);

    const isSectionSynced = useCallback((sectionId) => {
        const target = Number(sectionId || 0);
        if (!target) return false;
        return fieldGroupConfigs.some(cfg => cfg.group_id === 0 && cfg.section_id === target);
    }, [fieldGroupConfigs]);

    const isGroupSynced = useCallback((groupId, sectionId) => {
        const targetGroup = Number(groupId || 0);
        const targetSection = Number(sectionId || 0);
        if (!targetGroup) return false;
        return fieldGroupConfigs.some(cfg => cfg.group_id === targetGroup && (cfg.section_id === 0 || cfg.section_id === targetSection));
    }, [fieldGroupConfigs]);

    const updateFieldSyncRules = useCallback(async (nextFieldGroups) => {
        if (!isParentCase || !id || fieldSyncBusy || fieldSyncRules === null) return false;
        setFieldSyncBusy(true);
        try {
            const current = fieldSyncRules || {};
            const payload = {
                parent_case_id: id,
                share_docs: current.share_docs || 0,
                share_field_groups: nextFieldGroups.length > 0 ? 1 : 0,
                share_feed_updates: current.share_feed_updates || 0,
                folder_paths: current.folder_paths || [],
                field_groups: nextFieldGroups,
                feed_update_ids: current.feed_update_ids || []
            };

            const response = await fetch('https://api.casedb.co/case_sync_rules.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data?.success) {
                setFieldSyncRules({
                    ...current,
                    share_field_groups: payload.share_field_groups,
                    field_groups: nextFieldGroups
                });
                return true;
            }
        } catch (error) {
            // ignore
        } finally {
            setFieldSyncBusy(false);
        }
        return false;
    }, [fieldSyncBusy, fieldSyncRules, id, isParentCase]);

    const getSectionSyncEntry = useCallback((sectionId) => {
        const target = Number(sectionId || 0);
        if (!target) return null;
        const groups = fieldSyncRules?.field_groups || [];
        for (const entry of groups) {
            if (typeof entry !== 'object' || entry === null) continue;
            const entryGroup = Number(entry.group_id || 0);
            const entrySection = Number(entry.section_id || 0);
            if (entryGroup === 0 && entrySection === target) {
                return entry;
            }
        }
        return null;
    }, [fieldSyncRules]);

    const updateSectionAutoSync = useCallback(async (sectionId, enabled) => {
        if (!isParentCase || !id) return false;
        const currentGroups = fieldSyncRules?.field_groups || [];
        let updated = false;
        const nextGroups = currentGroups.map((entry) => {
            if (!entry || typeof entry !== 'object') return entry;
            const entryGroup = Number(entry.group_id || 0);
            const entrySection = Number(entry.section_id || 0);
            if (entryGroup === 0 && entrySection === Number(sectionId || 0)) {
                updated = true;
                return { ...entry, auto_sync: enabled ? 1 : 0 };
            }
            return entry;
        });
        if (!updated) {
            nextGroups.push({ section_id: Number(sectionId || 0), group_id: 0, auto_sync: enabled ? 1 : 0 });
        }
        return updateFieldSyncRules(nextGroups);
    }, [fieldSyncRules, id, isParentCase, updateFieldSyncRules]);

    const resyncSectionGroups = useCallback(async (sectionId) => {
        if (!isParentCase || !id || fieldSyncBusy || fieldSyncRules === null) return false;
        setFieldSyncBusy(true);
        try {
            const current = fieldSyncRules || {};
            const payload = {
                parent_case_id: id,
                share_docs: current.share_docs || 0,
                share_field_groups: (current.field_groups || []).length > 0 ? 1 : 0,
                share_feed_updates: current.share_feed_updates || 0,
                folder_paths: current.folder_paths || [],
                field_groups: current.field_groups || [],
                feed_update_ids: current.feed_update_ids || [],
                resync_section_id: Number(sectionId || 0)
            };

            const response = await fetch('https://api.casedb.co/case_sync_rules.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            return !!data?.success;
        } catch (error) {
            return false;
        } finally {
            setFieldSyncBusy(false);
        }
    }, [fieldSyncBusy, fieldSyncRules, id, isParentCase]);

    const addFieldGroupSync = useCallback(async ({ sectionId, groupId }) => {
        if (!isParentCase || !id) return false;
        const currentGroups = fieldSyncRules?.field_groups || [];
        const normalized = normalizeFieldGroupEntry({ section_id: sectionId, group_id: groupId });
        if (!normalized) return false;

        const exists = currentGroups
            .map(normalizeFieldGroupEntry)
            .filter(Boolean)
            .some(cfg => cfg.key === normalized.key);

        if (exists) return true;

        const nextGroups = [...currentGroups, { section_id: sectionId, group_id: groupId, ...(groupId === 0 ? { auto_sync: 1 } : {}) }];
        return updateFieldSyncRules(nextGroups);
    }, [fieldSyncRules, id, isParentCase, normalizeFieldGroupEntry, updateFieldSyncRules]);
    
    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
    
    // Pagination state
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, row: null });
    const [perPageOptions] = useState([25, 50, 100, 250, 500, 1000]);
    const [fetchingData, setFetchingData] = useState(false);

    useEffect(() => {
        setSortConfig({ key: null, direction: 'desc' });
        setPagination(prev => ({ ...prev, current_page: 1 }));
        setContextMenu({ visible: false, x: 0, y: 0, row: null });
    }, [id, lead_id, section_id, template_id, caseType]);
    
    // Settlement phase tracking
    const [settlementFieldsSaved, setSettlementFieldsSaved] = useState(false);
    const settlementBaseFieldIds = [199, 200, 203]; // Field IDs for Settlement Date, Final Settlement, and Final %/Flat Fee (dynamic)
    
    // Reset settlementFieldsSaved when settlementFieldsRequired changes to true
    useEffect(() => {
        if (settlementFieldsRequired) {
            setSettlementFieldsSaved(false);
            if (Number(section_id) === 20 && shouldRequireSettlementMadeWhole()) {
                setFields(prevFields => {
                    if (prevFields.some(field => Number(field.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID)) {
                        return prevFields;
                    }

                    return [
                        ...prevFields,
                        createSettlementMadeWholeField(
                            Math.max(0, ...prevFields.map(field => Number(field.order_id) || 0)) + 1
                        )
                    ];
                });
            } else {
                setFields(prevFields => prevFields.filter(field => !(Number(field.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID && field.settlement_phase_only)));
                setPendingSettlementMadeWholeValue(null);
                pendingSettlementMadeWholeValueRef.current = null;
            }
        } else {
            setFields(prevFields => prevFields.filter(field => !(Number(field.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID && field.settlement_phase_only)));
        }
    }, [settlementFieldsRequired, section_id, fieldUpdates, formData, newData.field_values]);

    // Reset settlementFieldsSaved when navigating away from settlement sections
    useEffect(() => {
        if (!settlementFieldsRequired && settlementFieldsSaved) {
            setSettlementFieldsSaved(false);
            setPendingSettlementMadeWholeValue(null);
            pendingSettlementMadeWholeValueRef.current = null;
        }
    }, [settlementFieldsRequired]);

    useEffect(() => {
        if (disengagementFieldsRequired) {
            setDisengagementFieldsSaved(false);
            return;
        }

        if (disengagementFieldsSaved) {
            setDisengagementFieldsSaved(false);
        }
    }, [disengagementFieldsRequired, disengagementFieldsSaved]);
    
    // Function to check if there are unsaved changes
    const hasUnsavedChanges = () => {
        console.log('Checking for unsaved changes...');
        console.log('   - newData:', newData);
        console.log('   - newData.field_values:', newData.field_values);
        console.log('   - formData keys:', Object.keys(formData));
        console.log('   - originalFormData keys:', Object.keys(originalFormData));
        
        // Check if newData has any field_values (excluding calculation fields)
        if (newData.field_values && Object.keys(newData.field_values).length > 0) {
            // Filter out calculation fields (field_id 17) from the check
            const nonCalculationChanges = Object.keys(newData.field_values).filter(fieldId => {
                const field = fields.find(f => String(f.id) === String(fieldId));
                return field && field.field_id !== 17; // Exclude calculation fields
            });
            
            if (nonCalculationChanges.length > 0) {
                console.log('   Has unsaved changes: newData.field_values has non-calculation changes');
                console.log('      - Non-calculation changes:', nonCalculationChanges);
                return true;
            }
        }
        
        // Get non-calculation fields from both formData and originalFormData
        const formDataKeys = Object.keys(formData).filter(key => {
            // Filter out system fields that start with underscore
            if (key.startsWith('_')) return false;
            
            const field = fields.find(f => String(f.id) === String(key));
            return field && field.field_id !== 17; // Exclude calculation fields
        });
        
        const originalKeys = Object.keys(originalFormData).filter(key => {
            // Filter out system fields that start with underscore
            if (key.startsWith('_')) return false;
            
            const field = fields.find(f => String(f.id) === String(key));
            return field && field.field_id !== 17; // Exclude calculation fields
        });
        
        console.log('   - formData length (non-calc):', formDataKeys.length);
        console.log('   - originalFormData length (non-calc):', originalKeys.length);
        
        // Only compare values for keys that exist in formData (user-edited fields)
        // Don't flag as "unsaved" if originalFormData has more fields - that's just initial state
        for (const key of formDataKeys) {
            const currentValue = JSON.stringify(formData[key]);
            const originalValue = JSON.stringify(originalFormData[key]);
            
            // Only flag as changed if the key exists in original and values differ
            if (originalFormData.hasOwnProperty(key) && currentValue !== originalValue) {
                console.log(`   Has unsaved changes: key ${key} differs`);
                console.log(`      - Current: ${currentValue}`);
                console.log(`      - Original: ${originalValue}`);
                return true;
            }
            
            // If key doesn't exist in original, it's a new change
            if (!originalFormData.hasOwnProperty(key)) {
                console.log(`   Has unsaved changes: new field ${key} added`);
                return true;
            }
        }
        
        console.log('   No unsaved changes detected');
        return false;
    };
    
    // Debug: Monitor message state changes
    useEffect(() => {
        console.log('Message state changed:', message);
        if (message) {
            console.log('Message type:', message.type, 'Message text:', message.text);
        }
    }, [message]);
    
    // Create a ref to access the Documents component
    const documentsRef = useRef(null);
    const rootFolderCacheRef = useRef(null);

    // Expose refreshCurrentFolder function to parent component
    useImperativeHandle(ref, () => ({
        refreshCurrentFolder: () => {
            // Try to call the Documents component's refresh function, fallback to fetchDocuments
            if (documentsRef.current?.refreshCurrentFolder) {
                documentsRef.current.refreshCurrentFolder();
            } else if (fetchDocuments) {
                fetchDocuments();
            }
        },
        hasUnsavedChanges: hasUnsavedChanges,
        refreshFields: () => {
            console.log('refreshFields called in Section component');
            console.log('   - Current pagination:', pagination);
            console.log('   - section_id:', section_id);
            console.log('   - lead_id:', lead_id);
            
            // Refresh the current section's fields if no unsaved changes
            const unsavedChanges = hasUnsavedChanges();
            console.log('   - Has unsaved changes:', unsavedChanges);
            
            if (!unsavedChanges) {
                console.log('Refreshing fields after vital update (no unsaved changes)');
                console.log('   - Fetching page:', pagination.current_page, 'perPage:', pagination.per_page);
                fetchFields(pagination.current_page, pagination.per_page);
            } else {
                console.log('Skipping field refresh - unsaved changes detected');
            }
        }
    }));

    const handleNewContactCreated = (contact) => {
        if (newContactFieldId !== null && contact?.id) {
            handleNewContactForField(contact, newContactFieldId, fields, formData, handleFieldChange);
        }
        setCreateContact(false);
        setNewContactFieldId(null);
    };

    const handleEditContact = (contact) => {
        setContactToEdit(contact);
        setEditContact(true);
    };

    const fetchContacts = () => {
        // add it when you remember it
        console.log('Contacts refreshed');
    };

    useEffect(() => {
        setAddItemMode(false);
        setNewData({});
        setDataChanged(false);
    }, [section_id]);

    useEffect(() => {
        if (
            section_id === 24 &&
            pendingSettlementPhase &&
            settlementVerificationNotice?.id &&
            settlementVerificationNotice.id !== lastSettlementNoticeId.current
        ) {
            setShowSettlementVerificationNotice(true);
            lastSettlementNoticeId.current = settlementVerificationNotice.id;
        }
    }, [section_id, pendingSettlementPhase, settlementVerificationNotice]);

    // Refetch data when sort configuration changes
    useEffect(() => {
        if (lead_id && section_id) {
            fetchFields(pagination.current_page, pagination.per_page);
        }
    }, [sortConfig.key, sortConfig.direction]);

    // Export grouped section data; section 19 (Medical) uses a specialized workbook layout below.
    const exportSectionData = async () => {
        const exportRequestScope = currentFieldRequestScope;
        if (exportRequestScope !== fieldRequestScopeRef.current) return;
        try {
            const url = buildSectionExportUrl({
                caseId: id,
                leadId: lead_id,
                sectionId: section_id,
                sortKey: customFieldSelectorIsVisibleForCase(sortConfig.key, fields, hiddenFieldCapability, id)
                    ? sortConfig.key : null,
                sortDirection: sortConfig.direction,
                filterBy: customFieldSelectorIsVisibleForCase(filterBy, fields, hiddenFieldCapability, id)
                    ? filterBy : null,
                filterValue
            });

            if (!url) {
                setMessage({
                    type: 'error',
                    text: 'This section cannot be exported until its case information has loaded. Refresh the case and try again.'
                });
                return;
            }
            
            const response = await fetch(url, { credentials: 'include' });
            const responseData = await response.json();
            if (exportRequestScope !== fieldRequestScopeRef.current) return;
            if (!response.ok || responseData?.success === false) {
                throw new Error(responseData?.message || 'Export failed');
            }
            const responseCapability = readCaseBoundHiddenFieldCapability(responseData, id);
            if (!responseCapability) {
                throw new Error('Export permission could not be verified for this case');
            }
            const data = sanitizeCustomFieldExportPayloadForCase(
                responseData,
                fields,
                responseCapability,
                id
            );
            if (!data) throw new Error('Export payload was invalid');
            
            if (data.success) {
                // Convert data to Excel
                if (data.data.length === 0) {
                    setMessage({ type: 'warning', text: 'No data to export' });
                    return;
                }
                
                //Hardcode: Special export format for Medical section (section_id 19)
                if (section_id === 19) {
                    const wb = new ExcelJS.Workbook();
                    const ws = wb.addWorksheet('Medical');
                    
                    // Row 1: Client Name header and value, DOB, DOA, Totals
                    ws.getCell('A1').value = 'Client Name';
                    ws.getCell('A1').font = { bold: true };
                    
                    ws.getCell('B1').value = 'DOB';
                    ws.getCell('B1').font = { bold: true };
                    
                    ws.getCell('C1').value = 'DOA';
                    ws.getCell('C1').font = { bold: true };
                    
                    // Row 2: Client data values
                    ws.getCell('A2').value = data.clientName || '';
                    
                    if (data.dob) {
                        const dobDate = new Date(data.dob);
                        if (!isNaN(dobDate.getTime())) {
                            ws.getCell('B2').value = dobDate;
                            ws.getCell('B2').numFmt = 'mm/dd/yyyy';
                        }
                    }
                    
                    if (data.incidentDate) {
                        const incidentDate = new Date(data.incidentDate);
                        if (!isNaN(incidentDate.getTime())) {
                            ws.getCell('C2').value = incidentDate;
                            ws.getCell('C2').numFmt = 'mm/dd/yyyy';
                        }
                    }
                    
                    ws.getCell('D2').value = 'Totals';
                    ws.getCell('D2').font = { bold: true };
                    
                    // Calculate the number of data rows
                    const dataRowCount = data.data.length;
                    const startDataRow = 4; // Data starts at row 4
                    const endDataRow = startDataRow + dataRowCount - 1;
                    
                    // Row 2: Sum formulas for columns E through J
                    // Column E (Amount Billed) -> E2
                    ws.getCell('E2').value = { formula: `SUM(E${startDataRow}:E${endDataRow})` };
                    ws.getCell('E2').numFmt = '"$"#,##0.00';
                    
                    // Column F (Balance) -> F2
                    ws.getCell('F2').value = { formula: `SUM(F${startDataRow}:F${endDataRow})` };
                    ws.getCell('F2').numFmt = '"$"#,##0.00';
                    
                    // Column G (Future Medical Amount) -> G2
                    ws.getCell('G2').value = { formula: `SUM(G${startDataRow}:G${endDataRow})` };
                    ws.getCell('G2').numFmt = '"$"#,##0.00';
                    
                    // Column H (Client Payment) -> H2
                    ws.getCell('H2').value = { formula: `SUM(H${startDataRow}:H${endDataRow})` };
                    ws.getCell('H2').numFmt = '"$"#,##0.00';
                    
                    // Column I (Original Lien Amount) -> I2
                    ws.getCell('I2').value = { formula: `SUM(I${startDataRow}:I${endDataRow})` };
                    ws.getCell('I2').numFmt = '"$"#,##0.00';
                    
                    // Column J (Reduced To) -> J2
                    ws.getCell('J2').value = { formula: `SUM(J${startDataRow}:J${endDataRow})` };
                    ws.getCell('J2').numFmt = '"$"#,##0.00';
                    
                    // Row 3: Column headers for the data
                    const medicalHeaders = [
                        'Provider',
                        'Client Treatment Status',
                        'Date Treatment Started',
                        'Date Treatment Completed',
                        'Amount Billed',
                        'Balance',
                        'Future Medical Amount',
                        'Client Payment',
                        'Original Lien Amount',
                        'Reduced To'
                    ];
                    
                    const headerRow = ws.getRow(3);
                    medicalHeaders.forEach((header, index) => {
                        const cell = headerRow.getCell(index + 1);
                        cell.value = header;
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4F81BD' } };
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                    });
                    
                    // Add data rows starting at row 4
                    data.data.forEach((row, rowIndex) => {
                        const dataRow = ws.getRow(startDataRow + rowIndex);
                        
                        // Map the data to the specific columns
                        const rowData = [
                            row['Provider'] || '',
                            row['Client Treatment Status'] || '',
                            row['Date Treatment Started'] || '',
                            row['Date Treatment Completed'] || '',
                            row['Amount Billed'] || '',
                            row['Balance'] || '',
                            row['Future Medical Amount'] || '',
                            row['Client Payment'] || '',
                            row['Original Lien Amount'] || '',
                            row['Reduced To'] || ''
                        ];
                        
                        rowData.forEach((value, colIndex) => {
                            const cell = dataRow.getCell(colIndex + 1);
                            
                            // Apply formatting based on column
                            if (colIndex === 0) {
                                // Provider (text)
                                cell.value = value;
                            } else if (colIndex === 1) {
                                // Client Treatment Status (text)
                                cell.value = value;
                            } else if (colIndex === 2 || colIndex === 3) {
                                // Date fields
                                if (value) {
                                    const dateValue = new Date(value);
                                    if (!isNaN(dateValue.getTime())) {
                                        cell.value = dateValue;
                                        cell.numFmt = 'mm/dd/yyyy';
                                    } else {
                                        cell.value = value;
                                    }
                                }
                            } else if (colIndex >= 4) {
                                // Currency fields (Amount Billed, Balance, Future Medical Amount, Client Payment, Original Lien Amount, Reduced To)
                                if (value && value !== '') {
                                    // Remove currency symbols and parse
                                    const numValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
                                    if (!isNaN(numValue)) {
                                        cell.value = numValue;
                                        cell.numFmt = '"$"#,##0.00';
                                    } else {
                                        cell.value = value;
                                    }
                                } else {
                                    cell.value = 0;
                                    cell.numFmt = '"$"#,##0.00';
                                }
                            }
                        });
                    });
                    
                    // Set all column widths to 29.17 with text wrap
                    for (let i = 1; i <= 10; i++) {
                        ws.getColumn(i).width = 29.17;
                        ws.getColumn(i).alignment = { wrapText: true };
                    }
                    
                    // Use custom filename from API if available
                    const filename = data.filename 
                        ? `${data.filename}_${new Date().toISOString().split('T')[0]}.xlsx`
                        : `Medical_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                    
                    const buf = await wb.xlsx.writeBuffer();
                    if (exportRequestScope !== fieldRequestScopeRef.current) return;
                    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
                    
                    setMessage({ type: 'success', text: 'Medical data exported successfully!' });
                    return;
                }
                
                const headers = Object.keys(data.data[0]);
                const headerNames = headers; // Use the header names as they are
                
                console.log('=== SECTION EXCEL EXPORT DEBUG ===');
                console.log('Data response length:', data.data.length);
                console.log('Headers:', headers);
                console.log('First row sample:', data.data[0]);
                console.log('Field types from backend:', data.fieldTypes);
                console.log('Field map from backend:', data.fieldMap);
                
                // Create reverse mapping from field name to field type
                const fieldTypesByName = {};
                if (data.fieldMap && data.fieldTypes) {
                    Object.entries(data.fieldMap).forEach(([fieldId, fieldName]) => {
                        const fieldType = data.fieldTypes[fieldId];
                        if (fieldType) {
                            fieldTypesByName[fieldName] = fieldType;
                        }
                    });
                }
                console.log('Field types by name:', fieldTypesByName);
                
                const wb = new ExcelJS.Workbook();
                const ws = wb.addWorksheet('Section Data');
                
                // Create header row
                const headerRow = ws.addRow(headerNames);
                headerRow.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4F81BD' } };
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                });
                
                // Add data rows
                data.data.forEach(row => {
                    const vals = headers.map(header => {
                        let value = row[header];
                        
                        // Handle arrays and null values
                        if (Array.isArray(value)) {
                            return value.filter(Boolean).join(', ');
                        }
                        return value ?? '';
                    });
                    
                    const addedRow = ws.addRow(vals);
                    
                    // Apply Excel formatting based on actual field types from backend
                    vals.forEach((cellValue, colIndex) => {
                        const header = headers[colIndex];
                        const cell = addedRow.getCell(colIndex + 1);
                        const fieldType = fieldTypesByName[header];
                        
                        // Always set cell value first
                        cell.value = cellValue;
                        
                        // Apply formatting based on actual field type from database
                        if (fieldType) {
                            console.log(`Formatting "${header}" (type ${fieldType}): ${cellValue}`);
                            
                            if (fieldType === 1 || fieldType === 2) {
                                // Text fields (1 = text, 2 = textarea) - force text formatting to prevent scientific notation
                                cell.value = String(cellValue || ''); // Ensure it's a string
                                cell.numFmt = '@'; // Excel text format
                                console.log(`✓ Applied text format (type ${fieldType}) to ${header}: "${cellValue}"`);
                            }
                            else if (fieldType === 3 || fieldType === 17) {
                                // Currency fields (3 = money, 17 = total_data_ident)
                                if (typeof cellValue === 'number') {
                                    cell.numFmt = '"$"#,##0.00';
                                    console.log(`✓ Applied currency format (type ${fieldType}) to ${header}: ${cellValue}`);
                                } else if (typeof cellValue === 'string' && cellValue !== '') {
                                    const numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                                    if (!isNaN(numValue)) {
                                        cell.value = numValue;
                                        cell.numFmt = '"$"#,##0.00';
                                        console.log(`✓ Converted currency (type ${fieldType}) ${header}: "${cellValue}" → ${numValue}`);
                                    }
                                }
                            }
                            else if (fieldType === 4) {
                                // Percentage fields
                                if (typeof cellValue === 'number') {
                                    cell.value = cellValue / 100; // Convert to decimal for Excel
                                    cell.numFmt = '0.00%';
                                    console.log(`✓ Applied percentage format (type 4) to ${header}: ${cellValue} → ${cellValue / 100}`);
                                } else if (typeof cellValue === 'string' && cellValue !== '') {
                                    const numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                                    if (!isNaN(numValue)) {
                                        cell.value = numValue / 100; // Convert to decimal for Excel
                                        cell.numFmt = '0.00%';
                                        console.log(`✓ Converted percentage (type 4) ${header}: "${cellValue}" → ${numValue / 100}`);
                                    }
                                }
                            }
                            else if (fieldType === 5) {
                                // Number fields
                                if (typeof cellValue === 'number') {
                                    cell.numFmt = '#,##0.00';
                                    console.log(`✓ Applied number format (type 5) to ${header}: ${cellValue}`);
                                } else if (typeof cellValue === 'string' && cellValue !== '') {
                                    const numValue = parseFloat(cellValue);
                                    if (!isNaN(numValue)) {
                                        cell.value = numValue;
                                        cell.numFmt = '#,##0.00';
                                        console.log(`✓ Converted number (type 5) ${header}: "${cellValue}" → ${numValue}`);
                                    }
                                }
                            }
                            else if (fieldType === 6) {
                                // Date fields
                                if (typeof cellValue === 'string' && cellValue !== '') {
                                    const date = new Date(cellValue);
                                    if (!isNaN(date.getTime())) {
                                        cell.value = date;
                                        cell.numFmt = 'mm/dd/yyyy';
                                        console.log(`✓ Applied date format (type 6) to ${header}: "${cellValue}" → ${date}`);
                                    }
                                }
                            }
                            else {
                                console.log(`No specific formatting for field type ${fieldType} (${header})`);
                            }
                        }
                        // Handle built-in timestamp columns (Created At, Last Updated At)
                        else if (header === 'Created At' || header === 'Last Updated At') {
                            if (typeof cellValue === 'string' && cellValue !== '') {
                                const date = new Date(cellValue);
                                if (!isNaN(date.getTime())) {
                                    cell.value = date;
                                    cell.numFmt = 'mm/dd/yyyy h:mm:ss AM/PM';
                                    console.log(`✓ Applied datetime format to ${header}: "${cellValue}" → ${date}`);
                                }
                            }
                        }
                        else {
                            console.log(`No field type found for "${header}" - no special formatting applied`);
                        }
                    });
                });
                
                // Calculate column widths based on actual content length with appropriate limits
                ws.columns.forEach((col, colIndex) => {
                    const header = headers[colIndex];
                    const fieldType = fieldTypesByName[header];
                    
                    // Calculate maximum content length in this column
                    let maxContentLength = header.length; // Start with header length
                    col.eachCell({ includeEmpty: true }, c => {
                        let cellText;
                        if (c.value && c.value.text) {
                            // Hyperlink or rich text
                            cellText = c.value.text;
                        } else if (c.value instanceof Date) {
                            // Date object - estimate display length
                            cellText = c.value.toLocaleDateString() + ' ' + c.value.toLocaleTimeString();
                        } else {
                            // Regular value
                            cellText = String(c.value ?? '');
                        }
                        
                        if (cellText.length > maxContentLength) {
                            maxContentLength = cellText.length;
                        }
                    });
                    
                    // Set width based on content length with field-type-specific limits
                    let minWidth = 8;   // Absolute minimum
                    let maxWidth = 50;  // Absolute maximum
                    let padding = 2;    // Extra padding for readability
                    
                    // Adjust limits based on field type
                    if (fieldType) {
                        if (fieldType === 1 || fieldType === 2) {
                            // Text/Textarea - can be longer
                            maxWidth = 60;
                            minWidth = 12;
                        }
                        else if (fieldType === 3 || fieldType === 17) {
                            // Currency - moderate width
                            maxWidth = 18;
                            minWidth = 12;
                        }
                        else if (fieldType === 4) {
                            // Percentage - small width
                            maxWidth = 12;
                            minWidth = 8;
                        }
                        else if (fieldType === 5) {
                            // Number - moderate width
                            maxWidth = 15;
                            minWidth = 8;
                        }
                        else if (fieldType === 6) {
                            // Date - fixed reasonable width
                            maxWidth = 18;
                            minWidth = 15;
                        }
                        else if (fieldType === 8 || fieldType === 9) {
                            // Contact fields - can be longer
                            maxWidth = 40;
                            minWidth = 15;
                        }
                        else if (fieldType === 10 || fieldType === 11) {
                            // Dropdown/Multi-select - moderate width
                            maxWidth = 25;
                            minWidth = 12;
                        }
                    }
                    
                    // Handle special timestamp columns
                    if (header === 'Created At' || header === 'Last Updated At') {
                        maxWidth = 22;
                        minWidth = 18;
                    }
                    
                    // Calculate final width: content + padding, within limits
                    const calculatedWidth = maxContentLength + padding;
                    const finalWidth = Math.min(maxWidth, Math.max(minWidth, calculatedWidth));
                    
                    col.width = finalWidth;
                    console.log(`Column "${header}" (type ${fieldType || 'unknown'}): content=${maxContentLength}, final width=${finalWidth} (min=${minWidth}, max=${maxWidth})`);
                });
                
                // Use custom filename from API if available, otherwise fallback to default
                const filename = data.filename 
                    ? `${data.filename}_${new Date().toISOString().split('T')[0]}.xlsx`
                    : `${sectionName}_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                
                const buf = await wb.xlsx.writeBuffer();
                if (exportRequestScope !== fieldRequestScopeRef.current) return;
                saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
                
                setMessage({ type: 'success', text: 'Data exported successfully!' });
            } else {
                setMessage({ type: 'error', text: data.message || 'Export failed' });
            }
        } catch (error) {
            if (exportRequestScope !== fieldRequestScopeRef.current) return;
            console.error('Export error:', error);
            setMessage({ type: 'error', text: 'Export failed: ' + error.message });
        }
    };

    const findMatchingRootFolder = (name, candidates) => {
        if (!name || !Array.isArray(candidates) || candidates.length === 0) return null;

        const sectionLower = name.toLowerCase();
        let bestMatch = null;

        candidates.forEach((candidate) => {
            if (!candidate) return;

            const normalized = candidate.replace(/^\d+\s*/, '');
            const normalizedLower = normalized.toLowerCase();

            if (normalizedLower === sectionLower) {
                bestMatch = candidate;
            } else if (!bestMatch && candidate.toLowerCase().includes(sectionLower)) {
                bestMatch = candidate;
            }
        });

        return bestMatch || null;
    };

    const resolveSectionFolderPathFromFolders = (name) => {
        if (!name || !folders || typeof folders !== 'object') return null;

        const rootNames = new Set();

        Object.keys(folders).forEach((key) => {
            if (!key || key === '{{Name}}') return;

            let rootName = key.startsWith('{{Name}}/')
                ? key.replace(/^{{Name}}\//, '')
                : key;

            rootName = rootName.split('/')[0];
            if (!rootName) return;

            rootNames.add(rootName);
        });

        const match = findMatchingRootFolder(name, Array.from(rootNames));
        return match ? `{{Name}}/${match}` : null;
    };

    // Determine the folder path for uploads (handles special cases like Depositions)
    const resolveUploadFolderPath = async () => {
        let folderPath = sectionName ? `{{Name}}/${sectionName}` : '{{Name}}';

        if (sectionName && sectionName !== "Depositions") {
            const resolvedPath = resolveSectionFolderPathFromFolders(sectionName);
            if (resolvedPath) {
                folderPath = resolvedPath;
            } else if (fetchFolderContents) {
                try {
                    if (!rootFolderCacheRef.current) {
                        const rootResult = await fetchFolderContents('', 1, 200);
                        rootFolderCacheRef.current = (rootResult?.folders || []).map(f => f.name).filter(Boolean);
                    }

                    const matchedRoot = findMatchingRootFolder(sectionName, rootFolderCacheRef.current || []);
                    if (matchedRoot) {
                        folderPath = `{{Name}}/${matchedRoot}`;
                    }
                } catch (error) {
                    console.error('Error resolving section folder path:', error);
                }
            }
        }

        if (sectionName === "Depositions") {
            const deponentFieldValue = formData[378] || fieldUpdates[378];
            
            if (deponentFieldValue) {
                try {
                    const response = await fetch(`https://api.casedb.co/contacts.php?id=${deponentFieldValue}`, {
                        credentials: 'include'
                    });
                    const data = await response.json();
                    
                    if (data.success && data.contacts) {
                        const deponentName = data.contacts.full_name || data.contacts.company_name || "Unknown";
                        folderPath = `{{Name}}/Litigation/Depositions/${deponentName.replace(/[\\/:*?"<>|]/g, '_')}`;
                    }
                } catch (error) {
                    console.error("Error fetching deponent information:", error);
                    folderPath = `{{Name}}/Litigation/Depositions/Unknown_Deponent`;
                }
            } else {
                folderPath = `{{Name}}/Litigation/Depositions/Uncategorized`;
            }
        }

        return folderPath;
    };

    const uploadSingleFile = async (file, fieldId, options = {}) => {
        const uploadRequestScope = currentFieldRequestScope;
        if (uploadRequestScope !== fieldRequestScopeRef.current) {
            return { fileId: null, fileMeta: null };
        }
        const { groupIdOverride = null, folderPathOverride = null } = options;
        const targetField = fields.find(field => Number(field?.id) === Number(fieldId));
        if (!isFieldEditable(targetField)) {
            setMessage({ type: 'error', text: 'This restricted field is read-only.' });
            return { fileId: null, fileMeta: null };
        }

        try {
            const folderPath = folderPathOverride || await resolveUploadFolderPath();
            if (uploadRequestScope !== fieldRequestScopeRef.current) {
                return { fileId: null, fileMeta: null };
            }
            
            const uploadData = new FormData();
            uploadData.append("file", file);
            uploadData.append("case_id", id);
            uploadData.append("folder_path", folderPath);
            uploadData.append("user_id", user_id);
            if (lead_id) uploadData.append("lead_id", lead_id);
            uploadData.append("section_id", section_id);
            uploadData.append("field_id", fieldId);

            const effectiveGroupId = groupIdOverride ?? (selectedRow?.group_id);
            if (effectiveGroupId) {
                uploadData.append("group_id", effectiveGroupId);
            }

            const response = await fetch("https://api.casedb.co/s3_upload.php", {
                method: "POST",
                body: uploadData,
                credentials: "include"
            });

            const result = await response.json();
            if (uploadRequestScope !== fieldRequestScopeRef.current) {
                return { fileId: null, fileMeta: null };
            }
            
            if (result.success && Array.isArray(result.files) && result.files.length > 0) {
                const uploadedFile = result.files.find(f => f.file_id) || result.files[0];
                const fileId = uploadedFile.file_id || uploadedFile.id || null;

                if (!fileId) {
                    console.error(`Upload succeeded but no file_id returned for field ${fieldId}`, result);
                    return { fileId: null, fileMeta: null };
                }

                const rawFilePath = uploadedFile.file_path || uploadedFile.filePath || null;
                if (rawFilePath) {
                    let displayPath = rawFilePath;
                    const casePrefix = `cases/${id}/`;
                    if (displayPath.startsWith(casePrefix)) {
                        displayPath = displayPath.slice(casePrefix.length);
                    }
                    const lastSlash = displayPath.lastIndexOf('/');
                    if (lastSlash !== -1) {
                        displayPath = displayPath.slice(0, lastSlash);
                    }
                    displayPath = displayPath.replace('{{Name}}', caseName || '{{Name}}');
                    if (!displayPath.startsWith('/')) {
                        displayPath = `${displayPath}`;
                    }
                    setMessage({
                        type: 'success',
                        text: `Uploaded to ${displayPath}`
                    });
                }

                const fileMeta = {
                    id: fileId,
                    file_name: uploadedFile.file_name || uploadedFile.filename || file.name,
                    file_url: uploadedFile.file_url || uploadedFile.url || null,
                    size: uploadedFile.file_size || uploadedFile.size || file.size || null,
                    uploaded_date: uploadedFile.uploaded_date || uploadedFile.creation_date || new Date().toISOString()
                };

                // Refresh the document list and field data
                setTimeout(() => {
                    try {
                        fetchDocuments?.();
                    } catch (refreshError) {
                        console.error("Error refreshing documents after upload:", refreshError);
                    }
                }, 500);
                
                return { fileId, fileMeta };
            } else {
                console.error("Upload failed:", result.message || result.error || result);
                return { fileId: null, fileMeta: null };
            }
        } catch (error) {
            console.error(`Error uploading file for field ${fieldId}:`, error);
            return { fileId: null, fileMeta: null };
        }
    };

    const handleFieldChange = async (fieldId, value) => {
        const changeRequestScope = currentFieldRequestScope;
        if (changeRequestScope !== fieldRequestScopeRef.current) return;
        console.log(`handleFieldChange called for field ${fieldId}:`, value, typeof value);
        console.log('Stack trace:', new Error().stack);

        const field = fields.find(candidate => Number(candidate?.id) === Number(fieldId));
        if (!isFieldEditable(field)) {
            setMessage({ type: 'error', text: 'This restricted field is read-only.' });
            return;
        }
        
        if (value && typeof value === 'object' && value.constructor?.name === 'File') {
            console.log(`File object detected for field ${fieldId}, uploading...`);
            
            try {
                const uploadResult = await uploadSingleFile(value, fieldId);
                if (changeRequestScope !== fieldRequestScopeRef.current) return;
                if (uploadResult?.fileId) {
                    console.log(`File uploaded successfully for field ${fieldId}, received file ID: ${uploadResult.fileId}`);
                    
                    // Use functional updates to ensure we have the latest state
                    setFormData((prev) => {
                        const updated = {
                            ...prev,
                            [fieldId]: uploadResult.fileMeta || uploadResult.fileId
                        };
                        console.log(`Updated formData for field ${fieldId}:`, updated);
                        return updated;
                    });
                    
                    setNewData(prev => {
                        const updated = {
                            ...prev,
                            lead_id,
                            field_values: {
                                ...prev.field_values,
                                [fieldId]: uploadResult.fileId
                            }
                        };
                        console.log(`Updated newData for field ${fieldId}:`, updated);
                        return updated;
                    });
                    
                    console.log(`Field ${fieldId} set to file ID: ${uploadResult.fileId}`);
                } else {
                    console.error(`File upload failed for field ${fieldId}`);
                }
            } catch (error) {
                console.error(`Error uploading file for field ${fieldId}:`, error);
            }
        } else {
            // Handle DocumentSearch object format {name: "filename", id: 123}
            const isDocumentSearchField = field && (field.field_id === 15 || field.field_id === 16);
            
            let processedValue = value;
            
            // For DocumentSearch fields, preserve the full object in formData for display purposes
            // The ID extraction will happen during save operations in saveFields function
            if (isDocumentSearchField && value && typeof value === 'object') {
                const hasDocId = value.id || value.file_id || value.fileId;
                const hasDocName = value.name || value.file_name || value.filename || value.fileName || value.title;
                if (hasDocId || hasDocName) {
                    console.log(`DocumentSearch object detected for field ${fieldId}, preserving full object:`, value);
                    processedValue = value; // Keep the full object for display
                }
            }
            
            // Check if we're trying to overwrite a file ID with a filename (only for file upload fields)
            const currentValue = formData[fieldId];
            const isFileField = field && field.field_id === 15;
            const isCurrentValueFileId = typeof currentValue === 'number' || (typeof currentValue === 'string' && /^\d+$/.test(currentValue));
            // Only consider it a filename if it has a dot AND is not a valid decimal number AND we're dealing with a file field
            const isValidDecimal = typeof processedValue === 'string' && /^\d*\.?\d+$/.test(processedValue);
            const isNewValueFilename = isFileField && typeof processedValue === 'string' && !(/^\d+$/.test(processedValue)) && processedValue.includes('.') && !isValidDecimal;
            
            if (isCurrentValueFileId && isNewValueFilename) {
                console.warn(`Preventing overwrite of file ID ${currentValue} with filename ${processedValue} for field ${fieldId}`);
                return;
            }
            
            console.log(`Regular field change for field ${fieldId}:`, processedValue);
            setFormData((prev) => ({ ...prev, [fieldId]: processedValue }));
            
            // For newData (which goes to backend), extract ID from DocumentSearch objects
            const newDataValue = isDocumentSearchField && processedValue && typeof processedValue === 'object'
                ? (processedValue.id || processedValue.file_id || processedValue.fileId || processedValue)
                : processedValue;    // Use as-is for other field types
                
            setNewData((prev) => ({
                ...prev,
                lead_id,
                field_values: {
                ...(prev.field_values || {}),
                [fieldId]: (newDataValue === null)
                    ? null
                    : (typeof newDataValue === 'object')
                    ? JSON.stringify(newDataValue)
                    : newDataValue
                }
            }));
        }
    };

    const refreshAfterCalc = (fieldId, value) => {
        handleFieldChange(fieldId, value);
    };

    const handleSort = (key, direction) => {
        if (!customFieldSelectorIsVisibleForCase(key, fields, hiddenFieldCapability, id)) return;
        setSortConfig({ key, direction });
        
        // Reset to first page when sorting changes
        setPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const fetchFields = async (page = 1, perPage = pagination.per_page) => {
        const requestScope = currentFieldRequestScope;
        if (requestScope !== fieldRequestScopeRef.current) return;
        if (
            ![id, lead_id, section_id].every(value => isSectionReadInteger(value))
            || !isSectionReadInteger(caseType, true)
        ) {
            setFetchingData(false);
            return;
        }
        const requestGeneration = ++fieldRequestGenerationRef.current;
        const selectorsBelongToRequestScope = fieldDataScopeRef.current === requestScope;
        setHiddenFieldCapability(null);
        setFetchingData(true);
        try {
            // Build URL with sort parameters
            let url = `https://api.casedb.co/custom_fields.php?section_id=${section_id}&case_type_id=${caseType}&lead_id=${lead_id}&case_id=${encodeURIComponent(String(id))}&page=${page}&limit=${perPage}`;
            
            if (sortConfig.key && selectorsBelongToRequestScope && customFieldSelectorIsVisibleForCase(
                sortConfig.key,
                fields,
                hiddenFieldCapability,
                id
            )) {
                url += `&sort_by=${encodeURIComponent(sortConfig.key)}&sort_direction=${encodeURIComponent(sortConfig.direction)}`;
            }
            
            // Add filter parameters if they exist
            if (filterBy && filterValue && selectorsBelongToRequestScope && customFieldSelectorIsVisibleForCase(
                filterBy,
                fields,
                hiddenFieldCapability,
                id
            )) {
                url += `&filter_by=${encodeURIComponent(filterBy)}&filter_value=${encodeURIComponent(filterValue)}`;
            }
            
            url += `&${Date.now()}`;
            
            const res = await fetch(url, { credentials: 'include' });
            const data = await res.json();
            if (
                requestGeneration !== fieldRequestGenerationRef.current
                || requestScope !== fieldRequestScopeRef.current
            ) {
                return;
            }
            if (!res.ok || data?.success === false) {
                throw new Error(data?.message || 'Failed to fetch section fields');
            }

            const responseCapability = readCaseBoundHiddenFieldCapability(data, id);
            const customFields = (Array.isArray(data.custom_fields) ? data.custom_fields : [])
                .filter(field => isCustomFieldVisible(field, responseCapability, id));
            const hasMadeWholeField = customFields.some(field => Number(field.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID);
            const fieldsForSection = (!hasMadeWholeField && settlementFieldsRequired && Number(section_id) === 20 && shouldRequireSettlementMadeWhole())
                ? [
                    ...customFields,
                    createSettlementMadeWholeField(
                        Math.max(0, ...customFields.map(field => Number(field.order_id) || 0)) + 1
                    )
                ]
                : customFields;
            
            // Helper to get effective order_id considering display_when/display_multi dependencies
            const getEffectiveOrderId = (field) => {
                const displayWhen = field.display_multi || field.display_when;
                if (!displayWhen) return field.order_id;
                
                // Try to parse as JSON array
                let parsed = null;
                try {
                    parsed = JSON.parse(String(displayWhen));
                } catch (e) {
                    parsed = displayWhen;
                }
                
                // If display_multi/display_when is an array, use the order_id of the last field dependency
                if (Array.isArray(parsed) && parsed.length > 0) {
                    const lastFieldId = parsed[parsed.length - 1];
                    const lastDepField = fieldsForSection.find(f => f.id === lastFieldId);
                    return lastDepField ? lastDepField.order_id : field.order_id;
                }
                
                return field.order_id;
            };
            
            const sortedFields = [...fieldsForSection].sort((a, b) => {
                const aEffectiveOrder = getEffectiveOrderId(a);
                const bEffectiveOrder = getEffectiveOrderId(b);
                
                // First sort by effective order
                if (aEffectiveOrder !== bEffectiveOrder) {
                    return aEffectiveOrder - bEffectiveOrder;
                }
                
                // If effective orders are the same, sort by own order_id
                return a.order_id - b.order_id;
            });
            fieldDataScopeRef.current = requestScope;
            setHiddenFieldCapability(responseCapability);
            setFields(sortedFields);
            setSectionName(data.section_name);
            setSortConfig(prev => (
                prev.key && !customFieldSelectorIsVisibleForCase(prev.key, sortedFields, responseCapability, id)
                    ? { key: null, direction: 'desc' }
                    : prev
            ));
            if (filterBy && !customFieldSelectorIsVisibleForCase(filterBy, sortedFields, responseCapability, id)) {
                setFilterBy('');
                setFilterValue('');
            }
            setFormData(prev => filterCustomFieldRecordForCase(
                prev,
                sortedFields,
                responseCapability,
                id,
                { editableOnly: true }
            ));
            setNewData(prev => sanitizeCustomFieldDraftForCase(
                prev,
                sortedFields,
                responseCapability,
                id
            ));
            setSelectedRow(prev => prev
                ? filterCustomFieldRecordForCase(prev, sortedFields, responseCapability, id)
                : null
            );
            setContextMenu(prev => ({
                ...prev,
                row: prev.row
                    ? filterCustomFieldRecordForCase(prev.row, sortedFields, responseCapability, id)
                    : null
            }));
            
            // Handle pagination response
            if (data.pagination) {
                setPagination(data.pagination);
            }
            
            // Store the global total from server
            if (data.total_data_ident_sum !== undefined) {
                setGlobalTotal(data.total_data_ident_sum);
            }
            
            // Store the calculation method and difference total
            if (data.total_data_ident_calculation_method !== undefined) {
                setCalculationMethod(data.total_data_ident_calculation_method);
            }
            
            if (data.total_data_ident_diff !== undefined) {
                setDifferenceTotal(data.total_data_ident_diff);
            }
            
            // Store the field-level totals from server
            if (data.total_data_ident_by_field !== undefined) {
                setTotalsByField(data.total_data_ident_by_field);
            }
            
            mergeFieldValues(
                sortedFields,
                filterCustomFieldUpdatesForCase(
                    data.field_updates,
                    sortedFields,
                    responseCapability,
                    id
                )
            );
            setFetchingData(false);
        } catch (error) {
            if (
                requestGeneration === fieldRequestGenerationRef.current
                && requestScope === fieldRequestScopeRef.current
            ) {
                fieldDataScopeRef.current = '';
                setHiddenFieldCapability(null);
                setFields([]);
                setFieldUpdates([]);
                setGroupedData([]);
                setOriginalFormData({});
                console.error("Error fetching section data:", error);
                setFetchingData(false);
            }
        }
    };

    const resetSectionFieldState = useCallback(() => {
        setFormData({});
        setNewData({});
        setSelectedRow(null);
        setAddItemMode(false);
        setDataChanged(false);
    }, []);

    const rollbackSectionSync = useCallback(async () => {
        if (isParentCase || !id || !section_id || fieldSyncBusy) return false;

        setFieldSyncBusy(true);
        try {
            resetSectionFieldState();
            const response = await fetch('https://api.casedb.co/case_sync_rules.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'rollback_section',
                    child_case_id: Number(id),
                    parent_case_id: parentCaseId,
                    section_id: Number(section_id)
                })
            });

            const data = await response.json();
            if (data?.success) {
                setChildSyncStatus(data.status || null);
                setMessage({ type: 'success', text: data.message || 'Sync rolled back successfully.' });
                resetSectionFieldState();
                await fetchFields(pagination.current_page, pagination.per_page);
                return true;
            }

            setMessage({ type: 'error', text: data?.message || 'Failed to roll back synced data.' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to roll back synced data.' });
        } finally {
            setFieldSyncBusy(false);
        }

        return false;
    }, [fetchFields, fieldSyncBusy, id, isParentCase, pagination.current_page, pagination.per_page, parentCaseId, resetSectionFieldState, section_id]);

    const syncSectionFromParent = useCallback(async () => {
        if (isParentCase || !id || !section_id || fieldSyncBusy) return false;

        setFieldSyncBusy(true);
        try {
            resetSectionFieldState();
            const response = await fetch('https://api.casedb.co/case_sync_rules.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'sync_section_to_child',
                    child_case_id: Number(id),
                    parent_case_id: parentCaseId,
                    section_id: Number(section_id)
                })
            });

            const data = await response.json();
            if (data?.success) {
                setChildSyncStatus(data.status || null);
                setMessage({ type: 'success', text: data.message || 'Section synced from parent.' });
                resetSectionFieldState();
                await fetchFields(pagination.current_page, pagination.per_page);
                return true;
            }

            setMessage({ type: 'error', text: data?.message || 'Failed to sync section from parent.' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to sync section from parent.' });
        } finally {
            setFieldSyncBusy(false);
        }

        return false;
    }, [fetchFields, fieldSyncBusy, id, isParentCase, pagination.current_page, pagination.per_page, parentCaseId, resetSectionFieldState, section_id]);

    const mergeFieldValues = (customFields, updates) => {
        let flat = {};
        const flatMap = {};
        const grouped = [];
    
        updates.forEach(update => {
            // Always push to grouped array for DataTable
            const processedUpdate = { ...update };
            
            // Process file fields in grouped data as well
            Object.entries(processedUpdate).forEach(([key, val]) => {
                if (key !== 'group_id') {
                    const fieldDef = customFields.find(f => String(f.id) === String(key));
                    
                    if (fieldDef?.field_id === 15) {
                        // For file fields, extract the numeric file ID from display strings like "[12] - filename.pdf"
                        if (typeof val === 'string' && val.match(/^\[\d+\]/)) {
                            const match = val.match(/^\[(\d+)\]/);
                            processedUpdate[key] = match ? parseInt(match[1]) : val;
                        }
                    } else if (fieldDef?.field_id === 16) {
                        // For DocumentSearch fields, preserve the object for display purposes
                        // ID extraction will happen during save operations
                        processedUpdate[key] = val;
                    }
                }
            });
            
            grouped.push(processedUpdate);
            
            if (update.group_id === 0) {
                flat = update;
    
                Object.entries(update).forEach(([key, val]) => {
                    if (key !== 'group_id') {
                        const fieldDef = customFields.find(f => String(f.id) === String(key));
                
                        if (fieldDef?.field_id === 17) {
                            if (isDateCalculationField(fieldDef)) {
                                flatMap[key] = val || '';
                            } else {
                                const num = parseFloat(val);
                                flatMap[key] = isNaN(num) ? 0 : parseFloat(num.toFixed(2));
                            }
                        } else if (fieldDef?.field_id === 15) {
                            // For file fields, extract the numeric file ID from display strings like "[12] - filename.pdf"
                            if (typeof val === 'string' && val.match(/^\[\d+\]/)) {
                                const match = val.match(/^\[(\d+)\]/);
                                flatMap[key] = match ? parseInt(match[1]) : val;
                            } else {
                                flatMap[key] = val;
                            }
                        } else if (fieldDef?.field_id === 16) {
                            // For DocumentSearch fields, preserve the object for display purposes
                            // ID extraction will happen during save operations
                            flatMap[key] = val;
                        } else {
                            flatMap[key] = val;
                        }
                    }
                });                
            }
        });
    
        setFieldUpdates((prev) => JSON.stringify(prev) !== JSON.stringify(flatMap) ? flatMap : prev);
        setGroupedData((prev) => JSON.stringify(prev) !== JSON.stringify(grouped) ? grouped : prev);
        
        // Store original form data for unsaved changes detection
        setOriginalFormData((prev) => {
            const newOriginal = { ...flatMap };
            // Only update if actually different to avoid unnecessary re-renders
            return JSON.stringify(prev) !== JSON.stringify(newOriginal) ? newOriginal : prev;
        });
    };



    const buildProcessedFieldData = useCallback((data = {}) => {
        const processedData = sanitizeCustomFieldDraftForCase(
            data,
            fields,
            hiddenFieldCapability,
            id
        );
        if (processedData.field_values) {
            const processedFieldValues = { ...processedData.field_values };

            Object.entries(processedFieldValues).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    const processedArray = value.map(item => {
                        if (item && typeof item === 'object') {
                            const itemId = item.id || item.file_id || item.fileId;
                            const itemName = item.name || item.file_name || item.filename || item.fileName || item.title;
                            if (itemId || itemName) {
                                console.log(`Converting DocumentSearch object in array to ID: ${itemId} for field ${key} before save`);
                                return itemId ?? item;
                            }
                        }
                        return item;
                    });
                    processedFieldValues[key] = processedArray;
                } else if (value && typeof value === 'object') {
                    const valueId = value.id || value.file_id || value.fileId;
                    const valueName = value.name || value.file_name || value.filename || value.fileName || value.title;
                    if (valueId || valueName) {
                        console.log(`Converting DocumentSearch object to ID: ${valueId} for field ${key} before save`);
                        processedFieldValues[key] = valueId ?? value;
                    }
                }
            });

            processedData.field_values = processedFieldValues;
        }

        return processedData;
    }, [fields, hiddenFieldCapability, id]);

    const updateFields = async (data, options = {}) => {
        const updateRequestScope = currentFieldRequestScope;
        if (updateRequestScope !== fieldRequestScopeRef.current || fieldSaveScope !== fieldSaveScopeRef.current) {
            return { success: false, error: 'The case section changed before this update could start.' };
        }
        const request = ++fieldSaveScope.request;
        const isCurrentSave = () => fieldSaveScope === fieldSaveScopeRef.current && request === fieldSaveScope.request;
        const staleSave = () => ({ success: false, error: 'This update no longer belongs to the current save request.' });
        const reportSaveFailure = (acknowledged = false) => {
            if (!isCurrentSave()) return staleSave();
            const error = acknowledged
                ? 'Fields were saved, but follow-up processing did not complete. Review the saved fields before trying again.'
                : 'The save did not complete. Some fields may already have been saved. Your draft is still here; review the saved fields before trying again.';
            setMessage({ type: 'error', text: error, duration: 0 });
            return { success: false, error };
        };
        const {
            preserveState = false,
            refresh = true,
            showSuccessMessage = true
        } = options;

        const hadFieldValues = Object.prototype.hasOwnProperty.call(data || {}, 'field_values');
        const scopedData = {
            ...buildProcessedFieldData(data),
            lead_id,
            case_id: id,
            section_id: Number(section_id)
        };
        if (hadFieldValues && Object.keys(scopedData.field_values || {}).length === 0) {
            return reportSaveFailure();
        }

        let saveAcknowledged = false;
        try {
            await ensureCaseDbCsrfToken();
            if (!isCurrentSave()) return staleSave();
            const res = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json'
                }, 'POST'),
                body: JSON.stringify(scopedData),
                credentials: 'include'
            });

            const result = await res.json();
            if (!isCurrentSave()) return staleSave();

            if (res.ok && result?.success === true) {
                saveAcknowledged = true;
                const groupId = result.group_id;

                if (preserveState) {
                    setFormData(prev => ({ ...prev, group_id: groupId }));
                    setNewData(prev => ({ ...prev, group_id: groupId }));
                } else {
                    setAddItemMode(false);
                    setForcedSectionDataMode(false);
                    setNewData({});
                    setFormData({});
                    setSelectedRow(null);
                }

                if (showSuccessMessage) {
                    setMessage({
                        type: 'success',
                        text: 'Fields updated successfully!'
                    });
                }

                const isFile = (val) =>
                    val &&
                    typeof val === 'object' &&
                    typeof val.name === 'string' &&
                    typeof val.type === 'string' &&
                    typeof val.size === 'number' &&
                    typeof val.lastModified === 'number';

                // Check if there are any actual File objects that need uploading
                // (Files that have been processed by immediate upload will be file IDs, not File objects)
                const hasUnprocessedFileUploads = Object.values(formData).some(val =>
                    isFile(val) || (Array.isArray(val) && val.length && isFile(val[0]))
                );

                console.log("Checking for unprocessed file uploads:", hasUnprocessedFileUploads);
                console.log("FormData values:", Object.entries(formData).map(([key, val]) => [key, typeof val, isFile(val)]));

                if (hasUnprocessedFileUploads) {
                    console.log("Found unprocessed file uploads, calling uploadFiles...");
                    await uploadFiles(groupId);
                } else {
                    console.log("No unprocessed file uploads found, skipping uploadFiles call");
                }

                // CRITICAL: Must await fetchFields() to ensure groupedData is updated
                // before any subsequent operations (like startCheckRequest) that depend on it
                if (refresh) {
                    await fetchFields();
                }
                if (!isCurrentSave()) return staleSave();
                
                // Return the group_id so caller can use it immediately
                return { success: true, group_id: groupId };

            } else {
                return reportSaveFailure();
            }
        } catch (error) {
            return reportSaveFailure(saveAcknowledged);
        }
    };

    const saveFields = async () => {
        const requestBeforeDelay = fieldSaveScope.request;
        // Allow state updates to complete
        await new Promise(resolve => setTimeout(resolve, 100));
        if (fieldSaveScope !== fieldSaveScopeRef.current || requestBeforeDelay !== fieldSaveScope.request) {
            return { success: false, error: 'The case section or save request changed before saving.' };
        }

        const processedData = buildProcessedFieldData(selectedRow
            ? { ...newData, group_id: selectedRow.group_id }
            : newData
        );
        if (Number(section_id) === 20 && shouldRequireSettlementMadeWhole() && processedData.field_values && Object.prototype.hasOwnProperty.call(processedData.field_values, SETTLEMENT_MADE_WHOLE_FIELD_ID)) {
            delete processedData.field_values[SETTLEMENT_MADE_WHOLE_FIELD_ID];
        }
        const hasProcessedFieldValues = processedData.field_values && Object.keys(processedData.field_values).length > 0;

        console.log("=== PROCESSED DATA FOR SAVE ===");
        console.log("processedData:", processedData);
        console.log("==============================");

        // No need to filter file fields anymore since they now contain file IDs
        const result = hasProcessedFieldValues
            ? await updateFields(processedData)
            : { success: true, group_id: selectedRow?.group_id ?? newData?.group_id ?? 0 };
        
        // If save was successful, update originalFormData to match current state
        if (result && result.success) {
            setOriginalFormData({ ...formData });
            if (!selectedRow && !newData?.group_id) {
                setForcedSectionDataMode(false);
            }
            
            // After successful field update, trigger phases sync via parent callback
            // This ensures subphases stay in sync without interrupting settlement verification
            if (onPhasesSynced) {
                onPhasesSynced();
            }
            
            // Check if this is section 20 and we should check settlement fields
            // Check when: settlementFieldsRequired is true, or we previously saved settlement fields
            if (section_id == 20 && (settlementFieldsRequired || settlementFieldsSaved)) {
                // Create merged state: existing values (fieldUpdates) + newly saved values
                const updatedFormData = { ...fieldUpdates, ...formData }; // Start with existing saved values plus local settlement override state
                if (processedData.field_values) {
                    Object.keys(processedData.field_values).forEach(fieldId => {
                        updatedFormData[fieldId] = processedData.field_values[fieldId];
                    });
                }
                
                // Check which settlement fields now have values (checking merged state)
                const settlementRequiredIds = getSettlementRequiredFieldIds();
                const filledSettlementFields = settlementRequiredIds.filter(fieldId => isSettlementRequiredValueFilled(fieldId, updatedFormData));
                
                console.log('Settlement field check:', {
                    field200: updatedFormData[200],
                    field203: updatedFormData[203],
                    madeWhole: updatedFormData[SETTLEMENT_MADE_WHOLE_FIELD_ID],
                    field200_source: processedData.field_values?.[200] ? 'just saved' : 'existing',
                    field203_source: processedData.field_values?.[203] ? 'just saved' : 'existing',
                    filledCount: filledSettlementFields.length,
                    requiredCount: settlementRequiredIds.length,
                    activeFinalFieldId: getActiveSettlementFinalFieldId(),
                    settlementFieldsRequired,
                    settlementFieldsSaved
                });
                
                // If settlement fields appear filled locally, confirm with backend before verification
                if (filledSettlementFields.length === settlementRequiredIds.length && settlementFieldsRequired && !settlementFieldsSaved) {
                    try {
                        const madeWholeValueForBackend = pendingSettlementMadeWholeValueRef.current ?? pendingSettlementMadeWholeValue ?? getSettlementMadeWholeResponseValue(formData[SETTLEMENT_MADE_WHOLE_FIELD_ID]);
                        const phaseCheckResponse = await fetch('https://api.casedb.co/phases.php', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                                case_id: id,
                                phase_id: pendingSettlementPhase || 19,
                                is_subphase: false,
                                create_feed_entry: false,
                                ...(
                                    shouldRequireSettlementMadeWhole() &&
                                    madeWholeValueForBackend !== null &&
                                    madeWholeValueForBackend !== undefined &&
                                    madeWholeValueForBackend !== ''
                                        ? { settlement_made_whole_value: Number(madeWholeValueForBackend) }
                                        : {}
                                )
                            })
                        });
                        const phaseCheck = await phaseCheckResponse.json();

                        console.log('Settlement backend readiness check:', phaseCheck);

                        if (phaseCheck.fields_missing) {
                            const backendMissingFieldId = phaseCheck.requires_made_whole && phaseCheck.has_made_whole === false
                                ? SETTLEMENT_MADE_WHOLE_FIELD_ID
                                : getMissingSettlementRequiredFieldIds(updatedFormData)[0];
                            if (backendMissingFieldId) {
                                scrollToSettlementField(backendMissingFieldId);
                            }
                            setMessage({
                                type: 'error',
                                text: phaseCheck.requires_made_whole && phaseCheck.has_made_whole === false
                                    ? 'Made Whole? is required before settlement verification.'
                                    : (phaseCheck.message || 'Settlement fields are required before verification.')
                            });
                            return result;
                        }

                        if (phaseCheck.fields_missing === false) {
                            console.log('Settlement fields verified by backend, navigating to section 24');
                            setPendingSettlementMadeWholeValue(null);
                            pendingSettlementMadeWholeValueRef.current = null;
                            setSettlementFieldsSaved(true);
                            window.dispatchEvent(new CustomEvent('navigateToVerification', {
                                detail: { sectionId: 24, pendingPhase: pendingSettlementPhase || 19 }
                            }));
                            window.dispatchEvent(new CustomEvent('settlementVerificationNotice', {
                                detail: {
                                    message: phaseCheck.message || 'Settlement fields verified. Please confirm to proceed.'
                                }
                            }));
                            return result;
                        }

                        if (!phaseCheck.success) {
                            setMessage({ type: 'error', text: phaseCheck.message || 'Unable to verify settlement fields.' });
                            return result;
                        }
                    } catch (error) {
                        console.error('Settlement backend readiness check failed:', error);
                        setMessage({ type: 'error', text: 'Unable to verify settlement fields.' });
                        return result;
                    }
                }
            }

            if (section_id == 20 && (disengagementFieldsRequired || disengagementFieldsSaved)) {
                const updatedFormData = { ...fieldUpdates, ...formData };
                if (processedData.field_values) {
                    Object.keys(processedData.field_values).forEach(fieldId => {
                        updatedFormData[fieldId] = processedData.field_values[fieldId];
                    });
                }

                const disengagementRequiredIds = getDisengagementRequiredFieldIds();
                const filledDisengagementFields = disengagementRequiredIds.filter(fieldId => isDisengagementRequiredValueFilled(fieldId, updatedFormData));

                console.log('Disengagement field check:', {
                    requiredIds: disengagementRequiredIds,
                    filledCount: filledDisengagementFields.length,
                    requiredCount: disengagementRequiredIds.length,
                    disengagementFieldsRequired,
                    disengagementFieldsSaved
                });

                if (filledDisengagementFields.length !== disengagementRequiredIds.length) {
                    const missingFieldId = getMissingDisengagementRequiredFieldIds(updatedFormData)[0];
                    if (missingFieldId) {
                        scrollToSettlementField(missingFieldId, { focus: false });
                    }
                    setMessage({ type: 'error', text: 'Disengagement fields are required before continuing.' });
                    return result;
                }

                if (disengagementFieldsRequired && !disengagementFieldsSaved) {
                    try {
                        const phaseCheckResponse = await fetch('https://api.casedb.co/phases.php', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                                case_id: id,
                                phase_id: pendingDisengagementPhase || DISENGAGEMENT_PHASE_ID,
                                is_subphase: false,
                                create_feed_entry: false
                            })
                        });
                        const phaseCheck = await phaseCheckResponse.json();

                        console.log('Disengagement backend readiness check:', phaseCheck);

                        if (phaseCheck.fields_missing) {
                            const backendMissingFieldId = phaseCheck.missing_disengagement_field_ids?.[0] || getMissingDisengagementRequiredFieldIds(updatedFormData)[0];
                            if (backendMissingFieldId) {
                                scrollToSettlementField(backendMissingFieldId, { focus: false });
                            }
                            setMessage({ type: 'error', text: phaseCheck.message || 'Disengagement fields are required before continuing.' });
                            return result;
                        }

                        if (phaseCheck.fields_missing === false) {
                            setDisengagementFieldsSaved(true);
                            window.dispatchEvent(new CustomEvent('navigateToDisengagementActivity', {
                                detail: {
                                    pendingPhase: pendingDisengagementPhase || DISENGAGEMENT_PHASE_ID,
                                    draft: phaseCheck.disengagement_activity_draft || null
                                }
                            }));
                            return result;
                        }

                        if (!phaseCheck.success) {
                            setMessage({ type: 'error', text: phaseCheck.message || 'Unable to verify disengagement fields.' });
                            return result;
                        }
                    } catch (error) {
                        console.error('Disengagement backend readiness check failed:', error);
                        setMessage({ type: 'error', text: 'Unable to verify disengagement fields.' });
                        return result;
                    }
                }
            }
        }
        
        return result; // Return the result including group_id
    };

    const prepareDocGenGeneration = async (docgenFieldId) => {
        const docgenField = fields.find(field => Number(field.id) === Number(docgenFieldId));
        if (!isFieldEditable(docgenField)) {
            return { success: false, message: 'This restricted field is read-only.' };
        }
        if (!docgenField || docgenField.add_item !== 1) {
            return {
                success: true,
                groupId: selectedRow?.group_id ?? formData?.group_id ?? newData?.group_id ?? null
            };
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        const existingGroupId = selectedRow?.group_id ?? formData?.group_id ?? newData?.group_id ?? null;
        const fieldValuesFromForm = {};

        fields
            .filter(field => isFieldEditable(field) && field?.add_item === 1 && Number(field.id) !== Number(docgenFieldId))
            .forEach(field => {
                if (formData[field.id] !== undefined) {
                    fieldValuesFromForm[field.id] = formData[field.id];
                }
            });

        const payload = buildProcessedFieldData({
            ...newData,
            lead_id,
            case_id: id,
            ...(existingGroupId !== null && existingGroupId !== undefined ? { group_id: existingGroupId } : {}),
            field_values: {
                ...fieldValuesFromForm,
                ...(newData.field_values || {})
            }
        });

        if (!payload.field_values || Object.keys(payload.field_values).length === 0) {
            return { success: true, groupId: existingGroupId };
        }

        const result = await updateFields(payload, {
            preserveState: true,
            refresh: false,
            showSuccessMessage: false
        });

        if (!result?.success) {
            return result;
        }

        return {
            success: true,
            groupId: result.group_id ?? existingGroupId
        };
    };

    const prepareDocGenReview = async (docgenFieldId) => {
        const docgenField = fields.find(field => Number(field.id) === Number(docgenFieldId));
        const currentGroupId = selectedRow?.group_id ?? formData?.group_id ?? newData?.group_id ?? null;
        if (!isFieldEditable(docgenField)) {
            return { success: false, message: 'This restricted field is read-only.' };
        }
        if (!docgenField || Number(docgenField.add_item) !== 1) {
            return { success: true, groupId: currentGroupId };
        }

        if (currentGroupId === null || currentGroupId === undefined || Number(currentGroupId) <= 0) {
            return {
                success: false,
                message: 'Save this item before reviewing its document values.'
            };
        }

        return { success: true, groupId: currentGroupId };
    };

    const handleDocGenGenerated = (fieldId, generatedValue, metadata = {}) => {
        const docgenField = fields.find(field => Number(field?.id) === Number(fieldId));
        if (!isFieldEditable(docgenField)) return;
        const resolvedGroupId = metadata?.groupId ?? metadata?.group_id ?? selectedRow?.group_id ?? formData?.group_id ?? newData?.group_id ?? null;

        setFormData(prev => ({
            ...prev,
            ...(resolvedGroupId !== null && resolvedGroupId !== undefined ? { group_id: resolvedGroupId } : {}),
            [fieldId]: generatedValue
        }));

        setNewData(prev => {
            const nextFieldValues = { ...(prev.field_values || {}) };
            delete nextFieldValues[fieldId];

            return {
                ...prev,
                ...(resolvedGroupId !== null && resolvedGroupId !== undefined ? { group_id: resolvedGroupId } : {}),
                field_values: nextFieldValues
            };
        });

        setTimeout(() => {
            fetchDocuments?.();
            fetchFields(pagination.current_page, pagination.per_page);
        }, 500);
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.total_pages) {
            fetchFields(newPage, pagination.per_page);
        }
    };

    const handlePerPageChange = (newPerPage) => {
        fetchFields(1, newPerPage);
    };

    // Hide context menu when clicking elsewhere
    useEffect(() => {
        const handleGlobalClick = () => setContextMenu(prev => ({ ...prev, visible: false, row: null }));
        window.addEventListener('click', handleGlobalClick);
        return () => window.removeEventListener('click', handleGlobalClick);
    }, []);

    const handleRowContextMenu = (event, row) => {
        if (![19, 27].includes(section_id)) return;
        setContextMenu({ visible: true, x: event.clientX, y: event.clientY, row });
    };

    const copyGroupToTrust = async () => {
        // Prefer context menu selection, fall back to the currently opened row
        const sourceRow = contextMenu.row || selectedRow;
        if (customFieldRecordContainsReadonlyValues(sourceRow, fields, hiddenFieldCapability, id)) {
            setMessage({ text: 'This group contains restricted read-only fields and cannot be copied.', type: 'error' });
            setContextMenu({ visible: false, x: 0, y: 0, row: null });
            return;
        }
        const resolvedGroupId = contextMenu.row?.group_id ?? selectedRow?.group_id ?? newData?.group_id;
        const numericGroupId = resolvedGroupId === undefined || resolvedGroupId === null ? NaN : Number(resolvedGroupId);
        const numericCaseId = Number(id) || Number(caseData?.id);

        if (!Number.isFinite(numericCaseId) || numericCaseId <= 0 || !Number.isFinite(numericGroupId) || numericGroupId < 0) {
            setMessage({ text: 'Missing or invalid case/group for trust copy.', type: 'error' });
            setContextMenu({ visible: false, x: 0, y: 0, row: null });
            return;
        }

        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    copy_to_trust: true,
                    case_id: numericCaseId,
                    group_id: numericGroupId,
                    section_id
                })
            });

            const result = await response.json();
            if (result.success) {
                setMessage({ text: 'Copied to Trust Accounting.', type: 'success' });
                await fetchFields();
            } else {
                setMessage({ text: result.message || 'Copy to Trust Accounting failed.', type: 'error' });
            }
        } catch (error) {
            setMessage({ text: 'Copy to Trust Accounting failed.', type: 'error' });
        }

        setContextMenu({ visible: false, x: 0, y: 0, row: null });
    };

    useEffect(() => {
        setDataChanged(true);
    }, [formData]);

    const getFieldValue = (field) => {
        if (!field) return '';
        
        if (field.add_item === 1) {
            // For Add Item fields, prioritize formData (user input) over selectedRow (initial data)
            // This ensures that when users type in fields, their input is shown
            if (Object.prototype.hasOwnProperty.call(formData, field.id)) {
                const currentValue = formData[field.id];
                const isDocField = field.field_id === 15 || field.field_id === 16;
                const isNumericId = typeof currentValue === 'number' || (typeof currentValue === 'string' && /^\d+$/.test(currentValue));

                if (isDocField && isNumericId && selectedRow && selectedRow[field.id]) {
                    const selectedValue = selectedRow[field.id];
                    if (selectedValue && typeof selectedValue === 'object') {
                        const selectedId = selectedValue.id || selectedValue.file_id || selectedValue.fileId || null;
                        if (selectedId && String(selectedId) === String(currentValue)) {
                            return selectedValue;
                        }
                    }
                }

                return currentValue;
            }
            // Fall back to selectedRow data if no user input yet
            if (selectedRow && selectedRow[field.id] !== undefined) {
                let value = selectedRow[field.id];
                
                // For DocumentSearch fields, preserve the object for display purposes
                if (field.field_id === 15 || field.field_id === 16) {
                    if (Array.isArray(value)) {
                        // Handle array of DocumentSearch objects - preserve objects for display
                        return value;
                    } else if (value && typeof value === 'object') {
                        // Handle single DocumentSearch object - preserve object for display
                        return value;
                    }
                }
                
                return value;
            }
            return '';
        } else {
            if (Object.prototype.hasOwnProperty.call(formData, field.id)) {
                return formData[field.id];
            }
    
            const stored = fieldUpdates?.[field.id];
            if (Object.prototype.hasOwnProperty.call(fieldUpdates || {}, field.id)) {
                // For DocumentSearch fields, preserve the object format for display
                if ((field.field_id === 15 || field.field_id === 16) && stored && typeof stored === 'object') {
                    return stored;
                }
                return stored;
            }
    
            if (field.field_id === 17) {
                return isDateCalculationField(field) ? (stored || '') : (stored || 0);
            }
    
            if (field.field_id === 12) return 2;
    
            return '';
        }
    };

    // Helper function to extract field IDs and conditions from display_when/display_multi
    const parseDisplayCriteria = (field) => {
        // Check display_multi first (array format), then fall back to display_when (single value)
        const displayWhen = field.display_multi || field.display_when;
        let isAnswered = field.is_answered;
        
        if (!displayWhen) return null;
        
        // Parse is_answered as JSON if it's a string
        if (typeof isAnswered === 'string') {
            try {
                isAnswered = JSON.parse(isAnswered);
            } catch (e) {
                // Keep as string if parse fails
            }
        }
        
        // Try to parse as JSON array first (display_multi)
        let parsed = null;
        try {
            parsed = JSON.parse(String(displayWhen));
        } catch (e) {
            parsed = displayWhen;
        }
        
        // Array format: [164, 199] with matching is_answered [1, 0]
        if (Array.isArray(parsed)) {
            const isAnsweredArray = Array.isArray(isAnswered) ? isAnswered : [isAnswered];
            
            return parsed.map((fieldId, index) => ({
                fieldId: fieldId,
                condition: (isAnsweredArray[index] === 0 || isAnsweredArray[index] === '0')
                    ? '0'
                    : (isAnsweredArray[index] ?? '')
            }));
        }
        
        // Single int/number format
        if (typeof parsed === 'number' || (typeof parsed === 'string' && !isNaN(parsed))) {
            const fieldId = typeof parsed === 'number' ? parsed : parseInt(parsed);
            // If isAnswered is an array, pass it as JSON so evaluateCriterion can handle multiple values
            const isAnsweredVal = Array.isArray(isAnswered)
                ? JSON.stringify(isAnswered)
                : ((isAnswered === 0 || isAnswered === '0') ? '0' : (isAnswered ?? ''));
            return [{
                fieldId: fieldId,
                condition: isAnsweredVal
            }];
        }
        
        // String format with operators (legacy)
        return null;
    };

    const normalizeComparableValue = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
            if (val.id !== undefined) return String(val.id);
            if (val.value !== undefined) return String(val.value);
            if (val.name !== undefined) return String(val.name);
        }
        return String(val);
    };

    const getFieldOptions = (field) => {
        if (!field?.options) return [];
        try {
            return Array.isArray(field.options) ? field.options : JSON.parse(field.options);
        } catch (error) {
            console.error('Error parsing field options:', error);
            return [];
        }
    };

    const valueMatchesCondition = (controllingField, controllingValue, cleanAnswers) => {
        const normalizedValue = normalizeComparableValue(controllingValue);
        const valueSet = Array.isArray(controllingValue)
            ? controllingValue.map(normalizeComparableValue)
            : [normalizedValue];

        if (!cleanAnswers.length) return false;

        const options = getFieldOptions(controllingField);
        const optionsByIndex = options.map((opt) => {
            if (opt && typeof opt === 'object') {
                const id = opt.id ?? opt.value ?? opt.name;
                const name = opt.name ?? opt.label ?? opt.value ?? opt.id;
                const value = opt.value ?? opt.id ?? opt.name;
                return {
                    id: id !== undefined ? String(id) : '',
                    name: name !== undefined ? String(name) : '',
                    value: value !== undefined ? String(value) : ''
                };
            }
            const str = String(opt);
            return { id: str, name: str, value: str };
        });

        return cleanAnswers.some((answer) => {
            const answerStr = String(answer);

            // Direct value match
            if (valueSet.includes(answerStr)) return true;

            // Match option index
            const index = parseInt(answerStr, 10);
            if (!Number.isNaN(index) && optionsByIndex[index]) {
                const option = optionsByIndex[index];
                if (valueSet.includes(option.id) || valueSet.includes(option.value) || valueSet.includes(option.name)) {
                    return true;
                }
            }

            // Match option value/name directly
            if (optionsByIndex.length > 0) {
                return optionsByIndex.some(option =>
                    answerStr === option.id || answerStr === option.value || answerStr === option.name
                ) && valueSet.some(val =>
                    optionsByIndex.some(option => val === option.id || val === option.value || val === option.name)
                );
            }

            return false;
        });
    };

    const findFieldById = (fieldId) => fields.find(f => isFieldRenderable(f) && Number(f.id) === Number(fieldId));

    // Helper function to evaluate a single criterion
    const evaluateCriterion = (fieldId, condition, controllingField) => {
        if (!controllingField) return false;
        
        let controllingValue = getFieldValue(controllingField);
        
        // Handle special conditions
        if (condition === '[!]') {
            return controllingValue === null || controllingValue === undefined || controllingValue === "";
        }
        
        if (condition === "" || condition === null) {
            return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
        }
        
        // Special handling for multiselect fields (field_id === 11)
        if (controllingField.field_id === 11) {
            const selectedValues = Array.isArray(controllingValue) ? controllingValue : [];
            
            let multiselectOptions = [];
            try {
                const rawOptions = Array.isArray(controllingField.options)
                    ? controllingField.options
                    : controllingField.options
                        ? JSON.parse(controllingField.options)
                        : [];
                
                multiselectOptions = rawOptions.map(opt =>
                    typeof opt === 'object' && opt !== null
                        ? opt
                        : { id: opt, name: String(opt) }
                );
            } catch (error) {
                console.error("Error parsing multiselect options:", error);
                multiselectOptions = [];
            }
            
            let parsedAnswers = [];
            try {
                const parsed = JSON.parse(condition);
                if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
                else parsedAnswers = [String(parsed)];
            } catch {
                parsedAnswers = String(condition).split(',').map(v => v.trim());
            }
            
            const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
            const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));
            
            const targetOptionIds = cleanAnswers.map(indexStr => {
                const index = parseInt(indexStr);
                return multiselectOptions[index]?.id;
            }).filter(id => id !== undefined);
            
            if (isNegated) {
                return !targetOptionIds.some(optionId => selectedValues.includes(optionId));
            } else {
                return targetOptionIds.some(optionId => selectedValues.includes(optionId));
            }
        }
        
        controllingValue = normalizeValueToIndex(controllingField, controllingValue);
        
        let parsedAnswers = [];
        try {
            const parsed = JSON.parse(condition);
            if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
            else parsedAnswers = [String(parsed)];
        } catch {
            parsedAnswers = String(condition).split(',').map(v => v.trim());
        }
        
        const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
        const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

        const isListField = [10, 12].includes(controllingField.field_id);
        if (isListField) {
            const options = getFieldOptions(controllingField);
            let controllingIndex = null;

            if (typeof controllingValue === 'number' || (typeof controllingValue === 'string' && controllingValue.trim() !== '' && !isNaN(controllingValue))) {
                controllingIndex = parseInt(controllingValue, 10);
            } else if (typeof controllingValue === 'string') {
                const matchIndex = options.findIndex(opt => String(opt).toLowerCase?.() === controllingValue.toLowerCase?.());
                controllingIndex = matchIndex >= 0 ? matchIndex : null;
            }

            const answerIndices = cleanAnswers
                .map((answer) => parseInt(answer, 10))
                .filter((num) => !Number.isNaN(num));

            if (controllingIndex !== null && answerIndices.length > 0) {
                const matchesByIndex = answerIndices.includes(controllingIndex);
                return isNegated ? !matchesByIndex : matchesByIndex;
            }
        }

        const matches = valueMatchesCondition(controllingField, controllingValue, cleanAnswers);
        return isNegated ? !matches : matches;
    };

    const shouldDisplay = (field) => {
        if (!isFieldRenderable(field)) return false;
        
        if (field.add_item === 1 && !addItemMode) return false;
    
        if (Number(field.case_type_id) !== Number(caseType) && Number(field.case_type_id) !== 0) return false;
    
        // If no display_when condition, show the field
        if (!field.display_when && !field.display_multi) return true;

        // Try to parse array format first (display_multi or display_when)
        const arrayParsed = parseDisplayCriteria(field);
        if (arrayParsed && arrayParsed.length > 0) {
            // Array format - evaluate all criteria with AND logic
            const results = arrayParsed.map(criterion => {
                const controllingField = findFieldById(criterion.fieldId);
                return evaluateCriterion(criterion.fieldId, criterion.condition, controllingField);
            });
            
            return results.every(result => result === true);
        }

        // Check for logical operators in multi_criteria first, fallback to display_when (legacy string format)
        const criteriaStr = field.multi_criteria ? String(field.multi_criteria) : String(field.display_when || field.display_multi || '');
        const hasAndOperator = criteriaStr.includes('&&');
        const hasOrOperator = criteriaStr.includes('||');
        
        if (hasAndOperator || hasOrOperator) {
            // Handle multiple conditions with logical operators
            const operator = hasAndOperator ? '&&' : '||';
            const fieldIds = criteriaStr.split(operator).map(id => parseInt(id.trim()));
            const hasAnswered = field.is_answered !== null && field.is_answered !== undefined;
            const conditions = hasAnswered
                ? (Array.isArray(field.is_answered) ? field.is_answered : String(field.is_answered).split(operator))
                : [];
            
            // Evaluate each condition
            const results = fieldIds.map((fieldId, index) => {
                const controllingField = findFieldById(fieldId);
                const condition = Array.isArray(conditions) ? (conditions[index] ? conditions[index].trim() : '') : '';
                return evaluateCriterion(fieldId, condition, controllingField);
            });
            
            // Apply logical operator to results
            if (operator === '&&') {
                return results.every(result => result === true);
            } else { // ||
                return results.some(result => result === true);
            }
        } else {
            // Handle single condition (original logic)
            let fieldIdToCheck = null;
            try {
                const parsed = JSON.parse(criteriaStr);
                fieldIdToCheck = parseInt(parsed);
            } catch (e) {
                fieldIdToCheck = parseInt(field.display_when || field.display_multi);
            }
            
            const controllingField = findFieldById(fieldIdToCheck);
            if (!controllingField) return false;
            
            const condition = Array.isArray(field.is_answered)
                ? ((field.is_answered[0] === 0 || field.is_answered[0] === '0') ? '0' : (field.is_answered[0] ?? ''))
                : ((field.is_answered === 0 || field.is_answered === '0') ? '0' : (field.is_answered ?? ''));
            return evaluateCriterion(fieldIdToCheck, condition, controllingField);
        }
    };

    // Recommendations only affect an explicitly opened add-item draft. Keep
    // distinct draft identities even when cancelling and reopening a new item.
    const recommendationGroupId = selectedRow?.group_id ?? newData?.group_id ?? null;
    const recommendationDraftScope = JSON.stringify([
        currentFieldRequestScope, user_id, !!addItemMode, recommendationGroupId,
    ]);
    const recommendationDraftRef = useRef({ scope: recommendationDraftScope, revision: 0 });
    if (recommendationDraftRef.current.scope !== recommendationDraftScope) {
        recommendationDraftRef.current = {
            scope: recommendationDraftScope,
            revision: recommendationDraftRef.current.revision + 1,
        };
    }
    const recommendationFields = fields.filter(field => Number(field.add_item) === 1
        && [8, 10].includes(Number(field.field_id)) && isFieldEditable(field) && shouldDisplay(field));
    const recommendations = useFieldRecommendations({
        caseId: id,
        sectionId: section_id,
        groupId: recommendationGroupId,
        draftKey: `${currentFieldRequestScope}:${recommendationDraftRef.current.revision}`,
        userId: user_id,
        fields: recommendationFields,
        fieldValues: Object.fromEntries(recommendationFields.map(field => [field.id, getFieldValue(field)])),
        enabled: !!addItemMode && fieldDataScopeRef.current === currentFieldRequestScope,
        onAccept: (fieldId, value) => {
            const field = recommendationFields.find(candidate => String(candidate.id) === String(fieldId));
            if (!field || !addItemMode || !isFieldEditable(field) || !shouldDisplay(field)
                || !isRecommendationTargetBlank(getFieldValue(field))) return;
            // Apply only to the ordinary draft; the user's Save/Create remains
            // the existing case-data write and authorization path.
            handleFieldChange(field.id, value);
        },
    });

    useEffect(() => {
        fetchFields();
    }, [id, lead_id, section_id, template_id, caseType]);

    // Reset pagination when section changes
    useEffect(() => {
        setPagination(prev => ({ ...prev, current_page: 1 }));
    }, [section_id, template_id, caseType]);

    // Refetch data when filters change
    useEffect(() => {
        if (filterBy && filterValue) {
            // Reset to first page when filtering
            setPagination(prev => ({ ...prev, current_page: 1 }));
            fetchFields(1); // Explicitly fetch page 1
        } else if (!filterBy || !filterValue) {
            // Refetch without filter when filter is cleared
            fetchFields();
        }
    }, [filterBy, filterValue]);

    // Reset pagination and refetch when filter is cleared
    useEffect(() => {
        if (!filterBy || !filterValue) {
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }
    }, [filterBy, filterValue]);

    const dependencyMap = {};
    fields.filter(isFieldRenderable).forEach(field => {
        if (field?.display_when || field?.display_multi) {
            let lastFieldId = null;
            
            // Check display_multi first, then fall back to display_when
            const displayWhen = field.display_multi || field.display_when;
            const criteriaStr = field.multi_criteria ? String(field.multi_criteria) : String(displayWhen);
            
            // Try to parse as JSON array (for display_multi or array display_when)
            let parsed = null;
            try {
                parsed = JSON.parse(String(criteriaStr));
            } catch (e) {
                parsed = criteriaStr;
            }
            
            // Check for logical operators in criteria
            const hasAndOperator = criteriaStr.includes('&&');
            const hasOrOperator = criteriaStr.includes('||');
            
            if (Array.isArray(parsed)) {
                // Array format - use the last field ID for grouping
                lastFieldId = parsed[parsed.length - 1];
            } else if (hasAndOperator || hasOrOperator) {
                // For fields with logical operators, group them under the last field ID
                const operator = hasAndOperator ? '&&' : '||';
                const fieldIds = criteriaStr.split(operator).map(id => parseInt(id.trim()));
                lastFieldId = fieldIds[fieldIds.length - 1];
            } else {
                // Handle single field dependency (original logic)
                lastFieldId = parseInt(displayWhen);
            }
            
            if (lastFieldId && !isNaN(lastFieldId)) {
                if (!dependencyMap[lastFieldId]) dependencyMap[lastFieldId] = [];
                dependencyMap[lastFieldId].push(field);
            }
        }
    });

    const rendered = new Set();
    const output = [];
    let currentSubSection = [];
    let currentInstructionGroup = [];

    const flushSubSection = () => {
        if (currentSubSection.length > 0) {
            // Get the first element - it might be a Fragment, so we need to look at its children
            const firstElement = currentSubSection[0];
            const firstField = firstElement?.props?.children?.[0] || firstElement;
            
            if (firstField?.props?.field?.field_id === 13) {
                output.push(
                    <React.Fragment key={`frag-${output.length}`}>
                        {currentSubSection}
                    </React.Fragment>
                );
            } else {
                output.push(
                    <div className="sub-section" key={`sub-${output.length}`}>
                        {currentSubSection}
                    </div>
                );
            }
            currentSubSection = [];
        }
    };

    const flushInstructionGroup = () => {
        if (currentInstructionGroup.length > 0) {
            output.push(
                <div className="instruction-group" key={`instruction-${output.length}`}>
                    {currentInstructionGroup}
                </div>
            );
            currentInstructionGroup = [];
        }
    };

    const startCheckRequest = async (field_id, explicit_group_id = null) => {
        const taskflowField = fields.find(field => Number(field?.id) === Number(field_id));
        if (!isFieldEditable(taskflowField)) {
            setMessage({ text: 'This restricted field is read-only.', type: 'error' });
            return;
        }
        // CRITICAL FIX: If explicit_group_id is provided (from saveFields result), use it
        // This ensures we use the freshly created group_id from custom_fields.php response
        let group_id = explicit_group_id;
        
        // Check if explicit_group_id was actually passed (even if it's 0)
        if (explicit_group_id === null || explicit_group_id === undefined) {
            // Fallback: Get the most recent group_id from groupedData
            // because selectedRow may be stale after a save operation
            if (groupedData && groupedData.length > 0) {
                // If we have a selectedRow, try to find its updated version in groupedData first
                if (selectedRow && selectedRow.group_id) {
                    const updatedRow = groupedData.find(row => row.group_id === selectedRow.group_id);
                    if (updatedRow) {
                        group_id = updatedRow.group_id;
                        console.log('DEBUG startCheckRequest: Found updated selectedRow with group_id:', group_id);
                    }
                }
                
                // If still no group_id, get the most recent one (highest group_id)
                if (!group_id) {
                    const sortedData = [...groupedData].sort((a, b) => b.group_id - a.group_id);
                    group_id = sortedData[0].group_id;
                    console.log('DEBUG startCheckRequest: Using most recent group_id from groupedData:', group_id);
                }
            } else if (selectedRow && selectedRow.group_id) {
                // Fallback to selectedRow if groupedData is empty (shouldn't happen)
                group_id = selectedRow.group_id;
                console.log('DEBUG startCheckRequest: Fallback to selectedRow.group_id:', group_id);
            }
        } else {
            console.log('DEBUG startCheckRequest: Using explicit_group_id passed from saveFields:', group_id);
        }
        
        const case_id = Number(id) || caseData?.id;
        console.log('DEBUG startCheckRequest FINAL: case_id:', case_id, 'group_id:', group_id, 'field_id:', field_id);
        
        const response = await fetch('https://api.casedb.co/invoices.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                group_id,
                case_id,
                field_id: field_id
            }),
            credentials: 'include'
        });

        const data = await response.json();
        if(data.success) {
            setMessage({
                text: 'Accounting check request started successfully.',
                type: 'success'
            });
            
            // Clear any cached form data for this field to ensure fresh value display
            setFormData(prev => {
                const updated = { ...prev };
                delete updated[field_id];
                return updated;
            });
            
            // Force field re-render
            setFieldRefreshKey(prev => prev + 1);
            
            // Refresh the fields to show updated data
            await fetchFields();
            
            // Update the selected row with fresh data if we're currently viewing a row
            if (selectedRow && selectedRow.group_id === group_id) {
                // Find the updated row data in groupedData after fetchFields completes
                setTimeout(() => {
                    setGroupedData(currentGroupedData => {
                        const updatedRow = currentGroupedData.find(row => row.group_id === group_id);
                        if (updatedRow) {
                            setSelectedRow(updatedRow);
                        }
                        return currentGroupedData;
                    });
                }, 100);
            }
        }
    }

    const resetTaskflow = async (field_id) => {
        const taskflowField = fields.find(field => Number(field?.id) === Number(field_id));
        if (!isFieldEditable(taskflowField)) {
            setMessage({ text: 'This restricted field is read-only.', type: 'error' });
            return;
        }
        const group_id = selectedRow ? selectedRow.group_id : null;
        const case_id = Number(id) || caseData?.id;

        // Detect action type from the field's options config
        const fieldDef = fields.find(f => String(f.id) === String(field_id));
        let actionConfig = {};
        try {
            actionConfig = typeof fieldDef?.options === 'string'
                ? JSON.parse(fieldDef.options || '{}')
                : (fieldDef?.options && typeof fieldDef.options === 'object' ? fieldDef.options : {});
        } catch (e) { actionConfig = {}; }
        const actionType = actionConfig.type || 'check_request';

        // For email / calendar: clear value directly, then re-trigger the action
        if (actionType === 'email' || actionType === 'add_to_calendar') {
            try {
                await updateFields({
                    lead_id,
                    case_id: id,
                    ...(group_id != null ? { group_id } : {}),
                    field_values: { [field_id]: null }
                });
                setFormData(prev => { const u = { ...prev }; delete u[field_id]; return u; });
                setFieldRefreshKey(prev => prev + 1);
                await fetchFields();
                // Re-trigger the configured action
                handleTaskflowAction(actionType, field_id, actionConfig);
            } catch (error) {
                console.error('Error resetting taskflow:', error);
                setMessage({ text: 'Error resetting taskflow.', type: 'error' });
            }
            return;
        }

        try {
            // First, save the current form data to ensure the invoice gets the most up-to-date data
            console.log("Saving current field data before reset...");
            await saveFields();
            
            // Call the reset endpoint
            const response = await fetch('https://api.casedb.co/invoices.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'reset',
                    group_id,
                    case_id,
                    field_id: field_id
                }),
                credentials: 'include'
            });

            const data = await response.json();
            if(data.success) {
                setMessage({
                    text: 'Taskflow reset successfully.',
                    type: 'success'
                });
                
                // Clear any cached form data for this field to ensure fresh value display
                setFormData(prev => {
                    const updated = { ...prev };
                    delete updated[field_id];
                    return updated;
                });
                
                // Force field re-render
                setFieldRefreshKey(prev => prev + 1);
                
                // Refresh the fields to show updated data
                await fetchFields();
                
                // Update the selected row with fresh data if we're currently viewing a row
                if (selectedRow && selectedRow.group_id === group_id) {
                    // Find the updated row data in groupedData after fetchFields completes
                    setTimeout(() => {
                        setGroupedData(currentGroupedData => {
                            const updatedRow = currentGroupedData.find(row => row.group_id === group_id);
                            if (updatedRow) {
                                setSelectedRow(updatedRow);
                            }
                            return currentGroupedData;
                        });
                    }, 100);
                }
            } else {
                setMessage({
                    text: data.message || 'Failed to reset taskflow.',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Error resetting taskflow:', error);
            setMessage({
                text: 'Error resetting taskflow.',
                type: 'error'
            });
        }
    }

    // Handles non-check-request taskflow actions (email, add_to_calendar)
    const handleTaskflowAction = async (type, fieldId, config = {}) => {
        const taskflowField = fields.find(field => Number(field?.id) === Number(fieldId));
        if (!isFieldEditable(taskflowField)) {
            setMessage({ text: 'This restricted field is read-only.', type: 'error' });
            return;
        }
        const case_id = Number(id) || caseData?.case?.id;

        // Interpolate template variables in any string from the action config.
        // Supported: {{caseName}}, {{clientName}}, {{uniqueEmail}}, {{caseId}}
        const interpolate = (template, extras = {}) => {
            if (!template) return '';
            const vars = {
                caseName:    caseName || '',
                uniqueEmail: caseData?.unique_email || `${case_id}@casedb.co`,
                caseId:      String(case_id || ''),
                ...extras
            };
            return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
        };

        // Resolve group_id the same way startCheckRequest does
        const resolveGroupId = () => {
            if (selectedRow?.group_id != null) return selectedRow.group_id;
            if (groupedData?.length > 0) {
                const sorted = [...groupedData].sort((a, b) => b.group_id - a.group_id);
                return sorted[0].group_id;
            }
            return null;
        };

        // Persist a value for this taskflow field to custom_fields.php
        const persistTaskflowValue = async (value) => {
            const group_id = resolveGroupId();
            await updateFields({
                lead_id: lead_id,
                case_id: id,
                ...(group_id != null ? { group_id } : {}),
                field_values: { [fieldId]: value }
            });
            // Clear stale cached value and force re-render
            setFormData(prev => { const u = { ...prev }; delete u[fieldId]; return u; });
            setFieldRefreshKey(prev => prev + 1);
            await fetchFields();
        };

        if (type === 'email') {
            try {
                // Fetch primary contact details
                const contactId = caseData?.case?.contact_id;
                let clientEmail = '';
                let clientName  = '';
                if (contactId) {
                    const resp = await fetch(`https://api.casedb.co/contacts.php?id=${contactId}`, { credentials: 'include' });
                    const data = await resp.json();
                    const emails = data.contacts?.emails || [];
                    clientEmail = emails[0]?.email || '';
                    clientName  = data.contacts?.full_name || data.contacts?.company_name || '';
                }

                const uniqueEmail = caseData?.unique_email || `${case_id}@casedb.co`;
                const extras = { clientName, uniqueEmail };

                const subject = interpolate(config.subject || '{{caseName}}', extras);
                const body    = interpolate(config.body || '', extras);

                // Build mailto: — Outlook (and most mail clients) will open a compose
                // window with To, CC, Subject, and Body pre-filled.
                let mailto = `mailto:${encodeURIComponent(clientEmail)}`
                    + `?cc=${encodeURIComponent(uniqueEmail)}`
                    + `&subject=${encodeURIComponent(subject)}`;
                if (body) {
                    mailto += `&body=${encodeURIComponent(body)}`;
                }

                // Open in a new tab so the current page is not navigated away.
                // window.open with mailto: triggers Outlook's compose window.
                window.open(mailto, '_blank');

                // Save a timestamp to mark this taskflow as completed
                const sentAt = new Date().toLocaleString();
                await persistTaskflowValue(`Email sent ${sentAt}`);
            } catch (err) {
                console.error('Error building email action:', err);
                setMessage({ text: 'Failed to prepare email.', type: 'error' });
            }
            return;
        }

        if (type === 'add_to_calendar') {
            const now     = new Date();
            const dtStamp = now.toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';

            const title       = interpolate(config.title       || '{{caseName}}').replace(/[\r\n]/g, ' ');
            const description = interpolate(config.description || '').replace(/\n/g, '\\n');

            const icsLines = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//CaseDB//CaseDB//EN',
                'METHOD:PUBLISH',
                'BEGIN:VEVENT',
                `DTSTAMP:${dtStamp}`,
                `UID:casedb-${case_id}-${fieldId}-${Date.now()}@casedb.co`,
                `SUMMARY:${title}`,
            ];
            if (description) icsLines.push(`DESCRIPTION:${description}`);
            icsLines.push('END:VEVENT', 'END:VCALENDAR');

            const blob   = new Blob([icsLines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
            const url    = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href  = url;
            anchor.download = `${(caseName || 'event').replace(/\s+/g, '_')}.ics`;
            anchor.click();
            URL.revokeObjectURL(url);

            // Save a timestamp to mark this taskflow as completed
            const addedAt = new Date().toLocaleString();
            await persistTaskflowValue(`Added to calendar ${addedAt}`);
            return;
        }
    };

    const getActiveSettlementFinalFieldId = () => {
        if (section_id != 20) return null;
        const field751 = fields.find(f => Number(f.id) === 751);
        const field752 = fields.find(f => Number(f.id) === 752);
        const show752 = field752 && shouldDisplay(field752);
        const show751 = field751 && shouldDisplay(field751);
        if (show752) return 752;
        if (show751) return 751;
        return null;
    };

    const getSettlementRequiredFieldIds = () => {
        const activeFinalFieldId = getActiveSettlementFinalFieldId();
        const requiredIds = activeFinalFieldId ? [...settlementBaseFieldIds, activeFinalFieldId] : [...settlementBaseFieldIds];
        const hasMadeWholeField = fields.some(f => Number(f.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID);
        return settlementFieldsRequired && hasMadeWholeField ? [SETTLEMENT_MADE_WHOLE_FIELD_ID, ...requiredIds] : requiredIds;
    };

    const isSettlementMadeWholeField = (field) => Number(field?.id) === SETTLEMENT_MADE_WHOLE_FIELD_ID;

    const isSettlementRequiredValueFilled = (fieldId, values) => {
        const field = fields.find(candidate => Number(candidate.id) === Number(fieldId));
        const rawValue = values?.[fieldId];
        const value = Number(fieldId) === SETTLEMENT_MADE_WHOLE_FIELD_ID
            ? getSettlementMadeWholeResponseValue(rawValue)
            : rawValue;
        const treatsAsBoolean = Number(field?.field_id) === 12 || Number(fieldId) === SETTLEMENT_MADE_WHOLE_FIELD_ID;
        return value !== null && value !== '' && value !== undefined && !(treatsAsBoolean && String(value) === '2');
    };

    const getMissingSettlementRequiredFieldIds = (values) => getSettlementRequiredFieldIds()
        .filter(fieldId => !isSettlementRequiredValueFilled(fieldId, values));

    const getDisengagementRequiredFieldIds = () => DISENGAGEMENT_REQUIRED_FIELD_IDS;

    const isDisengagementRequiredValueFilled = (fieldId, values) => {
        const rawValue = values?.[fieldId];
        if (rawValue === null || rawValue === undefined) return false;
        if (DISENGAGEMENT_UNKNOWN_IS_MISSING_FIELD_IDS.includes(Number(fieldId)) && String(rawValue).trim() === '2') return false;
        if (Array.isArray(rawValue)) return rawValue.length > 0;
        if (typeof rawValue === 'object') return Object.keys(rawValue).length > 0;
        return String(rawValue).trim() !== '';
    };

    const getMissingDisengagementRequiredFieldIds = (values) => getDisengagementRequiredFieldIds()
        .filter(fieldId => !isDisengagementRequiredValueFilled(fieldId, values));

    const handleSettlementMadeWholeChange = (fieldId, value) => {
        const currentValue = getFieldValue(fields.find(field => Number(field.id) === Number(fieldId)));
        let nextValue = currentValue;

        if (typeof nextValue === 'string' && nextValue.trim().startsWith('{')) {
            try {
                nextValue = JSON.parse(nextValue);
            } catch {
                nextValue = {};
            }
        }

        if (!nextValue || typeof nextValue !== 'object') {
            nextValue = {};
        }

        const responseValue = Number(value);
        const responseLabel = getSettlementMadeWholeLabel(responseValue);
        const localValue = {
            ...nextValue,
            form_activity_status: {
                ...(nextValue.form_activity_status || {}),
                status: 'submitted',
                response: {
                    ...(nextValue.form_activity_status?.response || {}),
                    value: responseValue,
                    label: responseLabel
                }
            }
        };

        setPendingSettlementMadeWholeValue(responseValue);
        pendingSettlementMadeWholeValueRef.current = responseValue;
        setFormData(prev => ({ ...prev, [fieldId]: localValue }));
        setDataChanged(true);
    };

    const scrollToSettlementField = (fieldId, options = {}) => {
        const shouldFocus = options.focus !== false;
        window.setTimeout(() => {
            const target = document.getElementById(`field-${fieldId}`)
                || (Number(fieldId) === SETTLEMENT_MADE_WHOLE_FIELD_ID ? document.getElementById(SETTLEMENT_MADE_WHOLE_SUBHEADER_ID) : null);
            if (!target) return;

            target.scrollIntoView({ behavior: 'smooth', block: 'center' });

            if (!shouldFocus) return;

            const focusTarget = target.querySelector('input:not([type="hidden"]), textarea, select, button');
            focusTarget?.focus?.({ preventScroll: true });
        }, 250);
    };

    useEffect(() => {
        if (!settlementFieldsRequired || Number(section_id) !== 20 || fields.length === 0) {
            lastSettlementRequiredScrollKey.current = null;
            return;
        }

        const currentValues = {
            ...fieldUpdates,
            ...formData,
            ...(newData.field_values || {})
        };
        const missingFieldIds = getMissingSettlementRequiredFieldIds(currentValues);
        const firstMissingFieldId = missingFieldIds[0];

        if (!firstMissingFieldId) return;

        const scrollKey = `${firstMissingFieldId}:${missingFieldIds.join(',')}`;
        if (lastSettlementRequiredScrollKey.current === scrollKey) return;

        lastSettlementRequiredScrollKey.current = scrollKey;
        scrollToSettlementField(firstMissingFieldId);
    }, [settlementFieldsRequired, section_id, fields, fieldUpdates, formData, newData.field_values]);

    const canSyncFields = isParentCase && !!id && !!section_id && fieldSyncRules !== null;

    const canRollbackSectionSync = !isParentCase
        && !!childSyncStatus?.parent_case_id
        && !!childSyncStatus?.rollback_available
        && !fetchingData;

    const canSyncSectionFromParent = !isParentCase
        && !!childSyncStatus?.parent_case_id
        && !!childSyncStatus?.section_is_synced
        && !fetchingData;

    const renderSyncSectionButton = () => {
        if (!canSyncFields) return null;
        if (fields.some(field => isFieldRenderable(field) && !isFieldEditable(field))) return null;
        const synced = isSectionSynced(section_id);
        const hasAddItemFields = fields.some(field => isFieldRenderable(field) && field.add_item == 1);
        const sectionEntry = getSectionSyncEntry(section_id);
        const autoSyncEnabled = sectionEntry && Object.prototype.hasOwnProperty.call(sectionEntry, 'auto_sync')
            ? !!sectionEntry.auto_sync
            : true;
        return (
            <>
                {!synced ? (
                    <button
                        className="action quaternary"
                        disabled={fieldSyncBusy}
                        onClick={() => addFieldGroupSync({ sectionId: Number(section_id), groupId: 0 })}
                        title={'Sync this section to child cases'}
                    >
                        Sync Section
                    </button>
                ) : (
                    <button
                        className="action quaternary"
                        disabled={fieldSyncBusy}
                        onClick={() => resyncSectionGroups(section_id)}
                        title={'Resync all items in this section to child cases'}
                    >
                        Resync Section
                    </button>
                )}
                {synced && hasAddItemFields && (
                    <button
                        className="action tertiary"
                        disabled={fieldSyncBusy}
                        onClick={() => updateSectionAutoSync(section_id, !autoSyncEnabled)}
                        title={autoSyncEnabled ? 'Disable auto-sync for new items in this section' : 'Enable auto-sync for new items in this section'}
                    >
                        {autoSyncEnabled ? 'Auto-sync On' : 'Auto-sync Off'}
                    </button>
                )}
            </>
        );
    };

    const renderRollbackSectionButton = () => {
        if (!canRollbackSectionSync) return null;
        if (fields.some(field => isFieldRenderable(field) && !isFieldEditable(field))) return null;

        return (
            <button
                className="action quaternary"
                disabled={fieldSyncBusy}
                onClick={rollbackSectionSync}
                title={'Rollback synced values for this child section and exclude future parent syncs'}
            >
                {fieldSyncBusy ? 'Rolling Back...' : 'Rollback Sync'}
            </button>
        );
    };

    const renderChildSyncSectionButton = () => {
        if (!canSyncSectionFromParent) return null;
        if (fields.some(field => isFieldRenderable(field) && !isFieldEditable(field))) return null;

        return (
            <button
                className="action quaternary"
                disabled={fieldSyncBusy}
                onClick={syncSectionFromParent}
                title={'Sync just this child case section from the parent case and re-enable future parent syncs if rolled back'}
            >
                {fieldSyncBusy ? 'Syncing...' : 'Sync from Parent'}
            </button>
        );
    };

    const renderSectionSyncAction = () => {
        if (isParentCase) {
            return renderSyncSectionButton();
        }

        return (
            <>
                {renderChildSyncSectionButton()}
                {renderRollbackSectionButton()}
            </>
        );
    };

    const renderSyncGroupButton = (groupId) => {
        if (!canSyncFields || !groupId) return null;
        const group = groupedData.find(row => Number(row?.group_id) === Number(groupId));
        if (customFieldRecordContainsReadonlyValues(group, fields, hiddenFieldCapability, id)) return null;
        const synced = isGroupSynced(groupId, section_id);
        return (
            <button
                className="action quaternary"
                disabled={fieldSyncBusy || synced}
                onClick={() => addFieldGroupSync({ sectionId: Number(section_id), groupId: Number(groupId) })}
                title={synced ? 'Group synced' : 'Sync this group to child cases'}
            >
                {synced ? 'Synced' : 'Sync Group'}
            </button>
        );
    };

    const renderFieldWithChildren = (field, index, conditional = false) => {
        // Check if this is a settlement required field (200 or 203) in section 20
        // Only apply required styling when actively attempting to set phase 19 AND the field is empty
        const renderMadeWholeAsSettlementBoolean = settlementFieldsRequired && section_id == 20 && isSettlementMadeWholeField(field);
        const rawFieldValue = getFieldValue(field);
        const fieldValue = renderMadeWholeAsSettlementBoolean
            ? getSettlementMadeWholeResponseValue(rawFieldValue)
            : rawFieldValue;
        const isBooleanField = field.field_id === 12 || renderMadeWholeAsSettlementBoolean;
        const isEmpty =
            fieldValue === undefined ||
            fieldValue === null ||
            fieldValue === '' ||
            (isBooleanField && String(fieldValue) === '2');
        const settlementRequiredIds = getSettlementRequiredFieldIds();
        const isSettlementRequired = settlementFieldsRequired && section_id == 20 && settlementRequiredIds.includes(field.id) && isEmpty;
        const disengagementRequiredIds = getDisengagementRequiredFieldIds();
        const disengagementCurrentValues = {
            ...fieldUpdates,
            ...formData,
            ...(newData.field_values || {})
        };
        const isDisengagementRequired = disengagementFieldsRequired && section_id == 20 && disengagementRequiredIds.includes(field.id) && !isDisengagementRequiredValueFilled(field.id, disengagementCurrentValues);

        const recommendationItem = recommendations.byField[String(field.id)];
        const suggestedValue = [8, 10].includes(Number(field.field_id)) && addItemMode
            && isFieldEditable(field) && isRecommendationTargetBlank(rawFieldValue)
            && recommendationItem?.recommendation && !recommendationItem.conflict
            && Number(recommendationItem.recommendation.target_field_type) === Number(field.field_id)
            ? recommendationItem.recommendation : null;
        // Display the suggestion in the ordinary control without putting it in
        // formData/newData. Only the check action accepts it; a manual selection
        // dismisses the suggestion and follows the existing field-edit path.
        const fieldControl = (
            <Field
                key={`${field.id}-${index}-${conditional ? 'child' : 'parent'}-${fieldRefreshKey}`}
                field={field}
                value={suggestedValue ? suggestedValue.value : rawFieldValue}
                handleFieldChange={recommendationItem ? (fieldId, value) => {
                    recommendations.dismiss(field.id);
                    handleFieldChange(fieldId, value);
                } : handleFieldChange}
                conditional={conditional}
                formData={formData}
                required={isSettlementRequired || isDisengagementRequired}
                newData={newData}
                fieldUpdates={fieldUpdates}
                fields={fields}
                lead_id={lead_id}
                case_id={id}
                caseName={caseName}
                user_id={user_id}
                refreshAfterCalc={refreshAfterCalc}
                sectionName={sectionName}
                section_id={section_id}
                setNewContactFieldId={setNewContactFieldId}
                setCreateContact={setCreateContact}
                startCheckRequest={startCheckRequest}
                handleEditContact={handleEditContact}
                saveFields={saveFields}
                resetTaskflow={resetTaskflow}
                selectedRow={selectedRow}
                groupedData={groupedData}
                addItemMode={addItemMode}
                handleTaskflowAction={handleTaskflowAction}
                prepareDocGenGeneration={prepareDocGenGeneration}
                prepareDocGenReview={prepareDocGenReview}
                handleDocGenGenerated={handleDocGenGenerated}
                settlementBooleanOverride={renderMadeWholeAsSettlementBoolean}
                handleSettlementMadeWholeChange={renderMadeWholeAsSettlementBoolean ? handleSettlementMadeWholeChange : null}
                domId={`field-${field.id}`}
                recommendation={recommendationItem ? (
                    <FieldRecommendation
                        item={recommendationItem}
                        fieldName={field.name}
                        onAccept={() => recommendations.accept(field.id)}
                        onDismiss={() => recommendations.dismiss(field.id)}
                        onRetry={recommendations.retry}
                    />
                ) : null}
            />
        );
        const fieldEl = Number(field?.hidden) === 1
            && !canEditHiddenFieldsForCase(hiddenFieldCapability, id)
            ? (
                <fieldset
                    className="restricted-custom-field-readonly"
                    disabled
                    aria-label={`${field.name || 'Restricted field'} (read only)`}
                    style={{ border: 0, padding: 0, margin: 0, minWidth: 0, pointerEvents: 'none' }}
                >
                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                        Restricted · Read only
                    </span>
                    {fieldControl}
                </fieldset>
            )
            : fieldControl;

        rendered.add(field.id);

        const deadlineCalculationChildren = Number(field.field_id) === 20
            ? fields
                .filter(child => isFieldRenderable(child) && !rendered.has(child.id) && shouldDisplay(child) && isDeadlineBasedCalculationField(child, fields, field.id))
                .sort((a, b) => a.order_id - b.order_id)
            : [];

        const childEls = [
            ...deadlineCalculationChildren,
            ...(dependencyMap[field.id] || [])
        ]
            .filter((child, childIndex, children) => child && children.findIndex(candidate => candidate.id === child.id) === childIndex)
            .filter(child => isFieldRenderable(child) && !rendered.has(child.id) && shouldDisplay(child))
            .sort((a, b) => a.order_id - b.order_id)
            .flatMap((child, childIndex) => renderFieldWithChildren(child, `${index}-child-${childIndex}`, true));

        return (
            <React.Fragment key={`field-wrapper-${field.id}`}>
                {fieldEl}
                {childEls}
            </React.Fragment>
        );
    };

    fields
        .filter(isFieldRenderable)
        .forEach((field, index) => {
            if (!field || rendered.has(field.id)) return;
            if (!shouldDisplay(field)) return;
            if (isDeadlineBasedCalculationField(field, fields)) {
                const sourceFieldId = getCalculationSourceFieldId(field);
                const sourceField = fields.find(candidate => isFieldRenderable(candidate) && Number(candidate.id) === Number(sourceFieldId));
                if (sourceField && !rendered.has(sourceField.id) && shouldDisplay(sourceField)) return;
            }

            const element = renderFieldWithChildren(field, index, false);

            if (field.field_id === 13) {
                flushInstructionGroup();
                flushSubSection();
                currentSubSection.push(element);
                flushSubSection();
            } else if (field.field_id === 14) {
                flushSubSection();
                currentInstructionGroup.push(element);
            } else {
                flushInstructionGroup();
                currentSubSection.push(element);
            }
        });

    flushSubSection();
    flushInstructionGroup();

    const sectionFields = fields.filter(f => isFieldRenderable(f) && f.field_id === 13) ?? [];
    const addItemFields = [];
    const seenFieldIds = new Set();
    
    fields.forEach(f => {
        if (isFieldRenderable(f) && f.add_item === 1 && !seenFieldIds.has(f.id)) {
            addItemFields.push(f);
            seenFieldIds.add(f.id);
        }
    });

    // Sort addItemFields by order_id to maintain consistent ordering
    addItemFields.sort((a, b) => a.order_id - b.order_id);
    const editableAddItemFields = addItemFields.filter(isFieldEditable);
    const selectedRowContainsReadonlyValues = customFieldRecordContainsReadonlyValues(
        selectedRow,
        fields,
        hiddenFieldCapability,
        id
    );

    // Fetch contact names for filtering
    useEffect(() => {
        if (!groupedData || !addItemFields || groupedData.length === 0) return;
        
        const contactFields = addItemFields.filter(f => f.field_id === 8);
        if (contactFields.length === 0) return;
        
        const contactIds = new Set();
        contactFields.forEach(field => {
            groupedData.forEach(row => {
                if (row[field.id] && !contactMap[row[field.id]]) { // Only fetch if not already in contactMap
                    contactIds.add(row[field.id]);
                }
            });
        });

        if (contactIds.size > 0) {
            // Add a flag to prevent multiple simultaneous requests
            const controller = new AbortController();
            
            fetch(`https://api.casedb.co/contacts.php?ids=${Array.from(contactIds).join(',')}`, {
                signal: controller.signal,
                credentials: 'include'
            })
                .then(res => res.json())
                .then(result => {
                    const requestedIds = Array.from(contactIds);
                    setContactMap(prev => {
                        const map = { ...prev };
                        const foundIds = new Set();

                        if (result.success && Array.isArray(result.contacts)) {
                            result.contacts.forEach(contact => {
                                map[contact.id] = Number(contact.is_company) === 1
                                    ? contact.company_name ?? contact.full_name ?? `Contact ${contact.id}`
                                    : contact.full_name ?? `Contact ${contact.id}`;
                                foundIds.add(String(contact.id));
                            });
                        }

                        // For any requested ids not returned, set a placeholder to prevent refetch loops
                        requestedIds.forEach(id => {
                            const idKey = String(id);
                            if (!foundIds.has(idKey) && !map[idKey]) {
                                map[idKey] = 'Contact does not exist';
                            }
                        });

                        return map;
                    });
                })
                .catch(err => {
                    if (err.name !== 'AbortError') {
                        console.log('Error fetching contacts for filtering:', err);
                    }
                });

            return () => controller.abort(); // Cleanup function to abort request if component unmounts
        }
    }, [groupedData, addItemFields]);

    // Filter the grouped data based on selected field and filter value
    // Since filtering is now handled server-side, just return the data as-is
    const filteredData = useMemo(() => {        
        // Backend filtering is now handling this, so just return the grouped data
        return groupedData;
    }, [groupedData]);

    const openGroupForEdit = useCallback((row) => {
        if (!row) return;

        const visibleRow = filterCustomFieldRecordForCase(
            row,
            fields,
            hiddenFieldCapability,
            id
        );
        const editableRow = filterCustomFieldRecordForCase(
            visibleRow,
            fields,
            hiddenFieldCapability,
            id,
            { editableOnly: true }
        );
        const editableFieldValues = filterCustomFieldValuesForCase(
            visibleRow,
            fields,
            hiddenFieldCapability,
            id,
            { editableOnly: true }
        );

        setSelectedRow(visibleRow);
        setFormData(editableRow);
        setAddItemMode(true);
        setNewData({
            lead_id,
            case_id: id,
            group_id: visibleRow.group_id,
            field_values: editableFieldValues
        });
    }, [fields, hiddenFieldCapability, id, lead_id]);

    const lastGenerationTargetToken = useRef(null);
    useEffect(() => {
        const target = generationEditTarget;
        if (!target?.token || Number(target.sectionId) !== Number(section_id) || fields.length === 0) return;
        if (lastGenerationTargetToken.current === target.token) return;
        lastGenerationTargetToken.current = target.token;

        let cancelled = false;
        const openTarget = async () => {
            const field = fields.find(candidate => isFieldEditable(candidate) && Number(candidate.id) === Number(target.fieldId));
            if (!field) {
                setMessage({ type: 'error', text: 'The source field is not available in this case section.' });
                onGenerationEditTargetConsumed?.();
                return;
            }

            if (Number(field.add_item) === 1) {
                if (target.groupId !== null && target.groupId !== undefined) {
                    let row = groupedData.find(item => Number(item.group_id) === Number(target.groupId));
                    if (!row) {
                        try {
                            const query = new URLSearchParams({
                                section_id: String(section_id),
                                case_type_id: String(caseType || 0),
                                lead_id: String(lead_id || 0),
                                case_id: String(id ?? ''),
                                group_id: String(target.groupId),
                                page: '1',
                                limit: '1',
                                t: String(Date.now())
                            });
                            const response = await fetch(`https://api.casedb.co/custom_fields.php?${query.toString()}`, {
                                credentials: 'include'
                            });
                            const data = await response.json();
                            if (!response.ok || data?.success === false) {
                                throw new Error(data?.message || 'The source item could not be loaded');
                            }
                            const responseCapability = readCaseBoundHiddenFieldCapability(data, id);
                            if (!responseCapability) {
                                throw new Error('The source item permission could not be verified');
                            }
                            const visibleUpdates = filterCustomFieldUpdatesForCase(
                                data?.field_updates,
                                fields,
                                responseCapability,
                                id
                            );
                            row = visibleUpdates.find(item => Number(item.group_id) === Number(target.groupId)) || null;
                        } catch (error) {
                            row = null;
                        }
                    }

                    if (cancelled) return;
                    if (!row) {
                        setMessage({ type: 'error', text: 'The source item could not be opened. It may have been removed.' });
                        onGenerationEditTargetConsumed?.();
                        return;
                    }
                    openGroupForEdit(row);
                } else {
                    // A mapped add-item field can be intentionally blank because
                    // no group exists yet. Open a new item instead of treating the
                    // missing group id as a read-only destination.
                    setForcedSectionDataMode(false);
                    setSelectedRow(null);
                    setFormData({});
                    setNewData({
                        lead_id,
                        case_id: id,
                        field_values: {}
                    });
                    setAddItemMode(true);
                }
            }

            window.setTimeout(() => {
                if (cancelled) return;
                const element = document.getElementById(`field-${target.fieldId}`);
                if (!element) {
                    setMessage({ type: 'error', text: 'The source field could not be opened in this section.' });
                    onGenerationEditTargetConsumed?.();
                    return;
                }
                element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const focusable = element?.querySelector?.('input, textarea, select, button');
                focusable?.focus?.({ preventScroll: true });
                onGenerationEditTargetConsumed?.();
            }, Number(field.add_item) === 1 ? 150 : 50);
        };

        openTarget();
        return () => {
            cancelled = true;
        };
    }, [caseType, fields, generationEditTarget, groupedData, id, isFieldEditable, lead_id, onGenerationEditTargetConsumed, openGroupForEdit, section_id]);

    const uploadFiles = async (group_id = null) => {
        const uploadBatchScope = currentFieldRequestScope;
        if (uploadBatchScope !== fieldRequestScopeRef.current) return;
        const folderPath = await resolveUploadFolderPath();
        if (uploadBatchScope !== fieldRequestScopeRef.current) return;
        const uploads = [];

        const queueUpload = (file, fieldId) => {
            uploads.push(
                uploadSingleFile(file, fieldId, {
                    groupIdOverride: group_id,
                    folderPathOverride: folderPath
                }).then((result) => ({ fieldId, ...result }))
            );
        };

        for (const field of fields.filter(isFieldEditable)) {
            const value = formData[field.id];

            if (value?.constructor?.name === "File") {
                queueUpload(value, field.id);
            }

            if (Array.isArray(value) && value[0] instanceof File) {
                value.forEach((file) => queueUpload(file, field.id));
            }
        }

        if (uploads.length === 0) {
            console.log("uploadFiles: no pending uploads to process");
            return;
        }

        try {
            const results = await Promise.all(uploads);
            if (uploadBatchScope !== fieldRequestScopeRef.current) return;

            results.forEach(({ fieldId, fileId, fileMeta }) => {
                if (!fileId) return;

                setFormData((prev) => ({
                    ...prev,
                    [fieldId]: fileMeta || fileId
                }));

                setNewData((prev) => ({
                    ...prev,
                    lead_id,
                    field_values: {
                        ...(prev.field_values || {}),
                        [fieldId]: fileId
                    }
                }));
            });

            await new Promise((resolve) => setTimeout(resolve, 300));
            fetchDocuments?.();
        } catch (error) {
            console.error("Error during file uploads:", error);
        }
    };

    const updateLeadFields = async () => {
        const editableFieldValues = filterCustomFieldValuesForCase(
            newData.field_values,
            fields,
            hiddenFieldCapability,
            id,
            { editableOnly: true }
        );
        const response = await fetch(`https://api.casedb.co/lead.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                lead_id,
                field_values: editableFieldValues,
            }),
        })
    }

    console.log('SECTION:', folders);

    useEffect(() => {
        setAddItemMode(selectedRow);
    }, [selectedRow]);

    useEffect(() => {
        if (deleteGroup) {
            if (window.confirm("Are you sure you want to delete this group?")) {
                handleDeleteGroup();
            }
            setDeleteGroup(null); // Reset deleteGroup after handling
        }
    }, [deleteGroup]);

    const staticSections = ['Documents', 'Intake Details', 'Activity Feed', 'Negotiation', 'Time & Billing', 'Team', 'Related Cases', 'Contacts', 'Calendar', 'Vitals', 'Weather Events'];
    if (staticSections.includes(sectionName)) {
        return (
            <div className={`case-section full horizontal`}>
                {sectionName === 'Documents' ? (
                    <Documents 
                        key={id}
                        ref={documentsRef}
                        fetchDocuments={fetchDocuments} 
                        fetchFolderContents={fetchFolderContents} 
                        folders={folders} 
                        metaData={metaData} 
                        caseName={caseName} 
                        case_id={id} 
                        user_id={user_id} 
                        caseType={caseType}
                        onFileUpload={onFileUpload}
                        showMessage={setMessage}
                        documentLiveUnreadRegistry={documentLiveUnreadRegistry}
                    />
                ) : sectionName === "Activity Feed" ? (
                    <ActivityFeed
                        key={id}
                        lead_id={lead_id}
                        case_id={id}
                        user_id={user_id}
                        childCaseCount={Number(caseData?.case?.child_case_count ?? caseData?.child_case_count ?? 0)}
                        activityKey={activityKey}
                        sections={sections}
                        settlementActivityDraft={settlementActivityDraft}
                        disengagementActivityDraft={disengagementActivityDraft}
                        guidedActivityDraft={guidedActivityDraft}
                        onSettlementDraftConsumed={onSettlementDraftConsumed}
                        onDisengagementDraftConsumed={onDisengagementDraftConsumed}
                        onGuidedDraftConsumed={onGuidedDraftConsumed}
                        onSettlementDraftSubmitted={handleSettlementDraftSubmitted}
                        onDisengagementDraftSubmitted={onDisengagementDraftSubmitted}
                        onSettlementDraftCancelled={onSettlementDraftCancelled}
                        onDisengagementDraftCancelled={onDisengagementDraftCancelled}
                    />
                ) : sectionName === "eSignatures" ? (
                    <ESignatureManager key={id} case_id={id} lead_id={lead_id} user_id={user_id} />
                ) : sectionName === "Team" ? (
                    <Team key={id} case_id={id} onTeamUpdated={fetchVitals} />
                ) : sectionName === "Vitals" ? (
                    <Vitals
                        key={id}
                        caseId={id}
                        leadId={lead_id}
                        caseData={caseData}
                        caseTypes={caseTypes}
                        fetchCase={fetchCase}
                        fetchVitals={fetchVitals}
                        user_id={user_id}
                    />
                ) : sectionName === "Intake Details" ? (
                    <IntakeDetails caseData={caseData} customFields={fields} fieldUpdates={fieldUpdates} fetchFields={refetchFields} fetchCase={fetchCase} caseTypes={caseTypes} user_id={user_id} />
                ) : sectionName === "Related Cases" ? (
                    <RelatedCases case_id={id} lead_id={lead_id} />
                ) : sectionName === "Contacts" ? (
                    <CaseContacts lead_id={lead_id} />
                ) : sectionName === "Time & Billing" ? (
                    <TimeBilling case_id={id} caseData={caseData} guidedBillingDraft={guidedBillingDraft} onGuidedBillingDraftConsumed={onGuidedBillingDraftConsumed} />
                ) : sectionName === "Calendar" ? (
                    <DeadlineCreation case_id={id} />
                ) : sectionName === "Negotiation" ? (
                    <Negotiation 
                        case_id={id} 
                        section_id={section_id} 
                        lead_id={lead_id}
                        fields={fields}
                        fieldUpdates={fieldUpdates}
                        groupedFieldUpdates={groupedData}
                        onRefresh={fetchFields}
                    />
                ) : sectionName === "Weather Events" && (
                    <WeatherEvents caseData={caseData} />
                )}
                {(() => {
                    console.log('Alert render check (Documents section) - message exists:', !!message);
                    console.log('Alert render check (Documents section) - message:', message);
                    if (message) {
                        console.log('Alert render check (Documents section) - rendering Alert component');
                        return <Alert type={message.type} message={message.text} duration={message.duration} onClose={() => setMessage(null)} />;
                    } else {
                        console.log('Alert render check (Documents section) - no message, not rendering Alert');
                        return null;
                    }
                })()}
            </div>
        );
    };

    const handleDeleteGroup = async () => {
        if (customFieldRecordContainsReadonlyValues(selectedRow, fields, hiddenFieldCapability, id)) {
            setMessage({ text: 'This group contains restricted read-only fields and cannot be deleted.', type: 'error' });
            setDeleteGroup(null);
            return;
        }
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(`https://api.casedb.co/custom_fields.php`, {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                body: JSON.stringify({
                    lead_id,
                    case_id: id, // Add case_id for invoice deletion
                    group_id: selectedRow.group_id,
                    section_id,
                    delete_group: 1,
                }),
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                fetchFields();
                setAddItemMode(false);
                setNewData({});
                setFormData({});
                setSelectedRow(null);
                setDeleteGroup(null); // Reset delete state
            } else {
                console.error("Failed to delete group:", data.message);
            }
        } catch (error) {
            console.error("Error deleting group:", error);
        }
    }

    // Handler for settlement phase verification confirmation
    async function handleSettlementPhaseConfirm() {
        if (!pendingSettlementPhase || !id) {
            console.error("Missing pending phase or case ID");
            return;
        }

        try {
            setSettlementFieldsSaved(false);
            setShowSettlementVerificationNotice(false);
            onSettlementPhaseConfirmed?.();
        } catch (error) {
            console.error('Error opening settlement activity draft:', error);
            setMessage({ text: 'Error preparing settlement note', type: 'error' });
        }
    }

    async function handleSettlementDraftSubmitted() {
        try {
            setSettlementFieldsSaved(false);
            setShowSettlementVerificationNotice(false);
            window.dispatchEvent(new CustomEvent('clearSettlementState'));
            fetchCase(true);
            onPhasesSynced?.();
            onSettlementDraftSubmitted?.();
        } catch (error) {
            console.error('Error finalizing settlement phase:', error);
            setMessage({ text: 'Settlement note saved, but refresh failed', type: 'error' });
        }
    }

    return (
        <div className='case-section'>
            {!addItemMode && sectionFields.some(field => field.add_item !== 1 && (field.case_type_id === Number(caseType) || Number(field.case_type_id) === 0)) ? (
                <>
                    <TableOfContents
                        key={section_id}
                        subheaders={sectionFields.filter(field => field.add_item !== 1 && (field.case_type_id === Number(caseType) || field.case_type_id === 0))}
                        dataChanged={dataChanged}
                        cancel={false}
                        saveFields={saveFields}
                        onCancel={() => {
                            setAddItemMode(false);
                            setSelectedRow(null);
                            setFormData({});
                            setNewData({});
                        }}
                        sectionName={sectionName}
                        headerActions={renderSectionSyncAction()}
                    />
                </>
            ) : (
                !addItemMode && (
                    <div className='toc-header'>
                        <h3>{sectionName}</h3>
                        {addItemFields.length > 0 ? (
                            <>
                                <div className="filter-controls" style={{ 
                                    display: 'flex', 
                                    gap: '10px',
                                    alignItems: 'center'
                                }}>
                                    <label style={{ fontSize: '14px', color: 'var(--text-color)', fontWeight: '500' }}>
                                        Filter By:
                                    </label>
                                    <select 
                                        className="default-select"
                                        value={filterBy}
                                        onChange={(e) => {
                                            console.log('Filter By changed:', e.target.value);
                                            setFilterBy(e.target.value);
                                            setFilterValue('');
                                        }}
                                        style={{ minWidth: '150px' }}
                                    >
                                        <option value="">All Columns</option>
                                        {addItemFields
                                            .filter(f => f.field_id !== 14 && f.field_id !== 13 && !f.name.includes("Add"))
                                            .map(field => (
                                                <option key={field.id} value={field.id}>
                                                    {field.name}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    
                                    <input
                                        type="text"
                                        placeholder={filterBy ? "Enter filter value..." : "Select a column to filter"}
                                        value={filterValue}
                                        onChange={(e) => {
                                            console.log('Filter Value changed:', e.target.value);
                                            setFilterValue(e.target.value);
                                        }}
                                        disabled={!filterBy}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            backgroundColor: 'var(--secondary-color)',
                                            color: 'var(--text-color)',
                                            minWidth: '200px'
                                        }}
                                    />
                                    
                                    {(filterBy || filterValue) && (
                                        <button
                                            onClick={() => {
                                                setFilterBy('');
                                                setFilterValue('');
                                            }}
                                            className='action'
                                            style={{
                                                backgroundColor: 'var(--secondary-color)',
                                                color: 'var(--text-color)',
                                                border: '1px solid var(--border-color)',
                                            }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                    
                                    <span style={{ 
                                        fontSize: '12px', 
                                        color: 'var(--subtext-color)',
                                        marginLeft: 'auto'
                                    }}>
                                        {pagination.total > 0 ? (
                                            <>
                                                {filteredData.length} of {groupedData.length} shown
                                                {pagination.total > groupedData.length && (
                                                    <> ({pagination.total} total)</>
                                                )}
                                            </>
                                        ) : (
                                            `${filteredData.length} of ${groupedData.length} items`
                                        )}
                                    </span>
                                </div>
                                <div className='toc-header-actions'>
                                    {renderSectionSyncAction()}
                                    <button
                                        className="action tertiary"
                                        onClick={exportSectionData}
                                        title="Export all data to Excel file"
                                    >
                                        Export Excel
                                    </button>
                                    {!selectedRow && editableAddItemFields.length > 0 && (
                                        <button
                                            className="action alt"
                                            onClick={() => {
                                                setAddItemMode(true);
                                                setFormData({});
                                                setNewData({});
                                            }}
                                        >
                                            Add Item
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            sectionName && (
                                <div className='toc-header-actions' style={{ display: 'flex', gap: '8px' }}>
                                    {renderSectionSyncAction()}
                                    {fields.some(field => isFieldEditable(field) && field.add_item !== 1) && (
                                        <button className="action" onClick={() => saveFields()}>
                                            Save
                                        </button>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                )
            )}
            {addItemFields.length > 0 && (
                <>
                    {!addItemMode ? (
                        <>
                            {/* Display totals by field from server */}
                            {Object.keys(totalsByField).length > 0 && (
                                <div className='data-table-totals'>
                                    {Object.entries(totalsByField).map(([fieldName, total], index) => (
                                        <div key={fieldName} className='data-table-total small-text' style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '2px 0'
                                        }}>
                                            <span style={{ color: 'var(--subtext-color)' }}>{fieldName}:</span>
                                            <span style={{ fontWeight: '500', color: 'var(--text-color)' }}>
                                                {formatValue(total, "currency")}
                                            </span>
                                        </div>
                                    ))}
                                    {/* Show overall total if there are multiple fields */}
                                    {Object.keys(totalsByField).length > 1 && globalTotal > 0 && (
                                        <div className='data-table-total small-text'>
                                            <span style={{ color: 'var(--text-color)' }}>
                                                {calculationMethod === 'difference' ? 'Difference' : 'Total'}:
                                            </span>
                                            <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>
                                                {formatValue(calculationMethod === 'difference' ? 
                                                    (differenceTotal !== null ? differenceTotal : globalTotal) : 
                                                    globalTotal, "currency")}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Pagination controls - Top */}
                            {pagination.total > 100 && (
                                <div className="pagination-controls top" style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    padding: '10px 0',
                                    borderBottom: '1px solid var(--border-color)',
                                    width: '100%',
                                    marginBottom: '0'
                                }}>
                                    <div className="pagination-info" style={{ fontSize: '12px', color: 'var(--subtext-color)' }}>
                                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
                                    </div>
                                    <div className="pagination-per-page">
                                        <select 
                                            value={pagination.per_page} 
                                            onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--background-color)',
                                                color: 'var(--text-color)',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {perPageOptions.map(option => (
                                                <option key={option} style={{ color: "var(--text-color)" }} value={option}>{option} per page</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="pagination-nav" style={{ display: 'flex', gap: '4px' }}>
                                        <button 
                                            onClick={() => handlePageChange(1)} 
                                            disabled={pagination.current_page === 1 || fetchingData}
                                        >
                                            First
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(pagination.current_page - 1)} 
                                            disabled={pagination.current_page === 1 || fetchingData}
                                        >
                                            Previous
                                        </button>
                                        <span style={{ 
                                            padding: '4px 8px', 
                                            fontSize: '11px',
                                            color: 'var(--text-color)',
                                            alignSelf: 'center'
                                        }}>
                                            Page {pagination.current_page} of {pagination.total_pages}
                                        </span>
                                        <button 
                                            onClick={() => handlePageChange(pagination.current_page + 1)} 
                                            disabled={pagination.current_page === pagination.total_pages || fetchingData}
                                        >
                                            Next
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(pagination.total_pages)} 
                                            disabled={pagination.current_page === pagination.total_pages || fetchingData}
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            )}

                            {fetchingData && (
                                <div>
                                    Loading...
                                </div>
                            )}
                            {contextMenu.visible && (
                                <div
                                    style={{
                                        top: contextMenu.y,
                                        left: contextMenu.x
                                    }}
                                    className='copy-button'
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div
                                        style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--fill)' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            copyGroupToTrust();
                                        }}
                                    >
                                        Copy to Trust Accounting
                                    </div>
                                </div>
                            )}
                            <DataTable
                                fields={addItemFields}
                                data={filteredData}
                                paginated={true}
                                sortConfig={sortConfig}
                                onSort={handleSort}
                                onRowContextMenu={handleRowContextMenu}
                                onRowClick={openGroupForEdit}
                            />
                            
                            {/* Pagination controls - Bottom */}
                            {pagination.total > 100 && (
                                <div className="pagination-controls bottom" style={{
                                    width: '100%',
                                    paddingTop: '10px',
                                }}>
                                    <div className="pagination-info" style={{ fontSize: '12px', color: 'var(--subtext-color)' }}>
                                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} results
                                    </div>
                                    <div className="pagination-nav" style={{ display: 'flex', gap: '4px' }}>
                                        <button 
                                            onClick={() => handlePageChange(1)} 
                                            disabled={pagination.current_page === 1 || fetchingData}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--background-color)',
                                                color: 'var(--text-color)',
                                                cursor: pagination.current_page === 1 || fetchingData ? 'not-allowed' : 'pointer',
                                                opacity: pagination.current_page === 1 || fetchingData ? 0.5 : 1,
                                                fontSize: '11px'
                                            }}
                                        >
                                            First
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(pagination.current_page - 1)} 
                                            disabled={pagination.current_page === 1 || fetchingData}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--background-color)',
                                                color: 'var(--text-color)',
                                                cursor: pagination.current_page === 1 || fetchingData ? 'not-allowed' : 'pointer',
                                                opacity: pagination.current_page === 1 || fetchingData ? 0.5 : 1,
                                                fontSize: '11px'
                                            }}
                                        >
                                            Previous
                                        </button>
                                        <span style={{ 
                                            padding: '4px 8px', 
                                            fontSize: '11px',
                                            color: 'var(--text-color)',
                                            alignSelf: 'center'
                                        }}>
                                            Page {pagination.current_page} of {pagination.total_pages}
                                        </span>
                                        <button 
                                            onClick={() => handlePageChange(pagination.current_page + 1)} 
                                            disabled={pagination.current_page === pagination.total_pages || fetchingData}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--background-color)',
                                                color: 'var(--text-color)',
                                                cursor: pagination.current_page === pagination.total_pages || fetchingData ? 'not-allowed' : 'pointer',
                                                opacity: pagination.current_page === pagination.total_pages || fetchingData ? 0.5 : 1,
                                                fontSize: '11px'
                                            }}
                                        >
                                            Next
                                        </button>
                                        <button 
                                            onClick={() => handlePageChange(pagination.total_pages)} 
                                            disabled={pagination.current_page === pagination.total_pages || fetchingData}
                                            style={{
                                                padding: '4px 8px',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--background-color)',
                                                color: 'var(--text-color)',
                                                cursor: pagination.current_page === pagination.total_pages || fetchingData ? 'not-allowed' : 'pointer',
                                                opacity: pagination.current_page === pagination.total_pages || fetchingData ? 0.5 : 1,
                                                fontSize: '11px'
                                            }}
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {!forcedSectionDataMode && !sectionFields.some(field => (field.add_item !== 1) && (field.case_type_id === Number(caseType) || field.case_type_id === 0)) && (
                                <div className='toc-header' style={{ justifyContent: formData?.["_oldest_created_at"] || formData?.["_earliest_user"] ? 'space-between' : 'flex-end', padding: '10px 0' }}>
                                    {formData?._oldest_created_at && <span className='subtext row-creation'>Created {formData._oldest_created_at !== '0000-00-00 00:00:00' ? `at ${new Date(formData._oldest_created_at + ' UTC').toLocaleString('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}` : ''} {formData?._earliest_user ? `by ${formData._earliest_user}` : ''}</span>}
                                    <div className='toc-header-actions'>
                                        {selectedRow && renderSyncGroupButton(selectedRow.group_id)}
                                        {selectedRow && !selectedRowContainsReadonlyValues && (
                                            <button
                                                className="action destroy"
                                                onClick={() => {
                                                    setDeleteGroup(true);
                                                }}
                                            >
                                                Delete
                                            </button>
                                        )}
                                        <button
                                            className="action alt"
                                            onClick={() => {
                                                setAddItemMode(false);
                                                setSelectedRow(null);
                                                setFormData({});
                                                setNewData({});
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        {(selectedRow && !selectedRowContainsReadonlyValues && [19, 27].includes(section_id)) && (
                                            <button className='action tertiary' onClick={copyGroupToTrust}>
                                                Copy to Trust Accounting
                                            </button>
                                        )}
                                        {editableAddItemFields.length > 0 && (
                                            <button className="action" disabled={recommendations.accepting} onClick={() => saveFields()}>
                                                {selectedRow ? "Save" : "Create"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </>
            )}
            {addItemMode && forcedSectionDataMode && (
                <div className='toc-header' style={{ justifyContent: 'flex-end', marginTop: '15px' }}>
                    <div className='toc-header-actions'>
                        <button
                            className="action alt"
                            onClick={() => {
                                setForcedSectionDataMode(false);
                                setAddItemMode(false);
                                setSelectedRow(null);
                                setFormData({});
                                setNewData({});
                            }}
                        >
                            Cancel
                        </button>
                        <button className="action" disabled={recommendations.accepting} onClick={() => saveFields()}>
                            Save
                        </button>
                    </div>
                </div>
            )}
            {addItemMode && recommendations.error && (
                <div className="field-recommendation-status" role="status">
                    <span>{recommendations.error}</span>
                    <button type="button" className="action quaternary" onClick={recommendations.retry}>
                        Retry recommendations
                    </button>
                </div>
            )}
            {(sectionName !== "Documents" && sectionName !== "Activity Feed") && output}
            {section_id === 20 && (
                <NestedFeed case_id={id} section_id={section_id} />
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated} 
                />
            )}
            {editContact && contactToEdit && (
                <EditDetail
                    setEditContact={setEditContact}
                    contactData={contactToEdit}
                    fetchContacts={fetchContacts}
                    setError={(error) => setMessage(error)}
                    setCreateContact={setCreateContact}
                />
            )}
            {(() => {
                console.log('Alert render check - message exists:', !!message);
                console.log('Alert render check - message:', message);
                console.log('Alert render check - typeof message:', typeof message);
                if (message) {
                    console.log('Alert render check - rendering Alert component');
                    return <Alert type={message.type} message={message.text} duration={message.duration} onClose={() => setMessage(null)} />;
                } else {
                    console.log('Alert render check - no message, not rendering Alert');
                    return null;
                }
            })()}
            {section_id === 24 && pendingSettlementPhase && (
                <>
                    {showSettlementVerificationNotice && settlementVerificationNotice?.message && (
                        <Alert
                            type="info"
                            message={settlementVerificationNotice.message}
                            onClose={() => setShowSettlementVerificationNotice(false)}
                        />
                    )}
                    <VerifyData onClick={handleSettlementPhaseConfirm} />
                </>
            )}
        </div>
      );
});
